import { useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import {
  ShoppingBag,
  Building2,
  FileText,
  DollarSign,
  Camera,
  ChefHat,
  Users,
  Loader2,
  Search,
  ExternalLink,
  ChevronDown,
  ChevronRight,
  AlertTriangle,
  Plus,
  Edit3,
  Send,
  Download,
  Copy,
  Image as ImageIcon,
  Package as PackageIcon,
  ArrowUp,
  ArrowDown,
  UtensilsCrossed,
  Activity,
  CheckCircle2,
  Pencil,
  Trash,
  CreditCard,
  CalendarDays,
  Gift,
  ArrowLeft,
  RefreshCw,
  Sparkles,
  UserCheck,
  XCircle,
  Database,
  CloudUpload,
  Mail,
  Send as SendIcon,
  Calendar as CalendarIcon,
  Trash2,
  X,
  Layers,
  UploadCloud,
  Bold,
  Italic,
  Underline,
  Link as LinkIcon,
  List as ListIcon,
  Heading1,
  Heading2,
  Pilcrow,
  Briefcase,
  User,
  ShieldCheck,
  ArrowRight,
  Landmark,
  Printer,
} from "lucide-react";

import Header from "./Header";
import Footer from "./Footer";
import { useAuth } from "../context/AuthContext";
import { api, formatApiErrorDetail } from "../lib/api";
import { Input } from "../components/ui/input";
import OrderEditorModal from "./OrderEditorModal";
import { ManualInvoiceModal } from "./ManualInvoiceModal";
import MarkAsPaidModal from "./MarkAsPaidModal";
import { toast } from "sonner";
import SendInvoiceModal from "./SendInvoiceModal";
import ExperienceEditorModal from "./ExperienceEditorModal";
import PackageEditorModal from "./PackageEditorModal";
import MenuItemEditorModal from "./MenuItemEditorModal";
import HiringTab from "./HiringTab";
import TaxExemptTab from "./TaxExemptTab";
import PrivateCustomersTab from "./PrivateCustomersTab";
import PolarisPOTab from "./PolarisPOTab";
import PaymentApprovalsTab from "./PaymentApprovalsTab";
import { MenuCategoryTiles } from "./MenuCategoryTiles";
import { ReorderableList } from "./ReorderableList";
import HolidayTab from "./HolidayTab";
import { AdminTabGroups, TAB_GROUPS } from "./AdminTabGroups";

const TABS = [
  { id: "orders", label: "Orders", icon: ShoppingBag },
  { id: "polaris", label: "Polaris PO", icon: Landmark },
  { id: "batches", label: "Batches", icon: Layers },
  { id: "partnerships", label: "Partnerships", icon: Building2 },
  { id: "quotes", label: "Quote Requests", icon: FileText },
  { id: "agencies", label: "Agencies", icon: UserCheck },
  { id: "private-customers", label: "Private", icon: User },
  { id: "tax-exempt", label: "Tax Exempt", icon: ShieldCheck },
  { id: "pay-approvals", label: "Pay Approvals", icon: CreditCard },
  { id: "holiday", label: "Holiday", icon: Gift },
  { id: "gallery", label: "Gallery", icon: ImageIcon },
  { id: "packages", label: "Packages", icon: PackageIcon },
  { id: "menu", label: "Menu", icon: UtensilsCrossed },
  { id: "hiring", label: "Hiring", icon: Briefcase },
  { id: "storage", label: "Storage", icon: Database },
  { id: "newsletter", label: "Newsletter", icon: Mail },
  { id: "activity", label: "Activity", icon: Activity },
];

const fmtMoney = (v) =>
  typeof v === "number" ? `$${v.toFixed(2)}` : v === 0 ? "$0.00" : "—";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const StatCard = ({ label, value, accent = "teal", icon: Icon, onClick }) => {
  const accentClasses = {
    teal: "bg-brand-teal/10 text-brand-teal",
    gold: "bg-brand-gold/15 text-brand-gold-dark",
    green: "bg-brand-green/15 text-brand-green",
    charcoal: "bg-brand-charcoal/8 text-brand-charcoal",
  };
  const testId = `stat-card-${label.toLowerCase().replace(/\s+/g, "-")}`;
  const clickable = typeof onClick === "function";
  const Wrapper = clickable ? "button" : "div";
  return (
    <Wrapper
      data-testid={testId}
      onClick={clickable ? onClick : undefined}
      type={clickable ? "button" : undefined}
      className={`rounded-2xl bg-white border border-black/5 p-3.5 sm:p-5 text-left w-full ${
        clickable
          ? "cursor-pointer transition-all hover:border-brand-teal/50 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-teal"
          : ""
      }`}
    >
      <div className="flex items-start justify-between gap-2 sm:gap-3">
        <div className="min-w-0">
          <p className="text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.12em] sm:tracking-[0.16em] text-brand-charcoal/55 leading-tight">
            {label}
          </p>
          <p className="mt-1.5 sm:mt-2 font-display font-black text-[20px] sm:text-[28px] tracking-tight text-brand-charcoal leading-none truncate">
            {value}
          </p>
        </div>
        <span
          className={`inline-flex h-8 w-8 sm:h-10 sm:w-10 items-center justify-center rounded-full shrink-0 ${accentClasses[accent]}`}
        >
          <Icon className="h-4 w-4 sm:h-5 sm:w-5" />
        </span>
      </div>
    </Wrapper>
  );
};

// ---------------------------------------------------------------------------
// PDF download helpers — trigger a browser save-as by streaming the response
// body into a blob and clicking an <a download> tag. Reuses the axios `api`
// instance so the admin Bearer token flows through automatically.
// ---------------------------------------------------------------------------
const triggerBlobDownload = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Free the URL asynchronously to make sure the download has started.
  setTimeout(() => URL.revokeObjectURL(url), 2000);
};

const parseFilenameFromResponse = (response, fallback) => {
  const cd = response.headers["content-disposition"] || "";
  const match = cd.match(/filename="?([^"]+)"?/i);
  return match ? match[1] : fallback;
};

const openKitchenTicketPdf = async (order) => {
  // Open the tab synchronously so mobile browsers don't block it as a pop-up,
  // then point it at a short-lived signed PDF link (works in every PDF viewer).
  const win = window.open("", "_blank");
  try {
    const { data } = await api.post(`/admin/kitchen/prep/order/${order.id}/ticket-link`);
    const url = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
    if (win) win.location.href = url;
    else window.location.href = url;
  } catch (e) {
    if (win) win.close();
    window.alert(`Kitchen ticket failed: ${e?.message || "try again"}`);
  }
};

const downloadInvoicePdf = async (order) => {
  try {
    const response = await api.get(`/admin/orders/${order.id}/pdf`, {
      responseType: "blob",
    });
    const filename = parseFilenameFromResponse(
      response,
      `invoice-${order.po_number || order.id.slice(0, 8)}.pdf`
    );
    triggerBlobDownload(response.data, filename);
  } catch (e) {
    // Fire-and-forget from onClick; surface the error via alert since we
    // don't have access to the parent toast here.
    const msg = e?.message || "Download failed";
    if (typeof window !== "undefined") window.alert(`Invoice PDF download failed: ${msg}`);
  }
};

const downloadOrderIcs = async (order) => {
  // Downloads the Outlook .ics invite for a single order. Same VCALENDAR
  // that ships with the internal notification email (1-week + 3-day
  // reminders). Double-clicking the downloaded file adds the event to
  // Outlook / Apple Calendar / Google Calendar. Skipped for orders with
  // no event_date — the backend returns 404 which we surface as a toast.
  try {
    const response = await api.get(`/admin/orders/${order.id}/calendar.ics`, {
      responseType: "blob",
    });
    const filename = parseFilenameFromResponse(
      response,
      `${order.invoice_number || order.id.slice(0, 8)}.ics`
    );
    triggerBlobDownload(response.data, filename);
  } catch (e) {
    const detail = e?.response?.data?.detail
      || e?.message
      || "Calendar download failed";
    if (typeof window !== "undefined") window.alert(String(detail));
  }
};

const downloadBulkInvoiceZip = async (orderIds) => {
  const response = await api.post(
    "/admin/orders/bulk-pdf",
    { order_ids: orderIds },
    { responseType: "blob" }
  );
  const filename = parseFilenameFromResponse(response, "invoices.zip");
  triggerBlobDownload(response.data, filename);
};



const StatusBadge = ({ status }) => {
  const map = {
    paid: "bg-brand-green/15 text-brand-green",
    submitted: "bg-brand-gold/20 text-brand-gold-dark",
    pending: "bg-brand-charcoal/10 text-brand-charcoal/70",
    new: "bg-brand-teal/15 text-brand-teal",
  };
  const cls = map[status] || "bg-brand-charcoal/10 text-brand-charcoal/70";
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.1em] ${cls}`}
    >
      {status || "—"}
    </span>
  );
};

const AssignAgencyModal = ({ order, agencies, onClose, onSaved }) => {
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);
  const currentUserId = order?.user_id || null;
  const approvedAgencies = useMemo(
    () => (agencies || []).filter((a) => (a.status || "approved") === "approved"),
    [agencies]
  );
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return approvedAgencies;
    return approvedAgencies.filter((a) =>
      [a.email, a.name, a.organization].filter(Boolean).some((v) => String(v).toLowerCase().includes(q))
    );
  }, [approvedAgencies, query]);

  const assign = async (userId) => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/orders/${order.id}/assign-agency`, { user_id: userId });
      toast.success(userId ? `Linked to ${data.user_email || "agency"}` : "Order unlinked");
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to link");
    } finally {
      setBusy(false);
    }
  };

  if (!order) return null;
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4">
      <div
        data-testid="assign-agency-modal"
        className="w-full max-w-lg rounded-3xl bg-white p-6 sm:p-7 shadow-2xl flex flex-col max-h-[85vh]"
      >
        <h3 className="font-display font-black text-[20px] text-brand-charcoal tracking-tight">
          Attach order to agency
        </h3>
        <p className="mt-1 text-[12.5px] text-brand-charcoal/65 leading-snug">
          {order.invoice_number || order.id.slice(0, 8)} · {order.customer_name || "—"} ·{" "}
          <span className="tabular-nums font-bold text-brand-charcoal">${(order.total || 0).toFixed(2)}</span>
        </p>
        {currentUserId && (
          <p className="mt-2 text-[12.5px] text-brand-teal">
            Currently linked to{" "}
            <b className="font-display font-black text-[15px] tracking-tight">
              {order.user_email}
            </b>
          </p>
        )}
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by name, email, or organization"
          data-testid="assign-agency-search"
          className="mt-4 h-11 rounded-xl bg-brand-cream/40 border border-black/10 px-3.5 text-[13px] focus:outline-none focus:ring-2 focus:ring-brand-teal/40"
          autoFocus
        />
        <ul className="mt-3 flex-1 overflow-y-auto space-y-2 pr-1">
          {filtered.length === 0 && (
            <li className="rounded-xl border border-dashed border-brand-charcoal/15 bg-brand-cream/30 px-3 py-6 text-center text-[12.5px] text-brand-charcoal/55">
              No approved agencies matched.
            </li>
          )}
          {filtered.map((a) => {
            const isCurrent = a.id === currentUserId;
            return (
              <li key={a.id}>
                <button
                  type="button"
                  onClick={() => assign(a.id)}
                  disabled={busy || isCurrent}
                  data-testid={`assign-agency-pick-${a.id}`}
                  className={`w-full flex items-center justify-between gap-3 rounded-xl border p-3 text-left transition-all ${
                    isCurrent
                      ? "border-brand-teal bg-brand-teal/5"
                      : "border-black/10 hover:border-brand-teal/60 hover:bg-brand-cream/40"
                  } disabled:opacity-70`}
                >
                  <div className="min-w-0 flex-1">
                    <p className="font-display font-black text-[16px] sm:text-[17px] text-brand-charcoal tracking-tight leading-tight truncate">
                      {a.name || "—"}
                    </p>
                    <p className="mt-0.5 text-[12px] text-brand-charcoal/60 truncate">
                      {a.email}
                      {a.organization && <> · <b className="text-brand-charcoal/80">{a.organization}</b></>}
                    </p>
                  </div>
                  {isCurrent ? (
                    <span className="text-[10.5px] font-black uppercase tracking-[0.12em] text-brand-teal">
                      Current
                    </span>
                  ) : (
                    <ArrowRight className="h-4 w-4 text-brand-charcoal/40" />
                  )}
                </button>
              </li>
            );
          })}
        </ul>
        <div className="mt-4 flex flex-wrap justify-between gap-2">
          {currentUserId && (
            <button
              type="button"
              onClick={() => assign(null)}
              disabled={busy}
              data-testid="assign-agency-unlink"
              className="rounded-full border border-red-200 text-red-600 px-4 py-2 text-[11px] font-bold uppercase tracking-[0.12em] hover:bg-red-50 disabled:opacity-50"
            >
              Unlink from agency
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            data-testid="assign-agency-cancel"
            className="ml-auto rounded-full bg-brand-charcoal text-white px-4 py-2 text-[11px] font-bold uppercase tracking-[0.12em] hover:bg-brand-teal"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

const OrderRow = ({ order, onEdit, onSendInvoice, onMarkPaid, onDelete, onAssignAgency, agencies = [], onOrdersChanged, selected = false, onToggleSelect }) => {
  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [marking, setMarking] = useState(false);
  const [printing, setPrinting] = useState(false);
  // Inline agency-assign dropdown state — POST /admin/orders/{id}/assign-agency
  // fires immediately when a value is picked so admins can move an order
  // to an agency without opening the search modal.
  const [assigning, setAssigning] = useState(false);
  const approvedAgencies = useMemo(
    () => (agencies || []).filter((a) => (a.status || "approved") === "approved"),
    [agencies]
  );
  const handleInlineAssign = async (e) => {
    e.stopPropagation();
    const value = e.target.value;
    // "__modal__" opens the full search modal — useful when the inline
    // dropdown gets too long or the admin wants to search by name.
    if (value === "__modal__") {
      e.target.value = order.user_id || "";
      onAssignAgency?.(order);
      return;
    }
    // Convert empty string ("Unassigned") to null to unlink.
    const userId = value || null;
    if (userId === (order.user_id || null)) return; // no-op
    if (assigning) return;
    setAssigning(true);
    try {
      const { data } = await api.post(
        `/admin/orders/${order.id}/assign-agency`,
        { user_id: userId },
      );
      toast.success(userId ? `Linked to ${data.user_email || "agency"}` : "Order unlinked");
      onOrdersChanged?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to link");
    } finally {
      setAssigning(false);
    }
  };
  const typeLabel =
    order.type === "agency_po"
      ? "Agency PO"
      : order.type === "cash"
      ? "Cash"
      : order.type === "self_pay_invoice"
      ? "Pay-Link"
      : "Stripe";
  const typeClasses =
    order.type === "agency_po"
      ? "bg-brand-gold/20 text-brand-gold-dark"
      : order.type === "cash"
      ? "bg-brand-charcoal/10 text-brand-charcoal"
      : order.type === "self_pay_invoice"
      ? "bg-brand-teal/10 text-brand-teal"
      : "bg-brand-teal/15 text-brand-teal";
  // Pay-Link ("self_pay_invoice") saved without emailing shows a "NOT SENT"
  // amber pill so the admin can spot invoices still queued for delivery.
  const showNotSentBadge =
    order.type === "self_pay_invoice" &&
    !order.invoice_sent_at &&
    (order.status || "").toLowerCase() !== "paid";
  const handleSend = async (e) => {
    e.stopPropagation();
    if (sending) return;
    setSending(true);
    try {
      await onSendInvoice?.(order);
    } finally {
      setSending(false);
    }
  };
  // Copy the customer's pay-link URL to clipboard. Only rendered on
  // self_pay_invoice (or manual_invoice) rows that already have a
  // share_token — otherwise there's no public URL to share yet.
  const isSelfPayRow =
    order.type === "self_pay_invoice" || order.type === "manual_invoice";
  const [payLinkCopied, setPayLinkCopied] = useState(false);
  const handleCopyPayLink = async (e) => {
    e.stopPropagation();
    if (!order.share_token) {
      toast.error("This invoice has no pay link yet");
      return;
    }
    const url = `${window.location.origin}/invoice/${order.share_token}`;
    try {
      await navigator.clipboard.writeText(url);
      setPayLinkCopied(true);
      toast.success("Pay link copied to clipboard");
      setTimeout(() => setPayLinkCopied(false), 1600);
    } catch (err) {
      console.error("clipboard write failed", err);
      // Fallback: pop a prompt so the admin can still grab the URL if the
      // browser blocks Clipboard API (e.g. iOS Safari without user
      // gesture in the right context).
      window.prompt("Copy this pay link:", url);
    }
  };
  const handleMarkPaidClick = async (e) => {
    e.stopPropagation();
    if (marking) return;
    setMarking(true);
    try {
      await onMarkPaid?.(order);
    } finally {
      setMarking(false);
    }
  };
  // Fire the OFFICE ORDER TICKET (PDF-attached) email to every printer address
  // configured on the backend (PRINTER_EMAIL env → HP ePrint). Idempotent — the
  // admin can hit this as many times as needed for a re-send. Auto-fires on
  // every new paid/confirmed order already; this button is the manual reprint.
  const handleSendToPrinter = async (e) => {
    e.stopPropagation();
    if (printing) return;
    setPrinting(true);
    try {
      const res = await api.post(`/api/admin/orders/${order.id}/send-to-printer`);
      const sent = res.data?.sent ?? 0;
      const printers = res.data?.printers || [];
      toast.success(
        sent > 0
          ? `Office ticket sent to ${sent} printer${sent === 1 ? "" : "s"}${printers.length ? ` (${printers[0]}${printers.length > 1 ? `, +${printers.length - 1}` : ""})` : ""}.`
          : "Office ticket email fired.",
      );
    } catch (err) {
      toast.error(formatApiErrorDetail(err) || "Could not send office ticket.");
    } finally {
      setPrinting(false);
    }
  };
  // Show "Mark as Paid" for any unpaid, non-cancelled order — the modal lets
  // the admin choose cash / check / card / PO and (for cash/check/card) send
  // a receipt.
  const status = (order.status || "").toLowerCase();
  const canMarkPaid = status !== "paid" && status !== "cancelled";
  const markPaidLabel =
    order.type === "agency_po"
      ? "Mark PO Paid"
      : order.type === "cash"
      ? "Mark Cash Paid"
      : order.type === "stripe"
      ? "Reconcile Card Payment"
      : "Mark as Paid";

  // Rush approval surface — only visible on orders that came in inside the
  // 5-business-day lead-time window AND are still pending our review.
  const isRushPending =
    order.rush_order && status === "pending_approval";
  const [rushBusy, setRushBusy] = useState(false);
  const handleApproveRush = async (e) => {
    e.stopPropagation();
    if (rushBusy) return;
    setRushBusy(true);
    try {
      await api.post(`/admin/orders/${order.id}/approve-rush`);
      toast.success("Rush order approved. Customer notified.");
      onOrdersChanged?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not approve rush order.");
    } finally {
      setRushBusy(false);
    }
  };
  const handleRejectRush = async (e) => {
    e.stopPropagation();
    if (rushBusy) return;
    const reason = window.prompt(
      "Optional note for the customer (included in the rejection email):",
      "",
    );
    if (reason === null) return; // cancelled
    setRushBusy(true);
    try {
      await api.post(`/admin/orders/${order.id}/reject-rush`, { reason: reason || null });
      toast.success("Rush order declined. Customer notified.");
      onOrdersChanged?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not reject rush order.");
    } finally {
      setRushBusy(false);
    }
  };

  // Full order cancellation — distinct from Decline Rush. Available on any
  // order that isn't already cancelled, including paid ones (the admin may
  // need to cancel a paid order and refund separately via Stripe). The
  // customer email mentions the refund path when applicable.
  const canCancel = status !== "cancelled";
  const [cancelBusy, setCancelBusy] = useState(false);
  const handleCancelOrder = async (e) => {
    e.stopPropagation();
    if (cancelBusy) return;
    const paidNote = status === "paid"
      ? "\n\n⚠ This order was PAID. Cancelling will NOT auto-refund the customer — you'll need to refund them in Stripe (or whatever method they used) separately."
      : "";
    const confirmed = window.confirm(
      `Cancel this order for ${order.customer_name || order.customer_email}?\n\nThis will set the status to cancelled and email the customer.${paidNote}`,
    );
    if (!confirmed) return;
    const reason = window.prompt(
      "Optional note for the customer (shown in their cancellation email). Leave blank to skip:",
      "",
    );
    if (reason === null) return; // user cancelled the prompt itself
    setCancelBusy(true);
    try {
      await api.post(`/admin/orders/${order.id}/cancel`, {
        reason: reason || null,
        notify_customer: true,
      });
      toast.success(
        status === "paid"
          ? "Order cancelled. Customer notified — remember to refund in Stripe."
          : "Order cancelled. Customer notified.",
      );
      onOrdersChanged?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not cancel order.");
    } finally {
      setCancelBusy(false);
    }
  };
  const handleCheckbox = (e) => {
    e.stopPropagation();
    onToggleSelect?.(order.id);
  };
  const canRedoPO = order.type !== "agency_po" && !["cancelled", "paid", "rejected"].includes(status);
  const [redoBusy, setRedoBusy] = useState(false);
  const handleRedoPO = async (e) => {
    e.stopPropagation();
    if (redoBusy) return;
    const po = window.prompt(
      `Re-issue this invoice for ${order.customer_name || order.customer_email} as an Agency Purchase Order?\n\n` +
      `• Items are re-priced at agency rates (tax-exempt)\n• This invoice is cancelled and the customer is emailed\n• The agency receives the new PO confirmation\n\n` +
      `PO number (optional — leave blank if you don't have it):`,
      "",
    );
    if (po === null) return;
    setRedoBusy(true);
    try {
      const { data } = await api.post(`/admin/orders/${order.id}/redo-as-po`, { po_number: po.trim() || null });
      toast.success(`Re-issued as Agency PO ${data.order.invoice_number} · $${Number(data.order.total).toFixed(2)}`);
      onOrdersChanged?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not re-issue as PO.");
    } finally {
      setRedoBusy(false);
    }
  };
  return (
    <li
      data-testid={`admin-order-row-${order.id}`}
      className={`rounded-2xl bg-white border overflow-hidden transition-colors ${
        selected ? "border-brand-teal/60 ring-2 ring-brand-teal/30" : "border-black/5"
      }`}
    >
      <div className="relative">
        {/* Bulk-select checkbox — sits over the row, click is isolated */}
        <label
          className="absolute top-3 left-3 z-10 inline-flex items-center justify-center h-6 w-6 cursor-pointer"
          onClick={(e) => e.stopPropagation()}
        >
          <input
            type="checkbox"
            data-testid={`admin-order-select-${order.id}`}
            checked={selected}
            onChange={handleCheckbox}
            aria-label={`Select order ${order.po_number || order.id.slice(0, 8)}`}
            className="h-4 w-4 rounded border-brand-charcoal/30 text-brand-teal focus:ring-brand-teal/50"
          />
        </label>
        <div
          role="button"
          tabIndex={0}
          onClick={() => setOpen((v) => !v)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setOpen((v) => !v);
            }
          }}
          className="w-full text-left hover:bg-brand-cream/40 transition-colors cursor-pointer pl-10"
        >
        {/* MOBILE: stacked card */}
        <div className="md:hidden flex flex-col gap-3 px-4 py-4">
          <div className="flex items-start gap-3">
            <span className="mt-1 text-brand-charcoal/40 shrink-0">
              {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </span>
            <div className="min-w-0 flex-1">
              <p className="font-bold text-[14px] text-brand-charcoal break-words leading-tight">
                {order.customer_name || "—"}
              </p>
              <p className="text-[12px] text-brand-charcoal/55 break-all leading-snug mt-0.5">
                {order.customer_email}
              </p>
              {order.user_email && (
                <p
                  data-testid={`admin-order-agency-pill-mobile-${order.id}`}
                  className="mt-1 inline-flex items-center gap-1 rounded-full bg-brand-teal/10 text-brand-teal px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.08em] max-w-full break-all"
                  title={`Linked to ${order.user_email}`}
                >
                  <UserCheck className="h-3 w-3 shrink-0" />
                  Agency · {order.organization || order.user_email}
                </p>
              )}
            </div>
            <div className="text-right shrink-0">
              <p className="font-display font-black text-[16px] text-brand-charcoal tabular-nums leading-none">
                {fmtMoney(order.total)}
              </p>
              <p className="text-[10.5px] text-brand-charcoal/55 mt-1">
                {fmtDate(order.created_at).split(",")[0]}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-1.5 pl-7">
            <span
              className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.1em] ${typeClasses}`}
            >
              {typeLabel}
            </span>
            {showNotSentBadge && (
              <span
                data-testid={`admin-order-not-sent-${order.id}`}
                className="inline-flex items-center rounded-full bg-amber-100 text-amber-800 border border-amber-300 px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.1em]"
                title="Invoice saved but not emailed to customer yet"
              >
                Not sent
              </span>
            )}
            {order.po_number && (
              <span className="inline-flex items-center rounded-full bg-brand-cream text-brand-charcoal/70 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.08em]">
                PO {order.po_number}
              </span>
            )}
            {order.batch_ref && (
              <span
                data-testid={`admin-order-batch-badge-${order.id}`}
                className="inline-flex items-center rounded-full bg-brand-gold/25 text-brand-gold-dark px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.08em]"
                title={`Part of batch ${order.batch_ref}`}
              >
                {order.batch_ref} · {order.batch_index}/{order.batch_size}
              </span>
            )}
            <span className="inline-flex items-center rounded-full bg-brand-cream text-brand-charcoal/70 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.08em] capitalize">
              {order.fulfillment || "—"}
            </span>
            <StatusBadge status={order.status} />
            {order.rush_order && status === "pending_approval" && (
              <span
                data-testid={`admin-order-rush-badge-${order.id}`}
                title={`Only ${order.rush_days_notice ?? "?"} business day${order.rush_days_notice === 1 ? "" : "s"} of notice`}
                className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-900 border border-amber-300 px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.08em]"
              >
                ⚠ Rush · {order.rush_days_notice ?? "?"}bd
              </span>
            )}
          </div>
        </div>

        {/* DESKTOP/TABLET: grid table row */}
        <div className="hidden md:grid grid-cols-12 items-center gap-3 px-4 py-3.5">
          <div className="col-span-1 flex justify-center text-brand-charcoal/40">
            {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </div>
          <div className="col-span-3 min-w-0">
            <p className="font-bold text-[13.5px] text-brand-charcoal truncate">
              {order.customer_name || "—"}
            </p>
            <p className="text-[11.5px] text-brand-charcoal/55 truncate">{order.customer_email}</p>
            {order.user_email && (
              <p
                data-testid={`admin-order-agency-pill-${order.id}`}
                className="mt-1 inline-flex items-center gap-1 rounded-full bg-brand-teal/10 text-brand-teal px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.08em] max-w-full truncate"
                title={`Linked to ${order.user_email}`}
              >
                <UserCheck className="h-3 w-3 shrink-0" />
                <span className="truncate">
                  Agency · {order.organization || order.user_email}
                </span>
              </p>
            )}
          </div>
          <div className="col-span-2">
            <span
              className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.1em] ${typeClasses}`}
            >
              {typeLabel}
            </span>
            {showNotSentBadge && (
              <p
                data-testid={`admin-order-not-sent-desktop-${order.id}`}
                className="mt-1 inline-flex items-center rounded-full bg-amber-100 text-amber-800 border border-amber-300 px-1.5 py-0.5 text-[9.5px] font-black uppercase tracking-[0.1em]"
                title="Invoice saved but not emailed to customer yet"
              >
                Not sent
              </p>
            )}
            {order.po_number && (
              <p className="mt-0.5 text-[10.5px] text-brand-charcoal/55 truncate">
                PO {order.po_number}
              </p>
            )}
            {order.batch_ref && (
              <p
                data-testid={`admin-order-batch-badge-${order.id}`}
                className="mt-1 inline-flex items-center rounded-full bg-brand-gold/25 text-brand-gold-dark px-1.5 py-0.5 text-[9.5px] font-black uppercase tracking-[0.08em]"
                title={`Part of batch ${order.batch_ref}`}
              >
                {order.batch_ref} · {order.batch_index}/{order.batch_size}
              </p>
            )}
          </div>
          <div className="col-span-2 text-[12px] text-brand-charcoal/65 capitalize">
            {order.fulfillment || "—"}
          </div>
          <div className="col-span-2">
            <StatusBadge status={order.status} />
          </div>
          <div className="col-span-1 text-right font-display font-black text-[14px] text-brand-charcoal tabular-nums">
            {fmtMoney(order.total)}
          </div>
          <div className="col-span-1 text-right text-[11px] text-brand-charcoal/55">
            {fmtDate(order.created_at).split(",")[0]}
          </div>
        </div>
        </div>
      </div>
      {open && (
        <div className="border-t border-black/5 bg-brand-cream/30 px-4 sm:px-5 py-5 sm:py-6 grid sm:grid-cols-3 gap-6 sm:gap-7 text-[12.5px]">
          <div className="space-y-1.5">
            <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-brand-charcoal/55">
              Customer
            </p>
            <p className="mt-1.5 text-brand-charcoal font-medium">{order.customer_name || "—"}</p>
            <p className="text-brand-charcoal/65">{order.customer_email}</p>
            <p className="text-brand-charcoal/65">{order.customer_phone || "—"}</p>
            {(order.address || order.city || order.zip_code) && (
              <div className="mt-1 text-brand-charcoal/65" data-testid={`order-address-${order.id}`}>
                {order.address && <div>{order.address}</div>}
                {order.address_line2 && <div>{order.address_line2}</div>}
                {(order.city || order.state || order.zip_code) && (
                  <div>
                    {[
                      order.city ? `${order.city},` : null,
                      order.state || null,
                      order.zip_code || null,
                    ]
                      .filter(Boolean)
                      .join(" ")}
                  </div>
                )}
              </div>
            )}
            {order.on_site_contact_name && (
              <p className="mt-1 text-brand-charcoal/65">
                On-site: {order.on_site_contact_name}
                {order.on_site_contact_phone ? ` · ${order.on_site_contact_phone}` : ""}
              </p>
            )}
            {(order.has_receiving_personnel === true || order.has_receiving_personnel === false ||
              order.has_delivery_cart === true || order.has_delivery_cart === false) && (
              <div className="mt-1 flex flex-wrap gap-1.5" data-testid={`admin-order-receiving-${order.id}`}>
                {order.has_receiving_personnel === true && (
                  <span className="inline-flex items-center rounded-full bg-brand-teal/15 text-brand-teal px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.1em]">Staff on-site</span>
                )}
                {order.has_receiving_personnel === false && (
                  <span className="inline-flex items-center rounded-full bg-brand-charcoal/10 text-brand-charcoal/70 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.1em]">No staff</span>
                )}
                {order.has_delivery_cart === true && (
                  <span className="inline-flex items-center rounded-full bg-brand-teal/15 text-brand-teal px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.1em]">Cart on-site</span>
                )}
                {order.has_delivery_cart === false && (
                  <span className="inline-flex items-center rounded-full bg-brand-charcoal/10 text-brand-charcoal/70 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.1em]">No cart</span>
                )}
              </div>
            )}
            {order.delivery_instructions && (
              <div
                data-testid={`admin-order-delivery-instructions-${order.id}`}
                className="mt-1.5 rounded-lg bg-brand-gold/10 border border-brand-gold/30 px-2.5 py-1.5 text-[11.5px] text-brand-charcoal/80 whitespace-pre-wrap"
              >
                <span className="font-bold text-brand-charcoal">Access notes:</span>{" "}
                {order.delivery_instructions}
              </div>
            )}
            {order.event_date && (
              <p className="mt-1 text-brand-charcoal/65">
                {order.fulfillment === "pickup" ? "Pickup" : "Drop-off"}:{" "}
                {order.event_date}
                {order.event_time ? ` @ ${order.event_time}` : ""}
              </p>
            )}
            {(order.needs_utensils || order.needs_drinks) && (
              <div className="mt-2 flex flex-wrap gap-1">
                {order.needs_utensils && (
                  <span className="inline-flex items-center rounded-full bg-brand-gold/20 text-brand-gold-dark px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.1em]">
                    + Utensils
                  </span>
                )}
                {order.needs_drinks && (
                  <span className="inline-flex items-center rounded-full bg-brand-gold/20 text-brand-gold-dark px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.1em]">
                    + Drinks
                  </span>
                )}
              </div>
            )}
          </div>
          <div className="sm:col-span-2">
            <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-brand-charcoal/55">
              Line Items
            </p>
            <ul className="mt-1.5 space-y-1">
              {(order.items || []).map((it, idx) => {
                const opts = it.selected_options || {};
                // Accept both scalar strings ("Buffalo") and arrays
                // (["Bacon", "Chopped Cheese"]). Previously this filter
                // required an array so single-pick modifiers (Cheese,
                // Slider Style, Flavor…) never surfaced in the row-expand.
                const optsLine = Object.entries(opts)
                  .map(([k, v]) => {
                    if (Array.isArray(v)) {
                      const kept = v.filter(Boolean);
                      return kept.length > 0 ? `${k}: ${kept.join(", ")}` : null;
                    }
                    return v ? `${k}: ${v}` : null;
                  })
                  .filter(Boolean)
                  .join(" · ");
                return (
                  <li key={`${idx}-${it.name || ""}-${it.package_id || ""}`} className="flex justify-between gap-3 text-brand-charcoal/85">
                    <span className="min-w-0 truncate">
                      <span className="font-bold">{it.name}</span>
                      <span className="text-brand-charcoal/55"> × {it.quantity}</span>
                      {optsLine && (
                        <span className="block text-[11px] text-brand-teal font-bold uppercase tracking-[0.1em]">
                          {optsLine}
                        </span>
                      )}
                    </span>
                    <span className="tabular-nums font-bold text-brand-charcoal">
                      {fmtMoney(it.line_total)}
                    </span>
                  </li>
                );
              })}
            </ul>
            <div className="mt-3 pt-2 border-t border-black/5 text-[12px] grid grid-cols-2 sm:grid-cols-4 gap-2">
              <div>
                <span className="text-brand-charcoal/55">Subtotal</span>
                <p className="font-bold text-brand-charcoal tabular-nums">
                  {fmtMoney(order.subtotal)}
                </p>
              </div>
              <div>
                <span className="text-brand-charcoal/55">Delivery</span>
                <p className="font-bold text-brand-charcoal tabular-nums">
                  {fmtMoney(order.delivery_fee || 0)}
                </p>
              </div>
              <div>
                <span className="text-brand-charcoal/55">
                  {order.tax_exempt ? "Tax (Exempt)" : "Tax"}
                </span>
                <p className="font-bold text-brand-charcoal tabular-nums">
                  {fmtMoney(order.tax || 0)}
                </p>
              </div>
              <div>
                <span className="text-brand-charcoal/55">Total</span>
                <p className="font-bold text-brand-charcoal tabular-nums">
                  {fmtMoney(order.total)}
                </p>
              </div>
            </div>
            {order.notes && (
              <p className="mt-2 text-[11.5px] italic text-brand-charcoal/70 leading-snug">
                Notes: {order.notes}
              </p>
            )}
            {order.internal_notes && (
              <p className="mt-1.5 text-[11.5px] text-brand-charcoal/70 leading-snug">
                <span className="font-bold text-brand-charcoal/55 uppercase tracking-[0.1em] text-[10px]">
                  Internal:{" "}
                </span>
                {order.internal_notes}
              </p>
            )}
            {order.last_invoice_sent_at && (
              <p className="mt-1.5 text-[10.5px] text-brand-charcoal/55">
                Last invoice sent {fmtDate(order.last_invoice_sent_at)}
              </p>
            )}
            <div className="mt-4 flex flex-wrap gap-2">
              <button
                type="button"
                data-testid={`admin-order-edit-${order.id}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit?.(order);
                }}
                className="inline-flex items-center gap-1.5 rounded-full bg-brand-charcoal text-white px-4 py-2 text-[11px] font-bold uppercase tracking-[0.1em] hover:bg-brand-teal transition-colors"
              >
                <Edit3 className="h-3.5 w-3.5" />
                Edit Order
              </button>
              <button
                type="button"
                data-testid={`admin-order-send-${order.id}`}
                onClick={handleSend}
                disabled={sending}
                title={isSelfPayRow
                  ? "Email the customer their pay-now link"
                  : "Send this invoice to the customer"}
                className="inline-flex items-center gap-1.5 rounded-full bg-white border border-brand-charcoal/15 text-brand-charcoal px-4 py-2 text-[11px] font-bold uppercase tracking-[0.1em] hover:border-brand-teal hover:text-brand-teal transition-colors disabled:opacity-60"
              >
                {sending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Send className="h-3.5 w-3.5" />
                )}
                {sending
                  ? "Sending…"
                  : isSelfPayRow
                  ? "Send Pay Link"
                  : "Send Invoice"}
              </button>
              {isSelfPayRow && order.share_token && (
                <button
                  type="button"
                  data-testid={`admin-order-copy-pay-link-${order.id}`}
                  onClick={handleCopyPayLink}
                  title="Copy the customer-facing pay link to your clipboard"
                  className="inline-flex items-center gap-1.5 rounded-full bg-brand-teal/10 border border-brand-teal/40 text-brand-teal px-4 py-2 text-[11px] font-bold uppercase tracking-[0.1em] hover:bg-brand-teal hover:text-white transition-colors"
                >
                  {payLinkCopied ? (
                    <CheckCircle2 className="h-3.5 w-3.5" />
                  ) : (
                    <Copy className="h-3.5 w-3.5" />
                  )}
                  {payLinkCopied ? "Copied" : "Copy Pay Link"}
                </button>
              )}
              {isRushPending && (
                <>
                  <button
                    type="button"
                    data-testid={`admin-order-approve-rush-${order.id}`}
                    onClick={handleApproveRush}
                    disabled={rushBusy}
                    title="Approve this rush order and notify the customer"
                    className="inline-flex items-center gap-1.5 rounded-full bg-brand-green text-white px-4 py-2 text-[11px] font-bold uppercase tracking-[0.1em] hover:bg-brand-green/90 transition-colors disabled:opacity-60"
                  >
                    {rushBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
                    Approve Rush
                  </button>
                  <button
                    type="button"
                    data-testid={`admin-order-reject-rush-${order.id}`}
                    onClick={handleRejectRush}
                    disabled={rushBusy}
                    title="Decline this rush order and notify the customer"
                    className="inline-flex items-center gap-1.5 rounded-full bg-white border border-red-200 text-red-700 px-4 py-2 text-[11px] font-bold uppercase tracking-[0.1em] hover:bg-red-50 transition-colors disabled:opacity-60"
                  >
                    <XCircle className="h-3.5 w-3.5" />
                    Decline Rush
                  </button>
                </>
              )}
              {canMarkPaid && (
                <button
                  type="button"
                  data-testid={`admin-order-mark-paid-${order.id}`}
                  onClick={handleMarkPaidClick}
                  disabled={marking}
                  title="Mark this order as paid (cash / check / PO)"
                  className="inline-flex items-center gap-1.5 rounded-full bg-brand-green/15 border border-brand-green/40 text-brand-green px-4 py-2 text-[11px] font-bold uppercase tracking-[0.1em] hover:bg-brand-green hover:text-white transition-colors disabled:opacity-60"
                >
                  {marking ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <CheckCircle2 className="h-3.5 w-3.5" />
                  )}
                  {marking ? "Opening…" : markPaidLabel}
                </button>
              )}
              {canRedoPO && (
                <button
                  type="button"
                  data-testid={`admin-order-redo-po-${order.id}`}
                  onClick={handleRedoPO}
                  disabled={redoBusy}
                  title="Cancel this invoice and re-issue it as an Agency PO at agency pricing"
                  className="inline-flex items-center gap-1.5 rounded-full bg-brand-gold text-brand-charcoal px-4 py-2 text-[11px] font-bold uppercase tracking-[0.1em] hover:bg-brand-gold/80 transition-colors disabled:opacity-60"
                >
                  {redoBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                  Redo as Agency PO
                </button>
              )}
              {canCancel && (
                <button
                  type="button"
                  data-testid={`admin-order-cancel-${order.id}`}
                  onClick={handleCancelOrder}
                  disabled={cancelBusy}
                  title="Cancel this order and email the customer"
                  className="inline-flex items-center gap-1.5 rounded-full bg-white border border-red-200 text-red-700 px-4 py-2 text-[11px] font-bold uppercase tracking-[0.1em] hover:bg-red-50 transition-colors disabled:opacity-60"
                >
                  {cancelBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <XCircle className="h-3.5 w-3.5" />}
                  Cancel Order
                </button>
              )}
              <button
                type="button"
                data-testid={`admin-order-pdf-${order.id}`}
                onClick={(e) => {
                  e.stopPropagation();
                  downloadInvoicePdf(order);
                }}
                className="inline-flex items-center gap-1.5 rounded-full bg-white border border-brand-charcoal/15 text-brand-charcoal px-4 py-2 text-[11px] font-bold uppercase tracking-[0.1em] hover:border-brand-teal hover:text-brand-teal transition-colors"
              >
                <FileText className="h-3.5 w-3.5" />
                Download PDF
              </button>
              <button
                type="button"
                data-testid={`admin-order-print-${order.id}`}
                onClick={handleSendToPrinter}
                disabled={printing}
                title="Email the ORDER TICKET (kitchen ticket) to the staff mailbox(es) configured in PRINTER_EMAIL"
                className="inline-flex items-center gap-1.5 rounded-full bg-brand-gold/15 border border-brand-gold/40 text-brand-gold px-4 py-2 text-[11px] font-bold uppercase tracking-[0.1em] hover:bg-brand-gold hover:text-brand-charcoal transition-colors disabled:opacity-60"
              >
                {printing ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Printer className="h-3.5 w-3.5" />
                )}
                {printing ? "Sending…" : "Print to Office"}
              </button>
              <button
                type="button"
                data-testid={`admin-order-kitchen-ticket-${order.id}`}
                onClick={(e) => {
                  e.stopPropagation();
                  openKitchenTicketPdf(order);
                }}
                title="Open a one-page kitchen ticket PDF: what to pack for this order, with amounts and instructions"
                className="inline-flex items-center gap-1.5 rounded-full bg-brand-charcoal text-white px-4 py-2 text-[11px] font-bold uppercase tracking-[0.1em] hover:bg-brand-teal transition-colors"
              >
                <ChefHat className="h-3.5 w-3.5" />
                Kitchen Ticket
              </button>
              {order.event_date && (
                <button
                  type="button"
                  data-testid={`admin-order-calendar-${order.id}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    downloadOrderIcs(order);
                  }}
                  title="Download .ics — double-click to add to Outlook / Google / Apple Calendar with 1-week + 3-day reminders"
                  className="inline-flex items-center gap-1.5 rounded-full bg-brand-teal/10 border border-brand-teal/40 text-brand-teal px-4 py-2 text-[11px] font-bold uppercase tracking-[0.1em] hover:bg-brand-teal hover:text-white transition-colors"
                >
                  <CalendarIcon className="h-3.5 w-3.5" />
                  Add to Calendar
                </button>
              )}
              {order.payment_url && order.status !== "paid" && (
                <a
                  href={order.payment_url}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`admin-order-pay-link-${order.id}`}
                  className="inline-flex items-center gap-1.5 rounded-full bg-brand-teal/15 text-brand-teal px-4 py-2 text-[11px] font-bold uppercase tracking-[0.1em] hover:bg-brand-teal hover:text-white transition-colors"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                  Open Pay Link
                </a>
              )}
              {/* Inline agency assign — no modal roundtrip. Falls back to
                  the search modal for large agency lists via a special
                  "Search all…" option. Change fires assign-agency POST. */}
              <label
                data-testid={`admin-order-assign-agency-inline-${order.id}`}
                className={`relative inline-flex items-center gap-2 rounded-full pl-3 pr-2 py-1.5 text-[11px] font-bold uppercase tracking-[0.08em] border transition-colors ${
                  order.user_email
                    ? "bg-brand-teal/10 border-brand-teal/30 text-brand-teal"
                    : "bg-white border-brand-charcoal/15 text-brand-charcoal"
                } ${assigning ? "opacity-60" : "hover:border-brand-teal"}`}
                onClick={(e) => e.stopPropagation()}
              >
                <UserCheck className="h-3.5 w-3.5" />
                <span className="normal-case tracking-normal">Agency:</span>
                <select
                  data-testid={`admin-order-assign-agency-select-${order.id}`}
                  value={order.user_id || ""}
                  onChange={handleInlineAssign}
                  disabled={assigning}
                  className="bg-transparent border-none outline-none text-[11.5px] font-bold pr-4 max-w-[180px] normal-case tracking-normal"
                >
                  <option value="">— Unassigned —</option>
                  {order.user_id
                    && !approvedAgencies.some((a) => a.id === order.user_id) && (
                    // Preserve the currently-linked agency in the list even
                    // if it's not in the loaded approved set (avoids the
                    // dropdown snapping back to "Unassigned" on mount).
                    <option value={order.user_id}>
                      {order.user_email || "Linked (unknown)"}
                    </option>
                  )}
                  {approvedAgencies.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.organization || a.name || a.email}
                    </option>
                  ))}
                  <option value="__modal__">🔍 Search all agencies…</option>
                </select>
              </label>
              {status !== "paid" && (
                <button
                  type="button"
                  data-testid={`admin-order-delete-${order.id}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete?.(order);
                  }}
                  title="Delete this order (e.g. duplicate). Paid orders can't be deleted."
                  className="inline-flex items-center gap-1.5 rounded-full bg-white border border-red-200 text-red-600 px-4 py-2 text-[11px] font-bold uppercase tracking-[0.1em] hover:bg-red-600 hover:text-white hover:border-red-600 transition-colors ml-auto"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Delete
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </li>
  );
};

// ----------------------------------------------------------------------------
// Bulk action toolbar — appears above the orders list when ≥ 1 row is checked.
// Sticky on scroll so it stays accessible while reviewing the list.
// ----------------------------------------------------------------------------
const BulkActionToolbar = ({
  selectedCount,
  selectionMixed,
  selectedTypes,
  onEdit,
  onEmail,
  onDownload,
  onAddToBatch,
  batchEligible,
  onSelectAll,
  onClear,
  totalVisible,
}) => (
  <div
    data-testid="admin-orders-bulk-toolbar"
    className="sticky top-2 z-30 mb-3 rounded-2xl bg-brand-charcoal text-white shadow-lg px-3 sm:px-4 py-2.5 sm:py-3"
  >
    <div className="flex flex-wrap items-center gap-2 sm:gap-3">
      <div className="flex items-center gap-2 min-w-0">
        <span className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-brand-teal text-brand-charcoal font-black text-[12px]">
          {selectedCount}
        </span>
        <span className="font-bold text-[12.5px] tracking-tight">
          {selectedCount === 1 ? "order selected" : "orders selected"}
        </span>
      </div>
      <div className="ml-auto flex flex-wrap items-center gap-2">
        <button
          type="button"
          data-testid="admin-orders-bulk-edit-btn"
          onClick={onEdit}
          className="inline-flex items-center gap-1.5 rounded-full bg-white text-brand-charcoal px-3.5 py-1.5 text-[11px] font-bold uppercase tracking-[0.1em] hover:bg-brand-cream transition-colors"
        >
          <Pencil className="h-3 w-3" />
          Edit
        </button>
        <button
          type="button"
          data-testid="admin-orders-bulk-email-btn"
          onClick={onEmail}
          disabled={selectionMixed}
          title={selectionMixed ? "Select orders of the same payment type" : undefined}
          className="inline-flex items-center gap-1.5 rounded-full bg-brand-teal text-brand-charcoal px-3.5 py-1.5 text-[11px] font-bold uppercase tracking-[0.1em] hover:bg-brand-teal/85 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Mail className="h-3 w-3" />
          Email
        </button>
        <button
          type="button"
          data-testid="admin-orders-bulk-batch-btn"
          onClick={onAddToBatch}
          disabled={!batchEligible}
          title={
            batchEligible
              ? undefined
              : "Only Agency PO orders can be grouped into a batch"
          }
          className="inline-flex items-center gap-1.5 rounded-full bg-brand-gold text-brand-charcoal px-3.5 py-1.5 text-[11px] font-bold uppercase tracking-[0.1em] hover:bg-brand-gold/85 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Layers className="h-3 w-3" />
          To batch
        </button>
        <button
          type="button"
          data-testid="admin-orders-bulk-download-btn"
          onClick={onDownload}
          className="inline-flex items-center gap-1.5 rounded-full bg-white text-brand-charcoal px-3.5 py-1.5 text-[11px] font-bold uppercase tracking-[0.1em] hover:bg-brand-cream transition-colors"
        >
          <FileText className="h-3 w-3" />
          Download
        </button>
        <button
          type="button"
          data-testid="admin-orders-bulk-select-all"
          onClick={onSelectAll}
          className="text-[10.5px] font-bold uppercase tracking-[0.1em] text-white/80 hover:text-white"
        >
          Select all {totalVisible}
        </button>
        <button
          type="button"
          data-testid="admin-orders-bulk-clear"
          onClick={onClear}
          className="text-[10.5px] font-bold uppercase tracking-[0.1em] text-white/65 hover:text-white"
        >
          Clear
        </button>
      </div>
    </div>
    {selectionMixed && (
      <p
        data-testid="admin-orders-bulk-mixed-warning"
        className="mt-2 text-[11px] text-brand-gold"
      >
        Mixed payment types selected ({selectedTypes.join(", ")}). Bulk email requires one type at a time.
      </p>
    )}
  </div>
);

// ----------------------------------------------------------------------------
// Bulk Edit Modal — patch customer name/email/phone across the selected orders.
// ----------------------------------------------------------------------------
const BulkEditModal = ({ orders, onClose, onSubmit }) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  const submit = async () => {
    setError("");
    const patch = {};
    if (name.trim()) patch.customer_name = name.trim();
    if (email.trim()) patch.customer_email = email.trim();
    if (phone.trim()) patch.customer_phone = phone.trim();
    if (Object.keys(patch).length === 0) {
      setError("Fill in at least one field to apply.");
      return;
    }
    setBusy(true);
    try {
      await onSubmit(patch);
    } catch {
      // toast handled by parent
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="admin-bulk-edit-modal"
      className="fixed inset-0 z-[80] flex items-center justify-center p-3 sm:p-6 bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-md max-h-[92vh] flex flex-col rounded-2xl bg-white shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-black/5">
          <div>
            <p className="text-[10.5px] font-bold uppercase tracking-[0.18em] text-brand-teal">
              Bulk Edit
            </p>
            <h3 className="mt-0.5 font-display font-black text-[16px] text-brand-charcoal tracking-tight">
              Apply changes to {orders.length} order{orders.length === 1 ? "" : "s"}
            </h3>
            <p className="mt-0.5 text-[11px] text-brand-charcoal/55">
              Only filled fields are applied. Blank fields are ignored.
            </p>
          </div>
          <button
            type="button"
            data-testid="admin-bulk-edit-close"
            onClick={onClose}
            className="shrink-0 inline-flex items-center justify-center h-8 w-8 rounded-full hover:bg-black/5"
            aria-label="Close"
          >
            <XCircle className="h-4 w-4 text-brand-charcoal/60" />
          </button>
        </div>
        <div className="px-5 py-4 space-y-3 overflow-y-auto">
          <div>
            <label className="text-[11px] font-bold uppercase tracking-[0.14em] text-brand-charcoal/65">
              Customer name
            </label>
            <input
              type="text"
              data-testid="admin-bulk-edit-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="(leave blank to keep current)"
              className="mt-1 w-full h-11 rounded-xl bg-brand-cream/30 border border-black/10 px-3 text-[13px]"
            />
          </div>
          <div>
            <label className="text-[11px] font-bold uppercase tracking-[0.14em] text-brand-charcoal/65">
              Customer email
            </label>
            <input
              type="email"
              data-testid="admin-bulk-edit-email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="(leave blank to keep current)"
              className="mt-1 w-full h-11 rounded-xl bg-brand-cream/30 border border-black/10 px-3 text-[13px]"
            />
          </div>
          <div>
            <label className="text-[11px] font-bold uppercase tracking-[0.14em] text-brand-charcoal/65">
              Customer phone
            </label>
            <input
              type="tel"
              data-testid="admin-bulk-edit-phone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="(leave blank to keep current)"
              className="mt-1 w-full h-11 rounded-xl bg-brand-cream/30 border border-black/10 px-3 text-[13px]"
            />
          </div>
          {error && (
            <div
              data-testid="admin-bulk-edit-error"
              className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-[12px] text-red-700"
            >
              {error}
            </div>
          )}
          <details className="text-[11px] text-brand-charcoal/60">
            <summary className="cursor-pointer font-bold uppercase tracking-[0.12em]">
              Show selected orders ({orders.length})
            </summary>
            <ul className="mt-2 space-y-1 max-h-32 overflow-y-auto font-mono">
              {orders.map((o) => (
                <li key={o.id} className="truncate">
                  · {o.po_number || o.id.slice(0, 8)} — {o.customer_name || "—"} — {fmtMoney(o.total)}
                </li>
              ))}
            </ul>
          </details>
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-black/5 bg-brand-cream/30">
          <button
            type="button"
            onClick={onClose}
            className="rounded-full px-4 py-2 text-[11px] font-bold uppercase tracking-[0.1em] text-brand-charcoal/60 hover:text-brand-charcoal"
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="admin-bulk-edit-submit"
            onClick={submit}
            disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-full bg-brand-charcoal text-white px-5 py-2 text-[11px] font-bold uppercase tracking-[0.1em] hover:bg-black/85 transition-colors disabled:opacity-50"
          >
            {busy ? "Saving…" : "Apply changes"}
          </button>
        </div>
      </div>
    </div>
  );
};

// ----------------------------------------------------------------------------
// Bulk Email Modal — individual or combined send, with extra CC + recipient.
// ----------------------------------------------------------------------------
const BulkEmailModal = ({ orders, selectionMixed, onClose, onSubmit }) => {
  const [mode, setMode] = useState("individual");
  const [extraCcRaw, setExtraCcRaw] = useState("");
  // Combined mode recipient — defaults to the most common customer_email in
  // the selection so the typical "send all 6 to the agency" flow is one tap.
  const [recipient, setRecipient] = useState(() => {
    const tally = {};
    for (const o of orders) {
      const e = (o.customer_email || "").trim();
      if (e) tally[e] = (tally[e] || 0) + 1;
    }
    const top = Object.entries(tally).sort((a, b) => b[1] - a[1])[0];
    return top ? top[0] : "";
  });
  const [projectLabel, setProjectLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  const parsedCc = extraCcRaw
    .split(/[,;\s]+/)
    .map((s) => s.trim())
    .filter(Boolean);
  const total = orders.reduce((sum, o) => sum + (Number(o.total) || 0), 0);

  const submit = async () => {
    setError("");
    if (selectionMixed) {
      setError("Selection mixes payment types. Pick orders of the same type and try again.");
      return;
    }
    const payload = {
      mode,
      extra_cc: parsedCc.length ? parsedCc : undefined,
    };
    if (mode === "combined") {
      if (!recipient.trim()) {
        setError("Recipient email is required for the combined summary.");
        return;
      }
      payload.recipient_email = recipient.trim();
      if (projectLabel.trim()) payload.project_label = projectLabel.trim();
    }
    setBusy(true);
    try {
      await onSubmit(payload);
    } catch {
      // toast handled by parent
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="admin-bulk-email-modal"
      className="fixed inset-0 z-[80] flex items-center justify-center p-3 sm:p-6 bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-lg max-h-[92vh] flex flex-col rounded-2xl bg-white shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-black/5">
          <div className="min-w-0">
            <p className="text-[10.5px] font-bold uppercase tracking-[0.18em] text-brand-teal">
              Bulk Email
            </p>
            <h3 className="mt-0.5 font-display font-black text-[16px] text-brand-charcoal tracking-tight">
              Send {orders.length} invoice{orders.length === 1 ? "" : "s"} · {fmtMoney(total)}
            </h3>
            <p className="mt-0.5 text-[11px] text-brand-charcoal/55">
              Always CC&apos;d to <strong className="text-brand-charcoal/75">info@btwnyc.com</strong>. Add more below.
            </p>
          </div>
          <button
            type="button"
            data-testid="admin-bulk-email-close"
            onClick={onClose}
            className="shrink-0 inline-flex items-center justify-center h-8 w-8 rounded-full hover:bg-black/5"
            aria-label="Close"
          >
            <XCircle className="h-4 w-4 text-brand-charcoal/60" />
          </button>
        </div>
        <div className="px-5 py-4 space-y-4 overflow-y-auto">
          <div>
            <label className="text-[11px] font-bold uppercase tracking-[0.14em] text-brand-charcoal/65">
              Send mode
            </label>
            <div className="mt-1.5 inline-flex rounded-full bg-brand-cream/40 border border-black/5 p-1">
              <button
                type="button"
                data-testid="admin-bulk-email-mode-individual"
                onClick={() => setMode("individual")}
                className={`rounded-full px-3.5 py-1.5 text-[11px] font-bold uppercase tracking-[0.1em] transition-colors ${
                  mode === "individual"
                    ? "bg-brand-charcoal text-white"
                    : "text-brand-charcoal/60 hover:text-brand-charcoal"
                }`}
              >
                Individual ({orders.length}×)
              </button>
              <button
                type="button"
                data-testid="admin-bulk-email-mode-combined"
                onClick={() => setMode("combined")}
                className={`rounded-full px-3.5 py-1.5 text-[11px] font-bold uppercase tracking-[0.1em] transition-colors ${
                  mode === "combined"
                    ? "bg-brand-charcoal text-white"
                    : "text-brand-charcoal/60 hover:text-brand-charcoal"
                }`}
              >
                Combined (1×)
              </button>
            </div>
            <p className="mt-1.5 text-[11px] text-brand-charcoal/55 leading-snug">
              {mode === "individual"
                ? "One email per invoice, sent to each order's own customer email."
                : "One consolidated summary email to the recipient below, listing all selected invoices in a single table."}
            </p>
          </div>

          {mode === "combined" && (
            <>
              <div>
                <label className="text-[11px] font-bold uppercase tracking-[0.14em] text-brand-charcoal/65">
                  Recipient email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  data-testid="admin-bulk-email-recipient"
                  value={recipient}
                  onChange={(e) => setRecipient(e.target.value)}
                  placeholder="agency@example.com"
                  className="mt-1 w-full h-11 rounded-xl bg-brand-cream/30 border border-black/10 px-3 text-[13px]"
                />
              </div>
              <div>
                <label className="text-[11px] font-bold uppercase tracking-[0.14em] text-brand-charcoal/65">
                  Project label <span className="text-brand-charcoal/40 normal-case">(optional · shows in subject line)</span>
                </label>
                <input
                  type="text"
                  data-testid="admin-bulk-email-project"
                  value={projectLabel}
                  onChange={(e) => setProjectLabel(e.target.value)}
                  placeholder="e.g. P469X Breakfast"
                  className="mt-1 w-full h-11 rounded-xl bg-brand-cream/30 border border-black/10 px-3 text-[13px]"
                />
              </div>
            </>
          )}

          <div>
            <label className="text-[11px] font-bold uppercase tracking-[0.14em] text-brand-charcoal/65">
              Extra CC <span className="text-brand-charcoal/40 normal-case">(comma-separated)</span>
            </label>
            <textarea
              data-testid="admin-bulk-email-cc"
              value={extraCcRaw}
              onChange={(e) => setExtraCcRaw(e.target.value)}
              placeholder="finance@agency.com, ops@agency.com"
              rows={2}
              className="mt-1 w-full rounded-xl bg-brand-cream/30 border border-black/10 px-3 py-2 text-[13px]"
            />
            {parsedCc.length > 0 && (
              <p className="mt-1 text-[10.5px] text-brand-charcoal/55">
                Will CC: <strong className="text-brand-charcoal/75">info@btwnyc.com</strong>
                {", "}{parsedCc.join(", ")}
              </p>
            )}
          </div>

          {error && (
            <div
              data-testid="admin-bulk-email-error"
              className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-[12px] text-red-700"
            >
              {error}
            </div>
          )}

          <details className="text-[11px] text-brand-charcoal/60">
            <summary className="cursor-pointer font-bold uppercase tracking-[0.12em]">
              Selected invoices ({orders.length})
            </summary>
            <ul className="mt-2 space-y-1 max-h-32 overflow-y-auto font-mono">
              {orders.map((o) => (
                <li key={o.id} className="truncate">
                  · {o.po_number || o.id.slice(0, 8)} — {o.customer_email || "—"} — {fmtMoney(o.total)}
                </li>
              ))}
            </ul>
          </details>
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-black/5 bg-brand-cream/30">
          <button
            type="button"
            onClick={onClose}
            className="rounded-full px-4 py-2 text-[11px] font-bold uppercase tracking-[0.1em] text-brand-charcoal/60 hover:text-brand-charcoal"
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="admin-bulk-email-submit"
            onClick={submit}
            disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-full bg-brand-teal text-brand-charcoal px-5 py-2 text-[11px] font-bold uppercase tracking-[0.1em] hover:bg-brand-teal/85 transition-colors disabled:opacity-50"
          >
            <SendIcon className="h-3 w-3" />
            {busy ? "Sending…" : mode === "combined" ? "Send Summary" : `Send ${orders.length} Emails`}
          </button>
        </div>
      </div>
    </div>
  );
};

// ----------------------------------------------------------------------------
// Bulk Add-to-Batch Modal — groups the selected agency PO orders into either
// an existing batch (picked from a live list) or a brand-new batch (with an
// optional custom name). Only enabled for homogeneous agency_po selections.
// ----------------------------------------------------------------------------
const BulkAddToBatchModal = ({ orders, onClose, onSubmit }) => {
  const [mode, setMode] = useState("new"); // "new" | "existing"
  const [existingBatches, setExistingBatches] = useState([]);
  const [existingBusy, setExistingBusy] = useState(true);
  const [selectedBatchId, setSelectedBatchId] = useState("");
  const [newBatchName, setNewBatchName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/admin/batches");
        if (cancelled) return;
        setExistingBatches(data.items || []);
        // Pre-select the newest batch to make the common case one-click.
        if ((data.items || []).length) setSelectedBatchId(data.items[0].id);
      } catch (e) {
        if (!cancelled) setError(formatApiErrorDetail(e?.response?.data?.detail) || e?.message || "Could not load batches");
      } finally {
        if (!cancelled) setExistingBusy(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const total = orders.reduce((sum, o) => sum + (Number(o.total) || 0), 0);

  const submit = async () => {
    setError("");
    const payload = {};
    if (mode === "existing") {
      if (!selectedBatchId) {
        setError("Pick a batch to attach the selected orders to.");
        return;
      }
      payload.batch_id = selectedBatchId;
    } else {
      // New batch — name is optional; if blank the server picks a default.
      const nm = newBatchName.trim();
      payload.new_batch_name = nm || `Batch · ${orders.length} order${orders.length === 1 ? "" : "s"}`;
    }
    setBusy(true);
    try {
      await onSubmit(payload);
    } catch {
      // toast handled by parent
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="admin-bulk-batch-modal"
      className="fixed inset-0 z-50 bg-brand-charcoal/70 backdrop-blur-sm flex items-center justify-center p-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="w-full max-w-[520px] rounded-3xl bg-white shadow-2xl overflow-hidden">
        <div className="bg-brand-charcoal text-white px-6 py-5">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-brand-gold">
                Add to Batch
              </p>
              <h3 className="font-display font-black text-[22px] tracking-tight mt-1">
                Group {orders.length} order{orders.length === 1 ? "" : "s"}
              </h3>
              <p className="text-[12px] text-white/65 mt-1">
                Combined total ${total.toFixed(2)} · All Agency PO
              </p>
            </div>
            <button
              type="button"
              data-testid="admin-bulk-batch-close"
              onClick={onClose}
              className="p-2 rounded-full text-white/70 hover:text-white hover:bg-white/10"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-5">
          {/* Mode toggle */}
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              data-testid="admin-bulk-batch-mode-new"
              onClick={() => setMode("new")}
              className={`rounded-2xl border-2 px-4 py-3 text-left transition-colors ${
                mode === "new"
                  ? "border-brand-teal bg-brand-teal/8"
                  : "border-black/10 bg-brand-cream/30 hover:border-black/20"
              }`}
            >
              <p className="font-bold text-[13px] text-brand-charcoal">New batch</p>
              <p className="text-[11px] text-brand-charcoal/60 mt-0.5">
                Mint a fresh batch from these orders
              </p>
            </button>
            <button
              type="button"
              data-testid="admin-bulk-batch-mode-existing"
              onClick={() => setMode("existing")}
              disabled={existingBatches.length === 0}
              title={existingBatches.length === 0 ? "No existing batches yet" : undefined}
              className={`rounded-2xl border-2 px-4 py-3 text-left transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                mode === "existing"
                  ? "border-brand-teal bg-brand-teal/8"
                  : "border-black/10 bg-brand-cream/30 hover:border-black/20"
              }`}
            >
              <p className="font-bold text-[13px] text-brand-charcoal">Existing batch</p>
              <p className="text-[11px] text-brand-charcoal/60 mt-0.5">
                Attach to a batch you already created
              </p>
            </button>
          </div>

          {/* Mode content */}
          {mode === "new" ? (
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-[0.14em] text-brand-charcoal/65 mb-1.5">
                Batch name (optional)
              </label>
              <Input
                data-testid="admin-bulk-batch-new-name"
                value={newBatchName}
                onChange={(e) => setNewBatchName(e.target.value)}
                placeholder={`e.g. ${orders[0]?.customer_name || "P469X"} · ${orders.length} drops`}
                maxLength={160}
                className="h-11 rounded-xl bg-brand-cream/40 border-black/10"
              />
              <p className="mt-1.5 text-[11.5px] text-brand-charcoal/55">
                Leave blank to auto-name based on the selection.
              </p>
            </div>
          ) : (
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-[0.14em] text-brand-charcoal/65 mb-1.5">
                Select batch
              </label>
              {existingBusy ? (
                <div className="rounded-xl bg-brand-cream/40 border border-black/10 px-3 py-3 text-[12.5px] text-brand-charcoal/60 flex items-center gap-2">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  Loading batches…
                </div>
              ) : existingBatches.length === 0 ? (
                <p className="text-[12.5px] text-brand-charcoal/60">
                  No batches yet — create one first.
                </p>
              ) : (
                <select
                  data-testid="admin-bulk-batch-existing-select"
                  value={selectedBatchId}
                  onChange={(e) => setSelectedBatchId(e.target.value)}
                  className="w-full h-11 rounded-xl bg-brand-cream/40 border border-black/10 px-3 text-[13px] text-brand-charcoal"
                >
                  {existingBatches.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.ref || b.id.slice(0, 8)} · {b.name || "—"} · {b.drops_count || 0} drop{b.drops_count === 1 ? "" : "s"} · ${Number(b.total || 0).toFixed(2)}
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}

          {error && (
            <div
              data-testid="admin-bulk-batch-error"
              role="alert"
              className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-[13px] text-red-700"
            >
              {error}
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-3 pt-1">
            <button
              type="button"
              data-testid="admin-bulk-batch-submit"
              onClick={submit}
              disabled={busy || (mode === "existing" && !selectedBatchId)}
              className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-brand-charcoal text-white px-6 py-3 text-[12px] font-bold uppercase tracking-[0.12em] hover:bg-brand-teal transition-colors disabled:opacity-60"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Layers className="h-4 w-4" />}
              {mode === "existing" ? "Attach to batch" : "Create batch"}
            </button>
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-white border border-black/10 text-brand-charcoal/70 px-5 py-3 text-[12px] font-bold uppercase tracking-[0.12em] hover:bg-brand-cream disabled:opacity-60"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const PartnershipRow = ({ inquiry }) => {
  const [open, setOpen] = useState(false);
  return (
    <li
      data-testid={`admin-partnership-row-${inquiry.id}`}
      className="rounded-2xl bg-white border border-black/5 overflow-hidden"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full text-left hover:bg-brand-cream/40 transition-colors"
      >
        {/* MOBILE */}
        <div className="md:hidden flex flex-col gap-3 px-4 py-4">
          <div className="flex items-start gap-3">
            <span className="mt-1 text-brand-charcoal/40 shrink-0">
              {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </span>
            <div className="min-w-0 flex-1">
              <p className="font-bold text-[14px] text-brand-charcoal break-words leading-tight">
                {inquiry.name}
              </p>
              <p className="text-[12px] text-brand-charcoal/55 break-all leading-snug mt-0.5">
                {inquiry.email}
              </p>
              {inquiry.organization && (
                <p className="text-[12px] text-brand-charcoal/70 mt-1 break-words">
                  {inquiry.organization}
                </p>
              )}
            </div>
            <div className="text-right shrink-0">
              <p className="text-[10.5px] font-bold uppercase tracking-[0.1em] text-brand-charcoal/55">
                {inquiry.reference_id || "—"}
              </p>
              <p className="text-[10.5px] text-brand-charcoal/55 mt-1">
                {fmtDate(inquiry.created_at).split(",")[0]}
              </p>
            </div>
          </div>
          <div className="pl-7">
            <span className="inline-flex items-center rounded-full bg-brand-teal/12 text-brand-teal px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.1em]">
              {inquiry.partnership_type}
            </span>
          </div>
        </div>

        {/* DESKTOP */}
        <div className="hidden md:grid grid-cols-12 items-center gap-3 px-4 py-3.5">
          <div className="col-span-1 flex justify-center text-brand-charcoal/40">
            {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </div>
          <div className="col-span-3 min-w-0">
            <p className="font-bold text-[13.5px] text-brand-charcoal truncate">{inquiry.name}</p>
            <p className="text-[11.5px] text-brand-charcoal/55 truncate">{inquiry.email}</p>
          </div>
          <div className="col-span-3 min-w-0 text-[12px] text-brand-charcoal/70 truncate">
            {inquiry.organization || "—"}
          </div>
          <div className="col-span-3 min-w-0 text-[12px]">
            <span className="inline-flex items-center rounded-full bg-brand-teal/12 text-brand-teal px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.1em]">
              {inquiry.partnership_type}
            </span>
          </div>
          <div className="col-span-1 text-[10.5px] font-bold text-brand-charcoal/55 uppercase tracking-[0.1em]">
            {inquiry.reference_id || "—"}
          </div>
          <div className="col-span-1 text-right text-[11px] text-brand-charcoal/55">
            {fmtDate(inquiry.created_at).split(",")[0]}
          </div>
        </div>
      </button>
      {open && (
        <div className="border-t border-black/5 bg-brand-cream/30 px-4 sm:px-5 py-5 sm:py-6 text-[12.5px] space-y-2.5">
          {inquiry.title && (
            <p>
              <span className="text-brand-charcoal/55">Title: </span>
              <span className="text-brand-charcoal">{inquiry.title}</span>
            </p>
          )}
          {inquiry.organization_type && (
            <p>
              <span className="text-brand-charcoal/55">Org type: </span>
              <span className="text-brand-charcoal">{inquiry.organization_type}</span>
            </p>
          )}
          {inquiry.phone && (
            <p>
              <span className="text-brand-charcoal/55">Phone: </span>
              <span className="text-brand-charcoal">{inquiry.phone}</span>
            </p>
          )}
          {inquiry.website && (
            <p>
              <span className="text-brand-charcoal/55">Website: </span>
              <a
                href={inquiry.website}
                target="_blank"
                rel="noreferrer"
                className="text-brand-teal hover:underline inline-flex items-center gap-1"
              >
                {inquiry.website}
                <ExternalLink className="h-3 w-3" />
              </a>
            </p>
          )}
          {inquiry.message && (
            <div className="rounded-xl bg-white border border-black/5 p-3.5 mt-2">
              <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-brand-charcoal/55">
                Message
              </p>
              <p className="mt-1.5 text-[13px] text-brand-charcoal/85 whitespace-pre-wrap leading-relaxed">
                {inquiry.message}
              </p>
            </div>
          )}
        </div>
      )}
    </li>
  );
};

const QuoteRow = ({ quote, onConvertToInvoice }) => {
  const [open, setOpen] = useState(false);
  const isItemQuote = quote.kind === "item_quote";
  const isSmallOrder = quote.kind === "small_order" || isItemQuote;
  const isPOInvoice = (quote.reason || "").toLowerCase() === "po_invoice";
  const items = Array.isArray(quote.items) ? quote.items : [];
  const estimate = typeof quote.subtotal_estimate === "number" ? quote.subtotal_estimate : null;
  return (
    <li
      data-testid={`admin-quote-row-${quote.id}`}
      className="rounded-2xl bg-white border border-black/5 overflow-hidden"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full text-left hover:bg-brand-cream/40 transition-colors"
      >
        {/* MOBILE */}
        <div className="md:hidden flex flex-col gap-3 px-4 py-4">
          <div className="flex items-start gap-3">
            <span className="mt-1 text-brand-charcoal/40 shrink-0">
              {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5 flex-wrap">
                <p className="font-bold text-[14px] text-brand-charcoal break-words leading-tight">
                  {quote.name}
                </p>
                {isSmallOrder && (
                  <span
                    data-testid={`admin-quote-small-order-badge-${quote.id}`}
                    className={`inline-flex items-center rounded-full px-2 py-0.5 text-[9.5px] font-black uppercase tracking-[0.1em] ${
                      isPOInvoice || isItemQuote
                        ? "bg-brand-teal text-white"
                        : "bg-brand-gold text-brand-charcoal"
                    }`}
                  >
                    {isItemQuote ? "Item Quote" : isPOInvoice ? "Invoice for PO" : "Small Order"}
                  </span>
                )}
              </div>
              <p className="text-[12px] text-brand-charcoal/55 break-all leading-snug mt-0.5">
                {quote.email}
              </p>
            </div>
            <p className="text-[10.5px] text-brand-charcoal/55 shrink-0 text-right">
              {fmtDate(quote.created_at).split(",")[0]}
            </p>
          </div>
          <div className="pl-7 flex flex-wrap items-center gap-1.5">
            {quote.event_date && (
              <span className="inline-flex items-center rounded-full bg-brand-cream text-brand-charcoal/75 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.08em]">
                {quote.event_date}
              </span>
            )}
            {quote.guests && (
              <span className="inline-flex items-center rounded-full bg-brand-cream text-brand-charcoal/75 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.08em]">
                {quote.guests} guests
              </span>
            )}
            {isSmallOrder && items.length > 0 && (
              <span className="inline-flex items-center rounded-full bg-brand-teal/15 text-brand-teal px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.08em]">
                {items.length} items{estimate != null ? ` · $${estimate.toFixed(2)}` : ""}
              </span>
            )}
            {!isSmallOrder && (quote.package_interest || quote.event_type) && (
              <span className="inline-flex items-center rounded-full bg-brand-teal/12 text-brand-teal px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.08em]">
                {quote.package_interest || quote.event_type}
              </span>
            )}
          </div>
        </div>

        {/* DESKTOP */}
        <div className="hidden md:grid grid-cols-12 items-center gap-3 px-4 py-3.5">
          <div className="col-span-1 flex justify-center text-brand-charcoal/40">
            {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </div>
          <div className="col-span-3 min-w-0">
            <div className="flex items-center gap-1.5">
              <p className="font-bold text-[13.5px] text-brand-charcoal truncate">{quote.name}</p>
              {isSmallOrder && (
                <span
                  data-testid={`admin-quote-small-order-badge-${quote.id}`}
                  className={`inline-flex items-center rounded-full px-2 py-0.5 text-[9.5px] font-black uppercase tracking-[0.1em] shrink-0 ${
                    isPOInvoice || isItemQuote
                      ? "bg-brand-teal text-white"
                      : "bg-brand-gold text-brand-charcoal"
                  }`}
                >
                  {isItemQuote ? "Item Quote" : isPOInvoice ? "Invoice for PO" : "Small Order"}
                </span>
              )}
            </div>
            <p className="text-[11.5px] text-brand-charcoal/55 truncate">{quote.email}</p>
          </div>
          <div className="col-span-2 text-[12px] text-brand-charcoal/70">
            {quote.event_date || "—"}
          </div>
          <div className="col-span-2 text-[12px] text-brand-charcoal/70 truncate">
            {isSmallOrder && items.length
              ? `${items.length} items${estimate != null ? ` · $${estimate.toFixed(2)}` : ""}`
              : quote.guests
              ? `${quote.guests} guests`
              : "—"}
          </div>
          <div className="col-span-3 min-w-0 text-[12px] text-brand-charcoal/70 truncate">
            {isSmallOrder
              ? (items[0]?.name || "Small delivery order")
              : (quote.package_interest || quote.event_type || "—")}
          </div>
          <div className="col-span-1 text-right text-[11px] text-brand-charcoal/55">
            {fmtDate(quote.created_at).split(",")[0]}
          </div>
        </div>
      </button>
      {open && (
        <div className="border-t border-black/5 bg-brand-cream/30 px-4 sm:px-5 py-5 sm:py-6 text-[12.5px] grid sm:grid-cols-2 gap-5">
          <div className="space-y-1.5">
            {quote.phone && <p><span className="text-brand-charcoal/55">Phone: </span>{quote.phone}</p>}
            {quote.organization && <p><span className="text-brand-charcoal/55">Org: </span>{quote.organization}</p>}
            {quote.event_time && <p><span className="text-brand-charcoal/55">Time: </span>{quote.event_time}</p>}
            {quote.event_location && <p><span className="text-brand-charcoal/55">Location: </span>{quote.event_location}</p>}
            {(quote.address || quote.city) && (
              <p>
                <span className="text-brand-charcoal/55">Address: </span>
                {[quote.address, quote.address_line2, quote.city, quote.state, quote.zip_code]
                  .filter(Boolean)
                  .join(", ")}
              </p>
            )}
            {quote.on_site_contact_name && (
              <p>
                <span className="text-brand-charcoal/55">On-site: </span>
                {quote.on_site_contact_name}
                {quote.on_site_contact_phone ? ` · ${quote.on_site_contact_phone}` : ""}
              </p>
            )}
            {quote.event_type && <p><span className="text-brand-charcoal/55">Event type: </span>{quote.event_type}</p>}
            {quote.service_style && <p><span className="text-brand-charcoal/55">Style: </span>{quote.service_style}</p>}
            {quote.budget && <p><span className="text-brand-charcoal/55">Budget: </span>{quote.budget}</p>}
            {quote.reference_id && (
              <p><span className="text-brand-charcoal/55">Ref: </span>{quote.reference_id}</p>
            )}
            {(quote.has_receiving_personnel === true || quote.has_receiving_personnel === false ||
              quote.has_delivery_cart === true || quote.has_delivery_cart === false) && (
              <p className="pt-1 flex flex-wrap gap-1.5">
                {quote.has_receiving_personnel === true && (
                  <span className="inline-flex items-center rounded-full bg-brand-teal/15 text-brand-teal px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.1em]">Staff on-site</span>
                )}
                {quote.has_receiving_personnel === false && (
                  <span className="inline-flex items-center rounded-full bg-brand-charcoal/10 text-brand-charcoal/70 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.1em]">No staff</span>
                )}
                {quote.has_delivery_cart === true && (
                  <span className="inline-flex items-center rounded-full bg-brand-teal/15 text-brand-teal px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.1em]">Cart on-site</span>
                )}
                {quote.has_delivery_cart === false && (
                  <span className="inline-flex items-center rounded-full bg-brand-charcoal/10 text-brand-charcoal/70 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.1em]">No cart</span>
                )}
              </p>
            )}
            {quote.delivery_instructions && (
              <div className="mt-1.5 rounded-lg bg-brand-gold/10 border border-brand-gold/30 px-2.5 py-1.5 text-[11.5px] text-brand-charcoal/80 whitespace-pre-wrap">
                <span className="font-bold text-brand-charcoal">Access notes:</span>{" "}
                {quote.delivery_instructions}
              </div>
            )}
            {isSmallOrder && (
              <button
                type="button"
                data-testid={`admin-quote-convert-${quote.id}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onConvertToInvoice?.(quote);
                }}
                className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-brand-charcoal text-white px-4 py-2 text-[11px] font-bold uppercase tracking-[0.1em] hover:bg-brand-teal transition-colors"
              >
                <Plus className="h-3.5 w-3.5" />
                Convert to Invoice
              </button>
            )}
          </div>
          <div className="space-y-3">
            {isSmallOrder && items.length > 0 && (
              <div className="rounded-xl bg-white border border-black/5 p-3.5">
                <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-brand-charcoal/55">
                  Cart items ({items.length})
                </p>
                <ul className="mt-2 space-y-1.5">
                  {items.map((it, idx) => (
                    <li
                      key={`${it.package_id}-${idx}`}
                      className="flex items-baseline justify-between gap-3 text-[12.5px] text-brand-charcoal/85"
                    >
                      <span className="min-w-0 truncate">
                        <span className="font-bold text-brand-charcoal">×{it.quantity}</span>{" "}
                        {it.name || it.package_id}
                      </span>
                      <span className="tabular-nums font-bold shrink-0">
                        ${(Number(it.line_total) || 0).toFixed(2)}
                      </span>
                    </li>
                  ))}
                </ul>
                {estimate != null && (
                  <p className="mt-2 pt-2 border-t border-black/5 flex items-baseline justify-between text-[12.5px] font-bold text-brand-charcoal">
                    <span>Customer estimate</span>
                    <span className="tabular-nums">${estimate.toFixed(2)}</span>
                  </p>
                )}
              </div>
            )}
            {isItemQuote && (
              <div
                data-testid={`admin-quote-item-details-${quote.id}`}
                className="rounded-xl bg-white border border-black/5 p-3.5 flex flex-wrap gap-x-6 gap-y-1 text-[12.5px] text-brand-charcoal/85"
              >
                <span><b>Guests:</b> {quote.guests || "—"}</span>
                <span><b>Fulfillment:</b> {quote.fulfillment ? quote.fulfillment[0].toUpperCase() + quote.fulfillment.slice(1) : "—"}</span>
                {quote.zip_code && <span><b>ZIP:</b> {quote.zip_code}</span>}
                {quote.organization && <span><b>Org:</b> {quote.organization}</span>}
              </div>
            )}
            {quote.message && (
              <div className="rounded-xl bg-white border border-black/5 p-3.5">
                <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-brand-charcoal/55">
                  Notes
                </p>
                <p className="mt-1.5 text-[13px] text-brand-charcoal/85 whitespace-pre-wrap leading-relaxed">
                  {quote.message}
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </li>
  );
};

const AdminOrdersPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const authChecking = user === null;
  const isAdmin = user && user.role === "admin";
  // Read the initial tab (and optional agency highlight / status filter) from
  // the URL query. Emails sent to admins deep-link into this page, e.g.
  // `/admin/orders?tab=agencies&status=pending&highlight=applicant@example.com`
  // → the Agencies tab opens with a pending filter + the applicant's row
  // gets an amber ring so the admin doesn't have to scroll to find them.
  const _initialParams = new URLSearchParams(typeof window !== "undefined" ? window.location.search : "");
  const _allowedTabs = new Set([
    "orders", "polaris", "batches", "partnerships", "quotes", "agencies",
    "gallery", "packages", "menu", "hiring", "storage", "newsletter", "activity",
    "private-customers", "tax-exempt", "pay-approvals", "holiday",
  ]);
  const _urlTab = _initialParams.get("tab");
  const _urlHighlight = (_initialParams.get("highlight") || "").toLowerCase();
  const _urlStatus = _initialParams.get("status");
  const _urlType = _initialParams.get("type");
  const _urlFrom = _initialParams.get("from") || "";
  const _urlTo = _initialParams.get("to") || "";
  const [tab, setTab] = useState(_allowedTabs.has(_urlTab) ? _urlTab : "orders");
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [partnerships, setPartnerships] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState(_urlStatus || "all");
  const [typeFilter, setTypeFilter] = useState(_urlType || "all");
  // Date range + sort — user asked for view-by-date + sort controls on the
  // Orders tab. `datePreset` drives the from/to inputs automatically; when
  // set to "custom" the user picks their own range in the two date inputs.
  const [datePreset, setDatePreset] = useState(_urlFrom || _urlTo ? "custom" : "all");
  const [dateFrom, setDateFrom] = useState(_urlFrom);
  const [dateTo, setDateTo] = useState(_urlTo);
  const [sortBy, setSortBy] = useState("newest");
  // "chronological" (flat list, respects sortBy) OR "by_agency" (grouped
  // and collapsible per agency, unassigned orders in an "Unassigned" bucket).
  const [viewMode, setViewMode] = useState("chronological");
  // Track which agency groups are collapsed in by_agency view — start
  // all expanded so operators see everything.
  const [collapsedAgencies, setCollapsedAgencies] = useState(() => new Set());
  const [menu, setMenu] = useState({ items: [] });
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [manualInvoiceOpen, setManualInvoiceOpen] = useState(false);
  // When set, opens the ManualInvoiceModal pre-filled with an agency's
  // contact info (Create Invoice button on the Agencies drill-down).
  const [manualInvoiceContact, setManualInvoiceContact] = useState(null);
  // Order → Agency attach modal state.
  const [assignAgencyOrder, setAssignAgencyOrder] = useState(null);
  // Bumped whenever an agency-linked invoice is created so all expanded
  // drill-down panels re-fetch and show the new invoice.
  const [agencyDrillRefreshTick, setAgencyDrillRefreshTick] = useState(0);
  const [markPaidOrder, setMarkPaidOrder] = useState(null);
  const [sendInvoiceOrder, setSendInvoiceOrder] = useState(null);
  // Pre-fills the OrderEditorModal in *create* mode from a customer's cart
  // quote — items + contact + address ready to review, admin sets delivery fee.
  const [editorPrefill, setEditorPrefill] = useState(null);
  const [toast, setToast] = useState("");
  // Bulk-selection — Set of order ids currently checked in the Orders tab.
  const [selectedOrderIds, setSelectedOrderIds] = useState(() => new Set());
  // Which bulk modal is open: "edit" | "email" | null.
  const [bulkMode, setBulkMode] = useState(null);
  const [experiences, setExperiences] = useState([]);
  const [expEditorOpen, setExpEditorOpen] = useState(false);
  const [editingExp, setEditingExp] = useState(null);
  const [packages, setPackages] = useState([]);
  const [pkgEditorOpen, setPkgEditorOpen] = useState(false);
  const [editingPkg, setEditingPkg] = useState(null);
  const [menuItems, setMenuItems] = useState([]);
  const [menuCategories, setMenuCategories] = useState([]);
  const [menuMarkup, setMenuMarkup] = useState(1.2);
  const [menuMarkupByCat, setMenuMarkupByCat] = useState({});
  const [menuCategoryFilter, setMenuCategoryFilter] = useState("all");
  const [menuEditorOpen, setMenuEditorOpen] = useState(false);
  const [editingMenuItem, setEditingMenuItem] = useState(null);
  const [activity, setActivity] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [agencyStatusFilter, setAgencyStatusFilter] = useState("pending");
  const [agencyActionBusy, setAgencyActionBusy] = useState("");

  // (isAdmin computed above from user)

  // ---- Data loader (extracted so we can re-invoke on tab change + interval)
  // Auto-refresh strategy: refetch the endpoints relevant to the active tab
  // whenever the user navigates to it, plus a lightweight background poll
  // every 60s while the tab is visible. `silent=true` skips the loading
  // spinner so tab switches feel instant instead of blanking the page.
  const loadAdminData = async ({ silent = false, mountedRef = { current: true }, tabScope = null } = {}) => {
    if (!isAdmin) return;
    if (!silent) setLoading(true);
    setError("");
    try {
      // Map of tab → endpoints. `null` means "load everything" (initial mount).
      const tabToKeys = {
        orders: ["stats", "orders", "menu", "adminMenu"],
        batches: ["orders"],
        partnerships: ["partnerships"],
        quotes: ["quotes", "menu"],
        agencies: ["agencies"],
        gallery: ["experiences"],
        packages: ["packages"],
        menu: ["adminMenu"],
        activity: ["activity"],
        hiring: [],
        polaris: [],
        storage: [],
        newsletter: [],
      };
      const keys = tabScope ? new Set(tabToKeys[tabScope] || []) : null;
      const want = (k) => keys === null || keys.has(k);
      const promises = [];
      const labels = [];
      const push = (label, p) => { labels.push(label); promises.push(p); };
      if (want("stats")) push("stats", api.get("/admin/stats"));
      if (want("orders")) push("orders", api.get("/admin/orders?limit=200"));
      if (want("partnerships")) push("partnerships", api.get("/admin/partnership-inquiries?limit=200"));
      if (want("quotes")) push("quotes", api.get("/admin/quotes?limit=200"));
      if (want("menu")) push("menu", api.get("/menu", { params: { include_hidden: 1 } }));
      if (want("experiences")) push("experiences", api.get("/admin/experiences"));
      if (want("packages")) push("packages", api.get("/admin/packages"));
      if (want("adminMenu")) push("adminMenu", api.get("/admin/menu"));
      if (want("activity")) push("activity", api.get("/admin/activity-log?limit=80"));
      if (want("agencies")) push("agencies", api.get("/admin/agencies?status=all&limit=300"));
      const results = await Promise.all(promises);
      if (!mountedRef.current) return;
      results.forEach((r, i) => {
        const label = labels[i];
        const data = r.data;
        if (label === "stats") setStats(data);
        else if (label === "orders") setOrders(data.items || []);
        else if (label === "partnerships") setPartnerships(data.items || []);
        else if (label === "quotes") setQuotes(data.items || []);
        else if (label === "menu") setMenu(data || { items: [] });
        else if (label === "experiences") setExperiences(data.items || []);
        else if (label === "packages") setPackages(data.items || []);
        else if (label === "adminMenu") {
          setMenuItems(data.items || []);
          setMenuCategories(data.categories_order || []);
          setMenuMarkup(data.agency_markup || 1.2);
          setMenuMarkupByCat(data.agency_markup_by_category || {});
        }
        else if (label === "activity") setActivity(data.items || []);
        else if (label === "agencies") setAgencies(data.items || []);
      });
    } catch (e) {
      if (!mountedRef.current) return;
      if (!silent) setError(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load admin data");
    } finally {
      if (mountedRef.current && !silent) setLoading(false);
    }
  };

  // Initial load — fetch everything on first mount.
  useEffect(() => {
    if (!isAdmin) return;
    const mountedRef = { current: true };
    loadAdminData({ silent: false, mountedRef, tabScope: null });
    return () => { mountedRef.current = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  // Re-fetch the active tab whenever it changes AND every 60s in background
  // while the tab is visible — so switching Orders → Quotes → Orders always
  // shows fresh data without a manual reload. Skipped on the very first
  // render because the initial full-load effect above already handled it.
  const _hasMountedRef = useRef(false);
  useEffect(() => {
    if (!isAdmin) return;
    const mountedRef = { current: true };
    // Skip the immediate refetch on the very first render (the initial
    // full-load effect above already fetched everything).
    if (_hasMountedRef.current) {
      loadAdminData({ silent: true, mountedRef, tabScope: tab });
    } else {
      _hasMountedRef.current = true;
    }
    // Background poll — 60s cadence, only when the browser tab is visible.
    const intervalId = setInterval(() => {
      if (document.visibilityState !== "visible") return;
      loadAdminData({ silent: true, mountedRef, tabScope: tab });
    }, 60000);
    return () => {
      mountedRef.current = false;
      clearInterval(intervalId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, isAdmin]);


  // Convert a date preset into a concrete [from, to] window (inclusive on
  // both ends, comparing ISO date-strings YYYY-MM-DD).
  const dateWindow = useMemo(() => {
    const today = new Date();
    const iso = (d) => d.toISOString().slice(0, 10);
    const startOf = (d) => { const c = new Date(d); c.setHours(0, 0, 0, 0); return c; };
    if (datePreset === "today") {
      const t = iso(today);
      return { from: t, to: t };
    }
    if (datePreset === "week") {
      const d = startOf(today);
      d.setDate(d.getDate() - d.getDay()); // Sunday-start week
      return { from: iso(d), to: iso(today) };
    }
    if (datePreset === "month") {
      const d = new Date(today.getFullYear(), today.getMonth(), 1);
      return { from: iso(d), to: iso(today) };
    }
    if (datePreset === "last30") {
      const d = new Date(today);
      d.setDate(d.getDate() - 30);
      return { from: iso(d), to: iso(today) };
    }
    if (datePreset === "custom") {
      return { from: dateFrom || null, to: dateTo || null };
    }
    return { from: null, to: null };
  }, [datePreset, dateFrom, dateTo]);

  const filteredOrders = useMemo(() => {
    const term = search.trim().toLowerCase();
    const filtered = orders.filter((o) => {
      if (typeFilter !== "all" && o.type !== typeFilter) return false;
      if (statusFilter !== "all" && o.status !== statusFilter) return false;
      if (dateWindow.from || dateWindow.to) {
        const createdIso = (o.created_at || "").slice(0, 10);
        if (dateWindow.from && createdIso < dateWindow.from) return false;
        if (dateWindow.to && createdIso > dateWindow.to) return false;
      }
      if (!term) return true;
      return [o.customer_name, o.customer_email, o.po_number, o.id]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(term));
    });
    // Sort — stable, non-destructive copy.
    const arr = [...filtered];
    const cmpStr = (a, b) => (a || "").toString().localeCompare((b || "").toString(), undefined, { sensitivity: "base" });
    if (sortBy === "oldest") {
      arr.sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
    } else if (sortBy === "total_desc") {
      arr.sort((a, b) => (b.total || 0) - (a.total || 0));
    } else if (sortBy === "total_asc") {
      arr.sort((a, b) => (a.total || 0) - (b.total || 0));
    } else if (sortBy === "name") {
      arr.sort((a, b) => cmpStr(a.customer_name, b.customer_name));
    } else if (sortBy === "event_date") {
      arr.sort((a, b) => (a.event_date || "9999").localeCompare(b.event_date || "9999"));
    } else {
      // "newest" default
      arr.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    }
    return arr;
  }, [orders, search, statusFilter, typeFilter, dateWindow, sortBy]);

  // Group filteredOrders by agency for the "By agency" view mode. Groups
  // are keyed by `user_id` (falling back to `user_email` when the order
  // was created before agency-linking existed); orders without either fall
  // into an "Unassigned" bucket at the bottom. Each group carries a total
  // sum + count so the operator can see LTV per agency at a glance.
  const agencyGroups = useMemo(() => {
    if (viewMode !== "by_agency") return [];
    const byKey = new Map();
    for (const o of filteredOrders) {
      const key = o.user_id || (o.user_email || "").toLowerCase() || "__unassigned__";
      if (!byKey.has(key)) {
        byKey.set(key, {
          key,
          label:
            o.organization
            || o.user_email
            || o.customer_email
            || "Unassigned / walk-in",
          user_id: o.user_id || null,
          email: o.user_email || o.customer_email || null,
          orders: [],
          total: 0,
        });
      }
      const g = byKey.get(key);
      g.orders.push(o);
      g.total += Number(o.total) || 0;
      // Prefer an organization label if any order in the group carries one.
      if (o.organization && !g.orders[0].organization) g.label = o.organization;
    }
    // Sort groups: unassigned goes last, everyone else alphabetical.
    const arr = Array.from(byKey.values());
    arr.sort((a, b) => {
      if (a.key === "__unassigned__") return 1;
      if (b.key === "__unassigned__") return -1;
      return (a.label || "").localeCompare(b.label || "", undefined, { sensitivity: "base" });
    });
    return arr;
  }, [filteredOrders, viewMode]);

  const toggleAgencyCollapse = (key) => {
    setCollapsedAgencies((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const filteredPartnerships = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return partnerships;
    return partnerships.filter((p) =>
      [p.name, p.email, p.organization, p.partnership_type, p.reference_id]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(term))
    );
  }, [partnerships, search]);

  const filteredQuotes = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return quotes;
    return quotes.filter((q) =>
      [q.name, q.email, q.organization, q.package_interest, q.event_type]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(term))
    );
  }, [quotes, search]);

  const filteredExperiences = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return experiences;
    return experiences.filter((e) =>
      [e.title, e.description, e.category, ...(e.tags || [])]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(term))
    );
  }, [experiences, search]);

  const filteredPackages = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return packages;
    return packages.filter((p) =>
      [p.name, p.description, p.best_for, p.id]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(term))
    );
  }, [packages, search]);

  const filteredMenuItems = useMemo(() => {
    const term = search.trim().toLowerCase();
    return menuItems.filter((it) => {
      if (menuCategoryFilter !== "all" && it.category !== menuCategoryFilter)
        return false;
      if (!term) return true;
      return [it.name, it.description, it.id, it.category]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(term));
    });
  }, [menuItems, search, menuCategoryFilter]);

  const filteredActivity = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return activity;
    return activity.filter((a) =>
      [a.kind, a.summary, a.actor]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(term))
    );
  }, [activity, search]);

  const filteredAgencies = useMemo(() => {
    const term = search.trim().toLowerCase();
    return agencies.filter((a) => {
      if (agencyStatusFilter !== "all" && (a.status || "approved") !== agencyStatusFilter)
        return false;
      if (!term) return true;
      return [a.name, a.email, a.organization]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(term));
    });
  }, [agencies, search, agencyStatusFilter]);

  const reloadAgencies = async () => {
    try {
      const r = await api.get("/admin/agencies?status=all&limit=300");
      setAgencies(r.data.items || []);
    } catch {
      /* swallow */
    }
  };
  const handleApproveAgency = async (a) => {
    if (agencyActionBusy) return;
    setAgencyActionBusy(a.id);
    try {
      await api.post(`/admin/agencies/${a.id}/approve`);
      setToast(`Approved · ${a.email}`);
      await reloadAgencies();
    } catch (e) {
      const msg = formatApiErrorDetail(e?.response?.data?.detail) || e?.message || "Failed";
      setToast(`Approve failed: ${msg}`);
    } finally {
      setAgencyActionBusy("");
      setTimeout(() => setToast(""), 3500);
    }
  };
  const handleRejectAgency = async (a) => {
    if (agencyActionBusy) return;
    const reason = window.prompt(
      `Reject ${a.email}? Optional note for the applicant (visible in their email):`,
      "",
    );
    if (reason === null) return; // user cancelled
    setAgencyActionBusy(a.id);
    try {
      await api.post(`/admin/agencies/${a.id}/reject`, { reason: reason || null });
      setToast(`Rejected · ${a.email}`);
      await reloadAgencies();
    } catch (e) {
      const msg = formatApiErrorDetail(e?.response?.data?.detail) || e?.message || "Failed";
      setToast(`Reject failed: ${msg}`);
    } finally {
      setAgencyActionBusy("");
      setTimeout(() => setToast(""), 3500);
    }
  };

  const handleSendPasswordReset = async (a) => {
    if (agencyActionBusy) return;
    const ok = window.confirm(
      `Email a password-reset link to ${a.email}?\n\nThe link expires in 60 minutes and can only be used once.`,
    );
    if (!ok) return;
    setAgencyActionBusy(a.id);
    try {
      await api.post(`/admin/users/${a.id}/send-password-reset`);
      setToast(`Reset link sent · ${a.email}`);
    } catch (e) {
      const msg = formatApiErrorDetail(e?.response?.data?.detail) || e?.message || "Failed";
      setToast(`Send failed: ${msg}`);
    } finally {
      setAgencyActionBusy("");
      setTimeout(() => setToast(""), 3500);
    }
  };

  const reloadExperiences = async () => {
    try {
      const r = await api.get("/admin/experiences");
      setExperiences(r.data.items || []);
    } catch {
      /* swallow */
    }
  };
  const handleNewExperience = () => {
    setEditingExp(null);
    setExpEditorOpen(true);
  };
  const handleEditExperience = (e) => {
    setEditingExp(e);
    setExpEditorOpen(true);
  };
  const handleExperienceSaved = async () => {
    await reloadExperiences();
    setExpEditorOpen(false);
    setEditingExp(null);
    setToast("Saved");
    setTimeout(() => setToast(""), 2500);
  };
  const handleExperienceDeleted = async () => {
    await reloadExperiences();
    setExpEditorOpen(false);
    setEditingExp(null);
    setToast("Deleted");
    setTimeout(() => setToast(""), 2500);
  };

  const reloadPackages = async () => {
    try {
      const r = await api.get("/admin/packages");
      setPackages(r.data.items || []);
    } catch {
      /* swallow */
    }
  };
  const handleEditPackage = (p) => {
    setEditingPkg(p);
    setPkgEditorOpen(true);
  };
  const handlePackageSaved = async () => {
    await reloadPackages();
    setPkgEditorOpen(false);
    setEditingPkg(null);
    setToast("Package saved — live on homepage");
    setTimeout(() => setToast(""), 3500);
  };
  const handleMovePackage = async (pkgId, dir) => {
    // Build the new id order from the current packages list, then submit it.
    const ids = packages.map((p) => p.id);
    const idx = ids.indexOf(pkgId);
    const swap = idx + dir;
    if (idx < 0 || swap < 0 || swap >= ids.length) return;
    [ids[idx], ids[swap]] = [ids[swap], ids[idx]];
    // Optimistic local reorder while we wait for the server.
    setPackages((curr) => {
      const next = [...curr];
      [next[idx], next[swap]] = [next[swap], next[idx]];
      return next;
    });
    try {
      const r = await api.post("/admin/packages/reorder", { order: ids });
      setPackages(r.data.items || []);
      setToast("Order saved — live on homepage");
      setTimeout(() => setToast(""), 2500);
    } catch (e) {
      // Roll back on failure
      await reloadPackages();
      const msg =
        formatApiErrorDetail(e?.response?.data?.detail) ||
        e?.message ||
        "Reorder failed";
      setToast(`Reorder failed: ${msg}`);
      setTimeout(() => setToast(""), 4500);
    }
  };

  const reloadMenuItems = async () => {
    try {
      const r = await api.get("/admin/menu");
      setMenuItems(r.data.items || []);
      setMenuCategories(r.data.categories_order || []);
      setMenuMarkup(r.data.agency_markup || 1.2);
      setMenuMarkupByCat(r.data.agency_markup_by_category || {});
    } catch {
      /* swallow */
    }
  };
  const saveMenuOrder = async (cats, items) => {
    setMenuCategories(cats);
    setMenuItems(items);
    const byCat = {};
    items.forEach((i) => { (byCat[i.category] = byCat[i.category] || []).push(i.id); });
    try {
      await api.put("/admin/menu/order", { categories: cats, items: byCat });
      setToast("Menu order saved — live on /menu");
      setTimeout(() => setToast(""), 2500);
    } catch {
      setToast("Couldn't save the new order — please try again");
      reloadMenuItems();
    }
  };
  const handleReorderCategories = (cats) => {
    const rank = Object.fromEntries(cats.map((c, i) => [c, i]));
    saveMenuOrder(cats, [...menuItems].sort((a, b) => (rank[a.category] ?? 999) - (rank[b.category] ?? 999)));
  };
  const handleReorderItems = (ids) => {
    const byId = Object.fromEntries(menuItems.map((i) => [i.id, i]));
    const others = menuItems.filter((i) => i.category !== menuCategoryFilter);
    saveMenuOrder(menuCategories, [...others, ...ids.map((id) => byId[id])]);
  };
  const handleEditMenuItem = (it) => {
    setEditingMenuItem(it);
    setMenuEditorOpen(true);
  };
  const handleMenuItemSaved = async () => {
    await reloadMenuItems();
    setMenuEditorOpen(false);
    setEditingMenuItem(null);
    setToast("Menu item saved — live on /menu");
    setTimeout(() => setToast(""), 3500);
  };
  const handleDeleteMenuItem = async (it) => {
    // Called from the inline trash icon on each MenuItemRow. The row itself
    // owns the two-click confirmation UX, so by the time we're here the
    // admin has already confirmed. Fire the DELETE, refresh the list, toast.
    try {
      await api.delete(`/admin/menu/${it.id}`);
      await reloadMenuItems();
      setToast(`Deleted — ${it.name}`);
      setTimeout(() => setToast(""), 3500);
    } catch (e) {
      const msg =
        formatApiErrorDetail(e?.response?.data?.detail) ||
        e?.message ||
        "Could not delete";
      setToast(`Delete failed: ${msg}`);
      setTimeout(() => setToast(""), 5000);
      throw e;
    }
  };

  // Action handlers — refresh after mutation so the row reflects new state
  const reloadOrders = async () => {
    try {
      const o = await api.get("/admin/orders?limit=200");
      setOrders(o.data.items || []);
      const s = await api.get("/admin/stats");
      setStats(s.data);
    } catch {
      /* swallow */
    }
  };
  const handleEdit = (o) => {
    setEditingOrder(o);
    setEditorPrefill(null);
    setEditorOpen(true);
  };
  // Convert a customer's small-order quote into a prefilled draft invoice
  // in the OrderEditorModal. Admin can then adjust delivery fee, choose the
  // payment method (Stripe link / PO / Cash), and save — one-click send follows.
  const handleConvertQuoteToInvoice = (quote) => {
    const isItemQuote = quote.kind === "item_quote";
    const items = (quote.items || []).map((it) => ({
      package_id: it.package_id,
      quantity: it.quantity || 1,
      selected_options: it.selected_options || undefined,
      unit_price_override: isItemQuote && it.unit_price ? it.unit_price : undefined,
    }));
    setEditingOrder(null);
    setEditorPrefill({
      customer_name: quote.name || "",
      customer_email: quote.email || "",
      customer_phone: quote.phone || "",
      address: quote.address || "",
      address_line2: quote.address_line2 || "",
      city: quote.city || "",
      state: quote.state || "",
      zip_code: quote.zip_code || "",
      event_date: quote.event_date || "",
      event_time: quote.event_time || "",
      on_site_contact_name: quote.on_site_contact_name || "",
      on_site_contact_phone: quote.on_site_contact_phone || "",
      delivery_instructions: quote.delivery_instructions || "",
      has_receiving_personnel:
        quote.has_receiving_personnel === true || quote.has_receiving_personnel === false
          ? quote.has_receiving_personnel
          : null,
      has_delivery_cart:
        quote.has_delivery_cart === true || quote.has_delivery_cart === false
          ? quote.has_delivery_cart
          : null,
      notes: quote.message || "",
      fulfillment: quote.fulfillment === "pickup" ? "pickup" : "delivery",
      items,
    });
    setEditorOpen(true);
    setToast("Quote loaded — set the delivery fee and payment method, then Save.");
    setTimeout(() => setToast(""), 4500);
  };
  const handleSendInvoice = async (o) => {
    // Open the SendInvoiceModal — it collects an optional custom note +
    // CC list, then posts to /admin/orders/{id}/send-invoice. Kept the
    // async signature so `OrderRow`'s local `sending` spinner still works.
    setSendInvoiceOrder(o);
  };
  const handleSendInvoiceSent = async ({ note_included, extra_cc }) => {
    const target = sendInvoiceOrder;
    setSendInvoiceOrder(null);
    const extras =
      Array.isArray(extra_cc) && extra_cc.length > 0
        ? ` (cc ${extra_cc.length})`
        : "";
    const noteSuffix = note_included ? " · note included" : "";
    setToast(
      `Invoice emailed to ${target?.customer_email || "customer"}${extras}${noteSuffix}`
    );
    setTimeout(() => setToast(""), 3500);
    await reloadOrders();
  };
  const handleMarkCashPaid = async (o) => {
    // Opens the MarkAsPaidModal — the modal handles method choice, reference,
    // customer notify preference and the API call. Kept the name for backwards
    // compatibility with the OrderRow onMarkPaid callback prop.
    setMarkPaidOrder(o);
  };
  const handleMarkPaidSaved = async ({ payment_method, notify }) => {
    const label = { cash: "cash", check: "check", po: "PO" }[payment_method] || payment_method;
    setMarkPaidOrder(null);
    setToast(
      notify
        ? `Marked ${label} paid — receipt sent to customer`
        : `Marked ${label} paid`,
    );
    setTimeout(() => setToast(""), 3500);
    await reloadOrders();
  };
  const handleEditorSaved = async () => {
    await reloadOrders();
    setEditorOpen(false);
    setEditingOrder(null);
    setToast("Saved");
    setTimeout(() => setToast(""), 2500);
  };
  const handleDeleteOrder = async (o) => {
    const label = o.customer_name || o.customer_email || o.id;
    const total = Number(o.total || 0).toFixed(2);
    if (!window.confirm(`Delete this order?\n\n${label}\nTotal: $${total}\n\nThis cannot be undone.`)) {
      return;
    }
    try {
      await api.delete(`/admin/orders/${o.id}`);
      setToast(`Deleted — ${label}`);
      setTimeout(() => setToast(""), 3500);
      await reloadOrders();
    } catch (e) {
      const msg =
        formatApiErrorDetail(e?.response?.data?.detail) || e?.message || "Delete failed";
      setToast(`Failed: ${msg}`);
      setTimeout(() => setToast(""), 4500);
    }
  };

  // -------- Bulk selection helpers --------
  const toggleSelectOrder = (orderId) => {
    setSelectedOrderIds((prev) => {
      const next = new Set(prev);
      if (next.has(orderId)) next.delete(orderId);
      else next.add(orderId);
      return next;
    });
  };
  const clearSelection = () => setSelectedOrderIds(new Set());
  const selectAllVisible = (visibleOrders) => {
    setSelectedOrderIds(new Set(visibleOrders.map((o) => o.id)));
  };
  const selectedOrders = orders.filter((o) => selectedOrderIds.has(o.id));
  // Bulk operations require a homogeneous payment type so the email template
  // stays consistent. Surface this in the toolbar so the admin can fix it.
  const selectedTypes = Array.from(new Set(selectedOrders.map((o) => o.type || "stripe")));
  const selectionMixed = selectedTypes.length > 1;
  // Only agency PO orders are eligible for batching — Stripe / private
  // orders don't fit the consolidated agency-invoice model.
  const batchEligible =
    selectedOrders.length > 0 &&
    selectedOrders.every((o) => o.type === "agency_po");

  const handleBulkEditSubmit = async (patch) => {
    const ids = Array.from(selectedOrderIds);
    if (!ids.length) return;
    try {
      const { data } = await api.post("/admin/orders/bulk-update", {
        order_ids: ids,
        ...patch,
      });
      setToast(`Bulk update applied to ${data.modified_count} order${data.modified_count === 1 ? "" : "s"}`);
      setTimeout(() => setToast(""), 3500);
      await reloadOrders();
      setBulkMode(null);
    } catch (e) {
      const msg = formatApiErrorDetail(e?.response?.data?.detail) || e?.message || "Update failed";
      setToast(`Failed: ${msg}`);
      setTimeout(() => setToast(""), 5000);
      throw e;
    }
  };
  const handleBulkSendSubmit = async (payload) => {
    const ids = Array.from(selectedOrderIds);
    if (!ids.length) return;
    try {
      const { data } = await api.post("/admin/orders/bulk-send-invoice", {
        order_ids: ids,
        ...payload,
      });
      const failedNote = data.failed_count
        ? ` (${data.failed_count} failed)`
        : "";
      setToast(`Sent ${data.sent_count} invoice${data.sent_count === 1 ? "" : "s"}${failedNote}`);
      setTimeout(() => setToast(""), 4000);
      await reloadOrders();
      setBulkMode(null);
      clearSelection();
    } catch (e) {
      const msg = formatApiErrorDetail(e?.response?.data?.detail) || e?.message || "Send failed";
      setToast(`Failed: ${msg}`);
      setTimeout(() => setToast(""), 5000);
      throw e;
    }
  };

  const handleBulkAddToBatchSubmit = async (payload) => {
    // payload shape (exactly one of): { batch_id } | { new_batch_name }
    const ids = Array.from(selectedOrderIds);
    if (!ids.length) return;
    try {
      const { data } = await api.post("/admin/orders/bulk-add-to-batch", {
        order_ids: ids,
        ...payload,
      });
      const label = data.ref || data.batch_id?.slice(0, 8) || "batch";
      const verb = data.is_new_batch ? "Created" : "Added to";
      setToast(
        `${verb} ${label} — ${data.attached_count} order${data.attached_count === 1 ? "" : "s"} attached (${data.total_in_batch} total in batch)`
      );
      setTimeout(() => setToast(""), 4000);
      await reloadOrders();
      setBulkMode(null);
      clearSelection();
    } catch (e) {
      const msg = formatApiErrorDetail(e?.response?.data?.detail) || e?.message || "Batch grouping failed";
      setToast(`Failed: ${msg}`);
      setTimeout(() => setToast(""), 5000);
      throw e;
    }
  };

  const handleBulkDownloadPdfs = async () => {
    const ids = Array.from(selectedOrderIds);
    if (!ids.length) return;
    try {
      await downloadBulkInvoiceZip(ids);
      setToast(`Downloaded ${ids.length} invoice${ids.length === 1 ? "" : "s"} as ZIP`);
      setTimeout(() => setToast(""), 4000);
    } catch (e) {
      const msg = formatApiErrorDetail(e?.response?.data?.detail) || e?.message || "Download failed";
      setToast(`Failed: ${msg}`);
      setTimeout(() => setToast(""), 5000);
    }
  };

  const handleExportCSV = async () => {
    // Reuse the user's currently-active filters so the export matches what
    // they see on screen. Uses axios so auth cookies are sent automatically.
    const params = new URLSearchParams();
    if (typeFilter !== "all") params.set("type_filter", typeFilter);
    if (statusFilter !== "all") params.set("status_filter", statusFilter);
    if (search.trim()) params.set("search", search.trim());
    if (dateWindow.from) params.set("date_from", dateWindow.from);
    if (dateWindow.to) params.set("date_to", dateWindow.to);
    try {
      const res = await api.get(`/admin/orders/export?${params.toString()}`, {
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const cd = res.headers?.["content-disposition"] || "";
      const m = cd.match(/filename="?([^";]+)"?/);
      const filename = m ? m[1] : `fff-orders-${new Date().toISOString().slice(0, 10)}.csv`;
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      const total = (filteredOrders?.length ?? 0);
      setToast(`Exported ${total} order${total === 1 ? "" : "s"} · ${filename}`);
      setTimeout(() => setToast(""), 3500);
    } catch (e) {
      const msg =
        formatApiErrorDetail(e?.response?.data?.detail) || e?.message || "Export failed";
      setToast(`Export failed: ${msg}`);
      setTimeout(() => setToast(""), 4500);
    }
  };

  // Show loading while auth check is in flight; redirect non-admins once known
  if (authChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-cream">
        <Loader2 className="h-6 w-6 animate-spin text-brand-teal" />
      </div>
    );
  }
  if (!isAdmin) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div data-testid="admin-orders-page" className="bg-brand-cream min-h-screen flex flex-col">
      <Header />

      <main className="flex-1 pt-[88px] sm:pt-[100px] pb-20">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-10">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
            <div>
              <p className="text-[12px] font-bold uppercase tracking-[0.22em] text-brand-teal">
                Admin Dashboard
              </p>
              <h1 className="mt-2 font-display font-black text-2xl sm:text-4xl tracking-tight text-brand-charcoal leading-tight">
                Orders, Partnerships, Quotes
              </h1>
            </div>
            <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
              <Link
                to="/admin/reports/sales"
                data-testid="admin-sales-report-link"
                aria-label="Open Sales Report"
                className="inline-flex items-center gap-2 rounded-full bg-white border border-brand-charcoal/15 text-brand-charcoal px-3 sm:px-5 py-2.5 text-[11.5px] font-bold uppercase tracking-[0.12em] hover:border-brand-teal hover:text-brand-teal transition-colors"
              >
                <DollarSign className="h-4 w-4" />
                <span className="hidden sm:inline">Sales Report</span>
                <span className="sm:hidden">Sales</span>
              </Link>
              <Link
                to="/admin/events"
                data-testid="admin-events-link"
                aria-label="Open Event Capture"
                className="inline-flex items-center gap-2 rounded-full bg-white border border-brand-charcoal/15 text-brand-charcoal px-3 sm:px-5 py-2.5 text-[11.5px] font-bold uppercase tracking-[0.12em] hover:border-brand-teal hover:text-brand-teal transition-colors"
              >
                <Camera className="h-4 w-4" />
                <span>Events</span>
              </Link>
              <Link
                to="/admin/kitchen"
                data-testid="admin-kitchen-link"
                aria-label="Open Kitchen"
                className="inline-flex items-center gap-2 rounded-full bg-white border border-brand-charcoal/15 text-brand-charcoal px-3 sm:px-5 py-2.5 text-[11.5px] font-bold uppercase tracking-[0.12em] hover:border-brand-teal hover:text-brand-teal transition-colors"
              >
                <ChefHat className="h-4 w-4" />
                <span>Kitchen</span>
              </Link>
              <Link
                to="/admin/calendars"
                data-testid="admin-calendars-link"
                aria-label="Open Calendars"
                className="inline-flex items-center gap-2 rounded-full bg-white border border-brand-charcoal/15 text-brand-charcoal px-3 sm:px-5 py-2.5 text-[11.5px] font-bold uppercase tracking-[0.12em] hover:border-brand-teal hover:text-brand-teal transition-colors"
              >
                <CalendarDays className="h-4 w-4" />
                <span>Calendars</span>
              </Link>

              <button
                type="button"
                data-testid="admin-export-csv-btn"
                onClick={handleExportCSV}
                aria-label="Export CSV"
                className="inline-flex items-center gap-2 rounded-full bg-white border border-brand-charcoal/15 text-brand-charcoal px-3 sm:px-5 py-2.5 text-[11.5px] font-bold uppercase tracking-[0.12em] hover:border-brand-teal hover:text-brand-teal transition-colors"
              >
                <Download className="h-4 w-4" />
                <span className="hidden sm:inline">Export CSV</span>
                <span className="sm:hidden">CSV</span>
              </button>
              <button
                type="button"
                data-testid="admin-create-invoice-btn"
                onClick={() => setManualInvoiceOpen(true)}
                aria-label="New Pay-Link Invoice"
                className="inline-flex items-center gap-2 rounded-full bg-brand-charcoal text-white px-3 sm:px-5 py-2.5 text-[11.5px] font-bold uppercase tracking-[0.12em] hover:bg-brand-teal transition-colors"
              >
                <Plus className="h-4 w-4" />
                <span className="hidden sm:inline">Pay-Link Invoice</span>
                <span className="sm:hidden">Invoice</span>
              </button>
              <Link
                to="/"
                data-testid="admin-back-link"
                className="hidden sm:inline text-[11.5px] font-bold uppercase tracking-[0.14em] text-brand-charcoal/55 hover:text-brand-charcoal"
              >
                ← Back to site
              </Link>
            </div>
          </div>

          {/* Stats */}
          {stats && (
            <div className="mt-5 sm:mt-8 grid grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-3">
              <StatCard
                label="Total Orders"
                value={stats.orders.total}
                icon={ShoppingBag}
                accent="teal"
                onClick={() => setTab("orders")}
              />
              <StatCard
                label="Revenue"
                value={`$${(stats.revenue_total || 0).toLocaleString("en-US", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}`}
                icon={DollarSign}
                accent="green"
                onClick={() => navigate("/admin/reports/sales")}
              />
              <StatCard
                label="Partnership Leads"
                value={stats.partnerships_total}
                icon={Building2}
                accent="gold"
                onClick={() => setTab("partnerships")}
              />
              <StatCard
                label="Quote Requests"
                value={stats.quotes_total}
                icon={FileText}
                accent="charcoal"
                onClick={() => setTab("quotes")}
              />
            </div>
          )}

          {/* Search + filters */}
          <div className="mt-5 sm:mt-8 flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-charcoal/40" />
              <Input
                data-testid="admin-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name, email, PO, ref…"
                className="h-12 pl-10 rounded-xl bg-white border-black/10"
              />
            </div>
            {tab === "orders" && (
              <div className="flex gap-2 flex-wrap">
                <select
                  data-testid="admin-type-filter"
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  className="h-12 rounded-xl bg-white border border-black/10 px-3 text-[13px] font-medium text-brand-charcoal"
                >
                  <option value="all">All types</option>
                  <option value="stripe">Stripe</option>
                  <option value="agency_po">Agency PO</option>
                  <option value="agency_cash">Agency Cash</option>
                  <option value="cash">Cash</option>
                  <option value="self_pay_invoice">Pay-Link</option>
                </select>
                <select
                  data-testid="admin-status-filter"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="h-12 rounded-xl bg-white border border-black/10 px-3 text-[13px] font-medium text-brand-charcoal"
                >
                  <option value="all">All statuses</option>
                  <option value="paid">Paid</option>
                  <option value="submitted">Submitted</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="awaiting_payment">Awaiting Payment</option>
                  <option value="pending_approval">Pending Approval</option>
                  <option value="pending">Pending</option>
                  <option value="draft">Draft</option>
                  <option value="rejected">Rejected</option>
                  <option value="cancelled">Cancelled</option>
                </select>
                <select
                  data-testid="admin-sort-by"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="h-12 rounded-xl bg-white border border-black/10 px-3 text-[13px] font-medium text-brand-charcoal"
                  title="Sort orders"
                >
                  <option value="newest">Newest first</option>
                  <option value="oldest">Oldest first</option>
                  <option value="total_desc">Highest total</option>
                  <option value="total_asc">Lowest total</option>
                  <option value="name">Customer name (A→Z)</option>
                  <option value="event_date">Event date</option>
                </select>
              </div>
            )}
          </div>

          {/* Date range chips + custom range for the Orders tab. */}
          {tab === "orders" && (
            <div className="mt-3 flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="-mx-1 overflow-x-auto sm:overflow-visible sm:mx-0">
                <div
                  role="tablist"
                  data-testid="admin-date-preset"
                  className="inline-flex items-center gap-1 rounded-xl bg-white border border-black/10 p-1 min-w-max"
                >
                  {[
                    { id: "all", label: "All time" },
                    { id: "today", label: "Today" },
                    { id: "week", label: "This week" },
                    { id: "month", label: "This month" },
                    { id: "last30", label: "Last 30 days" },
                    { id: "custom", label: "Custom" },
                  ].map((p) => {
                    const active = datePreset === p.id;
                    return (
                      <button
                        key={p.id}
                        type="button"
                        role="tab"
                        aria-selected={active}
                        data-testid={`admin-date-preset-${p.id}`}
                        onClick={() => setDatePreset(p.id)}
                        className={`shrink-0 rounded-lg px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.08em] transition-colors ${
                          active
                            ? "bg-brand-charcoal text-white"
                            : "text-brand-charcoal/60 hover:text-brand-charcoal"
                        }`}
                      >
                        {p.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              {datePreset === "custom" && (
                <div
                  data-testid="admin-date-custom-range"
                  className="flex flex-wrap items-center gap-2 text-[12px] text-brand-charcoal/70"
                >
                  <label className="flex items-center gap-1.5">
                    From
                    <input
                      type="date"
                      data-testid="admin-date-from"
                      value={dateFrom}
                      onChange={(e) => setDateFrom(e.target.value)}
                      className="h-10 rounded-lg bg-white border border-black/10 px-2.5 text-[12.5px]"
                    />
                  </label>
                  <label className="flex items-center gap-1.5">
                    To
                    <input
                      type="date"
                      data-testid="admin-date-to"
                      value={dateTo}
                      onChange={(e) => setDateTo(e.target.value)}
                      className="h-10 rounded-lg bg-white border border-black/10 px-2.5 text-[12.5px]"
                    />
                  </label>
                </div>
              )}
              {(datePreset !== "all" && datePreset !== "custom") && (
                <p className="text-[11.5px] text-brand-charcoal/55 tabular-nums">
                  {dateWindow.from} → {dateWindow.to}
                </p>
              )}
            </div>
          )}

          {/* View mode toggle — flat chronological list vs grouped by agency. */}
          {tab === "orders" && (
            <div className="mt-3">
              <div
                role="tablist"
                data-testid="admin-view-mode"
                className="inline-flex items-center gap-1 rounded-xl bg-white border border-black/10 p-1"
              >
                {[
                  { id: "chronological", label: "Chronological" },
                  { id: "by_agency", label: "By agency" },
                ].map((m) => {
                  const active = viewMode === m.id;
                  return (
                    <button
                      key={m.id}
                      type="button"
                      role="tab"
                      aria-selected={active}
                      data-testid={`admin-view-mode-${m.id}`}
                      onClick={() => setViewMode(m.id)}
                      className={`shrink-0 rounded-lg px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.08em] transition-colors ${
                        active
                          ? "bg-brand-charcoal text-white"
                          : "text-brand-charcoal/60 hover:text-brand-charcoal"
                      }`}
                    >
                      {m.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Tabs — mobile uses a branded dropdown, sm+ uses the inline tab row. */}
          <div data-testid="admin-tabs-row" className="mt-6">
            {/* MOBILE: dropdown select */}
            <div className="sm:hidden">
              {(() => {
                const active = TABS.find((t) => t.id === tab) || TABS[0];
                const ActiveIcon = active.icon;
                return (
                  <div className="relative rounded-2xl bg-brand-charcoal text-white px-4 py-3 shadow-[0_8px_20px_-10px_rgba(0,0,0,0.4)]">
                    <div className="flex items-center gap-3">
                      <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-brand-teal text-white shrink-0">
                        <ActiveIcon className="h-4 w-4" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="text-[9.5px] font-bold uppercase tracking-[0.18em] text-brand-gold">
                          Dashboard Section
                        </p>
                        <p className="font-display font-black text-[16px] text-white tracking-tight leading-none mt-1 truncate">
                          {active.label}
                        </p>
                      </div>
                      <ChevronDown className="h-4 w-4 text-white/70 shrink-0" />
                    </div>
                    <select
                      data-testid="admin-tabs-mobile-select"
                      aria-label="Choose dashboard section"
                      value={tab}
                      onChange={(e) => setTab(e.target.value)}
                      className="absolute inset-0 h-full w-full opacity-0 cursor-pointer"
                    >
                      {TAB_GROUPS.map((g) => (
                        <optgroup key={g.id} label={g.label}>
                          {g.tabs.map((id) => (
                            <option key={id} value={id}>
                              {TABS.find((t) => t.id === id)?.label}
                            </option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                  </div>
                );
              })()}
            </div>

            {/* TABLET/DESKTOP: grouped dropdowns */}
            <AdminTabGroups
              tabs={TABS}
              tab={tab}
              setTab={setTab}
              getCount={(id) =>
                    id === "orders"
                      ? filteredOrders.length
                      : id === "partnerships"
                      ? filteredPartnerships.length
                      : id === "agencies"
                      ? filteredAgencies.length
                      : id === "gallery"
                      ? filteredExperiences.length
                      : id === "packages"
                      ? filteredPackages.length
                      : id === "menu"
                      ? filteredMenuItems.length
                      : id === "activity"
                      ? filteredActivity.length
                      : id === "quotes"
                      ? filteredQuotes.length
                      : null
              }
            />
          </div>

          {/* Body */}
          <div className="mt-5">
            {error && (
              <div
                data-testid="admin-error"
                role="alert"
                className="inline-flex items-start gap-3 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-[13px] text-red-700"
              >
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2
                  data-testid="admin-loading"
                  className="h-6 w-6 animate-spin text-brand-teal"
                />
              </div>
            ) : tab === "orders" ? (
              <>
                {selectedOrderIds.size > 0 && (
                  <BulkActionToolbar
                    selectedCount={selectedOrderIds.size}
                    selectionMixed={selectionMixed}
                    selectedTypes={selectedTypes}
                    onEdit={() => setBulkMode("edit")}
                    onEmail={() => setBulkMode("email")}
                    onDownload={handleBulkDownloadPdfs}
                    onAddToBatch={() => setBulkMode("batch")}
                    batchEligible={batchEligible}
                    onSelectAll={() => selectAllVisible(filteredOrders)}
                    onClear={clearSelection}
                    totalVisible={filteredOrders.length}
                  />
                )}
                {filteredOrders.length === 0 ? (
                  <EmptyState label="No orders match your filters." icon={ShoppingBag} />
                ) : viewMode === "by_agency" ? (
                  <ul className="space-y-3" data-testid="admin-orders-by-agency-list">
                    {agencyGroups.map((g) => {
                      const collapsed = collapsedAgencies.has(g.key);
                      const isUnassigned = g.key === "__unassigned__";
                      return (
                        <li
                          key={g.key}
                          data-testid={`admin-agency-group-${g.key}`}
                          className={`rounded-2xl border overflow-hidden ${
                            isUnassigned
                              ? "border-black/10 bg-brand-cream/60"
                              : "border-brand-teal/25 bg-brand-teal/5"
                          }`}
                        >
                          <button
                            type="button"
                            onClick={() => toggleAgencyCollapse(g.key)}
                            data-testid={`admin-agency-group-toggle-${g.key}`}
                            className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-black/5 transition-colors"
                          >
                            <div className="min-w-0 flex-1">
                              <p className="font-display font-black text-[14px] tracking-tight text-brand-charcoal truncate">
                                {g.label}
                              </p>
                              <p className="mt-0.5 text-[11.5px] text-brand-charcoal/60 truncate">
                                {g.email || "no contact email"}
                                {" · "}
                                {g.orders.length} order{g.orders.length === 1 ? "" : "s"}
                                {" · "}
                                LTV {fmtMoney(g.total)}
                              </p>
                            </div>
                            <span
                              aria-hidden
                              className={`shrink-0 inline-flex h-6 w-6 items-center justify-center rounded-full bg-white border border-black/10 text-brand-charcoal/60 transition-transform ${
                                collapsed ? "" : "rotate-180"
                              }`}
                            >
                              <ChevronDown className="h-3.5 w-3.5" />
                            </span>
                          </button>
                          {!collapsed && (
                            <ul className="p-2 pt-0 space-y-2">
                              {g.orders.map((o) => (
                                <OrderRow
                                  key={o.id}
                                  order={o}
                                  onEdit={handleEdit}
                                  onSendInvoice={handleSendInvoice}
                                  onMarkPaid={handleMarkCashPaid}
                                  onDelete={handleDeleteOrder}
                                  onAssignAgency={(order) => setAssignAgencyOrder(order)}
                                  agencies={agencies}
                                  onOrdersChanged={reloadOrders}
                                  selected={selectedOrderIds.has(o.id)}
                                  onToggleSelect={toggleSelectOrder}
                                />
                              ))}
                            </ul>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <ul className="space-y-2" data-testid="admin-orders-list">
                    {filteredOrders.map((o) => (
                      <OrderRow
                        key={o.id}
                        order={o}
                        onEdit={handleEdit}
                        onSendInvoice={handleSendInvoice}
                        onMarkPaid={handleMarkCashPaid}
                        onDelete={handleDeleteOrder}
                        onAssignAgency={(order) => setAssignAgencyOrder(order)}
                        agencies={agencies}
                        onOrdersChanged={reloadOrders}
                        selected={selectedOrderIds.has(o.id)}
                        onToggleSelect={toggleSelectOrder}
                      />
                    ))}
                  </ul>
                )}
              </>
            ) : tab === "batches" ? (
              <BatchesTab />
            ) : tab === "partnerships" ? (
              filteredPartnerships.length === 0 ? (
                <EmptyState label="No partnership inquiries yet." icon={Building2} />
              ) : (
                <ul className="space-y-2" data-testid="admin-partnerships-list">
                  {filteredPartnerships.map((p) => (
                    <PartnershipRow key={p.id} inquiry={p} />
                  ))}
                </ul>
              )
            ) : tab === "agencies" ? (
              <>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
                  <p className="text-[12px] text-brand-charcoal/55">
                    Agency signups require admin approval before they can sign in.
                    Approving sends a welcome email; rejecting sends an update with your optional note.
                  </p>
                  {/* Horizontal-scroll on mobile so tabs never overflow the viewport. */}
                  <div className="-mx-1 overflow-x-auto sm:overflow-visible sm:mx-0">
                    <div className="inline-flex items-center gap-1.5 rounded-xl bg-white border border-black/10 p-1 min-w-max">
                      {["pending", "approved", "rejected", "all"].map((s) => {
                        const active = agencyStatusFilter === s;
                        const n = s === "all"
                          ? agencies.length
                          : agencies.filter((a) => (a.status || "approved") === s).length;
                        return (
                          <button
                            key={s}
                            type="button"
                            data-testid={`admin-agency-filter-${s}`}
                            onClick={() => setAgencyStatusFilter(s)}
                            className={`inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[11px] font-bold uppercase tracking-[0.1em] transition-colors shrink-0 ${
                              active
                                ? "bg-brand-charcoal text-white"
                                : "text-brand-charcoal/60 hover:text-brand-charcoal"
                            }`}
                          >
                            {s}
                            <span className={`rounded-full px-1.5 py-0.5 text-[9.5px] ${active ? "bg-white/20 text-white" : "bg-brand-charcoal/8 text-brand-charcoal/60"}`}>
                              {n}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
                {filteredAgencies.length === 0 ? (
                  <EmptyState
                    label={
                      agencyStatusFilter === "pending"
                        ? "No agency accounts awaiting approval."
                        : "No agency accounts match this filter."
                    }
                    icon={UserCheck}
                  />
                ) : (
                  <ul className="space-y-2" data-testid="admin-agencies-list">
                    {filteredAgencies.map((a) => (
                      <AgencyRow
                        key={a.id}
                        agency={a}
                        busy={agencyActionBusy === a.id}
                        onApprove={handleApproveAgency}
                        onReject={handleRejectAgency}
                        onSendPasswordReset={handleSendPasswordReset}
                        refreshTick={agencyDrillRefreshTick}
                        onCreateInvoice={(ag) =>
                          setManualInvoiceContact({
                            user_id: ag.id,
                            email: ag.email,
                            name: ag.name || "",
                            organization: ag.organization || "",
                            tax_exempt: ag.tax_exempt_status === "approved",
                          })
                        }
                        highlighted={
                          !!_urlHighlight && (a.email || "").toLowerCase() === _urlHighlight
                        }
                      />
                    ))}
                  </ul>
                )}
              </>
            ) : tab === "gallery" ? (
              <>
                <div className="flex items-center justify-between mb-3">
                  <p className="text-[12px] text-brand-charcoal/55">
                    Posts auto-publish to <span className="font-bold text-brand-charcoal/70">/gallery</span> on save.
                  </p>
                  <button
                    type="button"
                    data-testid="admin-new-experience-btn"
                    onClick={handleNewExperience}
                    className="inline-flex items-center gap-2 rounded-full bg-brand-charcoal text-white px-4 py-2 text-[11px] font-bold uppercase tracking-[0.12em] hover:bg-brand-teal transition-colors"
                  >
                    <Plus className="h-3.5 w-3.5" />
                    New Post
                  </button>
                </div>
                {filteredExperiences.length === 0 ? (
                  <EmptyState label="No gallery posts yet — click New Post above." icon={ImageIcon} />
                ) : (
                  <ul className="space-y-2" data-testid="admin-gallery-list">
                    {filteredExperiences.map((e) => (
                      <ExperienceRow key={e.id} entry={e} onEdit={handleEditExperience} />
                    ))}
                  </ul>
                )}
              </>
            ) : tab === "packages" ? (
              <>
                <div className="flex items-center justify-between mb-3">
                  <p className="text-[12px] text-brand-charcoal/55">
                    Edits go live on the <span className="font-bold text-brand-charcoal/70">homepage</span> the moment you save.
                  </p>
                </div>
                {filteredPackages.length === 0 ? (
                  <EmptyState label="No packages found." icon={PackageIcon} />
                ) : (
                  <ul className="space-y-2" data-testid="admin-packages-list">
                    {filteredPackages.map((p) => {
                      const fullIdx = packages.findIndex((x) => x.id === p.id);
                      return (
                        <PackageRow
                          key={p.id}
                          pkg={p}
                          onEdit={handleEditPackage}
                          onMove={handleMovePackage}
                          canMoveUp={fullIdx > 0}
                          canMoveDown={
                            fullIdx >= 0 && fullIdx < packages.length - 1
                          }
                        />
                      );
                    })}
                  </ul>
                )}
              </>
            ) : tab === "menu" ? (
              <>
                <PricingMarkupPanel
                  categories={menuCategories}
                  onSaved={reloadMenuItems}
                />
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
                  <p className="text-[12px] text-brand-charcoal/55">
                    Edits go live on the{" "}
                    <span className="font-bold text-brand-charcoal/70">
                      /menu
                    </span>{" "}
                    page instantly. Agency price auto-applies +
                    {Math.round((menuMarkup - 1) * 100)}%.
                  </p>
                  <select
                    data-testid="admin-menu-category-filter"
                    value={menuCategoryFilter}
                    onChange={(e) => setMenuCategoryFilter(e.target.value)}
                    className="h-10 rounded-xl bg-white border border-black/10 px-3 text-[12.5px] font-medium text-brand-charcoal"
                  >
                    <option value="all">All categories ({menuItems.length})</option>
                    {menuCategories.map((c) => {
                      const n = menuItems.filter((i) => i.category === c).length;
                      return (
                        <option key={c} value={c}>
                          {c} ({n})
                        </option>
                      );
                    })}
                  </select>
                </div>
                {menuCategoryFilter !== "all" && (
                  <button
                    type="button"
                    data-testid="admin-menu-back-to-categories"
                    onClick={() => setMenuCategoryFilter("all")}
                    className="mb-3 inline-flex items-center gap-1.5 rounded-full border border-black/10 bg-white px-3.5 py-2 text-[11px] font-bold uppercase tracking-[0.12em] text-brand-charcoal hover:border-brand-teal hover:text-brand-teal transition-colors"
                  >
                    <ArrowLeft className="h-3.5 w-3.5" /> All categories
                  </button>
                )}
                {menuCategoryFilter === "all" && !search.trim() ? (
                  <MenuCategoryTiles
                    categories={menuCategories}
                    items={menuItems}
                    onOpen={setMenuCategoryFilter}
                    onReorder={handleReorderCategories}
                  />
                ) : filteredMenuItems.length === 0 ? (
                  <EmptyState label="No menu items match your filters." icon={UtensilsCrossed} />
                ) : menuCategoryFilter !== "all" && !search.trim() ? (
                  <ReorderableList
                    ids={filteredMenuItems.map((i) => i.id)}
                    onReorder={handleReorderItems}
                    testid="admin-menu-item"
                    className="space-y-2"
                  >
                    {(id, handle) => {
                      const it = filteredMenuItems.find((x) => x.id === id);
                      return (
                        <div className="flex items-center gap-2">
                          {handle}
                          <ul className="flex-1 min-w-0">
                            <MenuItemRow
                              item={it}
                              markup={menuMarkup}
                              markupByCat={menuMarkupByCat}
                              onEdit={handleEditMenuItem}
                              onDelete={handleDeleteMenuItem}
                            />
                          </ul>
                        </div>
                      );
                    }}
                  </ReorderableList>
                ) : (
                  <ul className="space-y-2" data-testid="admin-menu-list">
                    {filteredMenuItems.map((it) => (
                      <MenuItemRow
                        key={it.id}
                        item={it}
                        markup={menuMarkup}
                        markupByCat={menuMarkupByCat}
                        onEdit={handleEditMenuItem}
                        onDelete={handleDeleteMenuItem}
                      />
                    ))}
                  </ul>
                )}
              </>
            ) : tab === "storage" ? (
              <StorageTab />
            ) : tab === "newsletter" ? (
              <NewsletterTab />
            ) : tab === "polaris" ? (
              <PolarisPOTab />
            ) : tab === "hiring" ? (
              <HiringTab />
            ) : tab === "tax-exempt" ? (
              <TaxExemptTab />
            ) : tab === "pay-approvals" ? (
              <PaymentApprovalsTab />
            ) : tab === "holiday" ? (
              <HolidayTab />
            ) : tab === "private-customers" ? (
              <PrivateCustomersTab onOpenAssign={(order) => setAssignAgencyOrder(order)} />
            ) : tab === "activity" ? (
              <>
                <div className="mb-3">
                  <p className="text-[12px] text-brand-charcoal/55">
                    Latest activity across all admin areas (newest first, last 80 events).
                  </p>
                </div>
                {filteredActivity.length === 0 ? (
                  <EmptyState label="No activity yet." icon={Activity} />
                ) : (
                  <ul className="space-y-2" data-testid="admin-activity-list">
                    {filteredActivity.map((a) => (
                      <ActivityRow key={a.id} entry={a} />
                    ))}
                  </ul>
                )}
              </>
            ) : filteredQuotes.length === 0 ? (
              <EmptyState label="No quote requests yet." icon={FileText} />
            ) : (
              <ul className="space-y-2" data-testid="admin-quotes-list">
                {filteredQuotes.map((q) => (
                  <QuoteRow
                    key={q.id}
                    quote={q}
                    onConvertToInvoice={handleConvertQuoteToInvoice}
                  />
                ))}
              </ul>
            )}
          </div>

          {stats && (
            <p className="mt-6 text-[11px] text-brand-charcoal/45 inline-flex items-center gap-2">
              <Users className="h-3.5 w-3.5" />
              {stats.users_total} registered users · sandbox data
            </p>
          )}
        </div>
      </main>

      <Footer />

      <OrderEditorModal
        open={editorOpen}
        onOpenChange={(v) => {
          setEditorOpen(v);
          if (!v) {
            setEditingOrder(null);
            setEditorPrefill(null);
          }
        }}
        menu={menu}
        order={editingOrder}
        prefill={editorPrefill}
        onSaved={handleEditorSaved}
      />

      <ManualInvoiceModal
        open={manualInvoiceOpen}
        onClose={() => setManualInvoiceOpen(false)}
        menu={menu}
        onCreated={async () => {
          await reloadOrders();
        }}
      />

      {/* Same modal, opened pre-filled with an agency's contact from the
          Agencies drill-down "Create Invoice" button. Kept as a distinct
          instance so the two entry points don't stomp each other's state. */}
      <ManualInvoiceModal
        open={!!manualInvoiceContact}
        onClose={() => setManualInvoiceContact(null)}
        menu={menu}
        defaultContact={manualInvoiceContact}
        onCreated={async () => {
          await reloadOrders();
          setAgencyDrillRefreshTick((n) => n + 1);
        }}
      />

      {/* Attach existing orders to an agency (or unlink). Uses the same
          list of approved agencies already loaded for the Agencies tab. */}
      {assignAgencyOrder && (
        <AssignAgencyModal
          order={assignAgencyOrder}
          agencies={agencies}
          onClose={() => setAssignAgencyOrder(null)}
          onSaved={async () => {
            await reloadOrders();
            setAgencyDrillRefreshTick((n) => n + 1);
          }}
        />
      )}

      {markPaidOrder && (
        <MarkAsPaidModal
          order={markPaidOrder}
          onClose={() => setMarkPaidOrder(null)}
          onSaved={handleMarkPaidSaved}
        />
      )}

      {sendInvoiceOrder && (
        <SendInvoiceModal
          order={sendInvoiceOrder}
          onClose={() => setSendInvoiceOrder(null)}
          onSent={handleSendInvoiceSent}
        />
      )}

      {bulkMode === "edit" && (
        <BulkEditModal
          orders={selectedOrders}
          onClose={() => setBulkMode(null)}
          onSubmit={handleBulkEditSubmit}
        />
      )}
      {bulkMode === "email" && (
        <BulkEmailModal
          orders={selectedOrders}
          selectionMixed={selectionMixed}
          onClose={() => setBulkMode(null)}
          onSubmit={handleBulkSendSubmit}
        />
      )}
      {bulkMode === "batch" && (
        <BulkAddToBatchModal
          orders={selectedOrders}
          onClose={() => setBulkMode(null)}
          onSubmit={handleBulkAddToBatchSubmit}
        />
      )}

      <ExperienceEditorModal
        open={expEditorOpen}
        onOpenChange={(v) => {
          setExpEditorOpen(v);
          if (!v) setEditingExp(null);
        }}
        experience={editingExp}
        onSaved={handleExperienceSaved}
        onDeleted={handleExperienceDeleted}
      />

      <PackageEditorModal
        open={pkgEditorOpen}
        onOpenChange={(v) => {
          setPkgEditorOpen(v);
          if (!v) setEditingPkg(null);
        }}
        pkg={editingPkg}
        onSaved={handlePackageSaved}
      />

      <MenuItemEditorModal
        open={menuEditorOpen}
        onOpenChange={(v) => {
          setMenuEditorOpen(v);
          if (!v) setEditingMenuItem(null);
        }}
        item={editingMenuItem}
        categories={menuCategories}
        markup={menuMarkup}
        markupByCat={menuMarkupByCat}
        onSaved={handleMenuItemSaved}
      />

      {toast && (
        <div
          data-testid="admin-toast"
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 rounded-full bg-brand-charcoal text-white px-5 py-3 text-[12.5px] font-bold shadow-[0_20px_50px_-20px_rgba(0,0,0,0.4)]"
        >
          {toast}
        </div>
      )}
    </div>
  );
};

const ExperienceRow = ({ entry, onEdit }) => {
  const primary = (entry.images || [])[0];
  const dateStr = entry.date
    ? new Date(entry.date).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : "—";
  return (
    <li
      data-testid={`admin-gallery-row-${entry.id}`}
      className="rounded-2xl bg-white border border-black/5 overflow-hidden"
    >
      <button
        type="button"
        onClick={() => onEdit?.(entry)}
        className="w-full flex items-stretch gap-3 text-left hover:bg-brand-cream/40 transition-colors"
      >
        <div className="w-24 sm:w-28 aspect-square shrink-0 bg-brand-cream">
          {primary ? (
            <img
              src={primary}
              alt=""
              loading="lazy"
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-brand-charcoal/30">
              <ImageIcon className="h-6 w-6" />
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0 p-3 sm:p-4">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center rounded-full bg-brand-teal/12 text-brand-teal px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.1em]">
              {entry.category}
            </span>
            {entry.is_published === false && (
              <span className="inline-flex items-center rounded-full bg-brand-charcoal/10 text-brand-charcoal/65 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.1em]">
                Hidden
              </span>
            )}
            <span className="text-[11px] text-brand-charcoal/55">{dateStr}</span>
          </div>
          <p className="mt-1.5 font-bold text-[14px] text-brand-charcoal leading-tight line-clamp-2">
            {entry.title}
          </p>
          <p className="mt-1 text-[12px] text-brand-charcoal/65 line-clamp-2">
            {entry.description}
          </p>
          {entry.tags?.length > 0 && (
            <div className="mt-1.5 flex flex-wrap gap-1">
              {entry.tags.slice(0, 4).map((t) => (
                <span
                  key={t}
                  className="text-[10.5px] font-bold text-brand-charcoal/50"
                >
                  #{t}
                </span>
              ))}
            </div>
          )}
        </div>
        <div className="flex items-center pr-4 text-brand-charcoal/40">
          <Edit3 className="h-4 w-4" />
        </div>
      </button>
    </li>
  );
};

const EmptyState = ({ label, icon: Icon }) => (
  <div className="flex flex-col items-center text-center py-20 rounded-2xl bg-white border border-dashed border-black/10">
    <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-brand-cream text-brand-charcoal/50">
      <Icon className="h-5 w-5" />
    </span>
    <p className="mt-4 text-[13.5px] text-brand-charcoal/55">{label}</p>
  </div>
);


const StorageTab = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState("");
  const [migration, setMigration] = useState(null);

  const refresh = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/admin/storage/status");
      setStatus(data);
    } catch (e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const runMigration = async () => {
    if (running) return;
    const ok = window.confirm(
      "Migrate any local-disk images into MongoDB GridFS? Safe to re-run.\n\n" +
        "Run this on PRODUCTION before redeploying to make sure no admin-uploaded images are lost."
    );
    if (!ok) return;
    setRunning(true);
    setError("");
    setMigration(null);
    try {
      const { data } = await api.post("/admin/storage/migrate");
      setMigration(data.migration);
      setStatus(data.status);
    } catch (e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setRunning(false);
    }
  };

  if (loading) {
    return <p className="text-[13px] text-brand-charcoal/60">Loading storage status…</p>;
  }
  if (error) {
    return (
      <div className="rounded-2xl bg-red-50 border border-red-200 p-4 text-[13px] text-red-700">
        {error}
      </div>
    );
  }
  if (!status) return null;
  const c = status.counts;
  const total = c.gridfs + c.local + c.external;

  return (
    <div className="space-y-4" data-testid="admin-storage-tab">
      <div
        className={`rounded-2xl border p-4 ${
          status.all_safe
            ? "bg-brand-green/10 border-brand-green/30 text-brand-green-dark"
            : "bg-brand-gold/15 border-brand-gold/40 text-brand-charcoal"
        }`}
      >
        <div className="flex items-start gap-3">
          <div
            className={`shrink-0 h-9 w-9 inline-flex items-center justify-center rounded-full ${
              status.all_safe ? "bg-brand-green text-white" : "bg-brand-gold text-brand-charcoal"
            }`}
          >
            {status.all_safe ? <CheckCircle2 className="h-5 w-5" /> : <AlertTriangle className="h-5 w-5" />}
          </div>
          <div className="flex-1">
            <h3 className="font-display font-black text-[16px] tracking-tight">
              {status.all_safe
                ? "All images are safe for redeploy"
                : `${status.at_risk_total} image${status.at_risk_total === 1 ? "" : "s"} are on local disk`}
            </h3>
            <p className="mt-1 text-[12.5px] leading-relaxed opacity-90">
              {status.all_safe
                ? "Every image reference is either in MongoDB or on an external CDN. You can redeploy without losing anything."
                : "Local-disk images will be wiped when you redeploy. Click 'Migrate Now' below to move them into MongoDB first."}
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <StorageStat label="MongoDB (safe)" value={c.gridfs} tone="safe" />
        <StorageStat label="Local disk (at risk)" value={c.local} tone="warn" />
        <StorageStat label="External CDN (safe)" value={c.external} tone="safe" />
        <StorageStat label="No image" value={c.missing} tone="neutral" />
      </div>

      <div className="rounded-2xl bg-white border border-black/5 p-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h4 className="font-display font-black text-[14px] text-brand-charcoal">
              Manual migration
            </h4>
            <p className="text-[12px] text-brand-charcoal/55 leading-snug">
              Scans `/app/backend/uploads/*`, uploads each file to GridFS, and rewrites DB references.
              Idempotent — safe to run multiple times.
            </p>
          </div>
          <button
            type="button"
            onClick={runMigration}
            disabled={running}
            data-testid="admin-storage-migrate-btn"
            className="shrink-0 inline-flex items-center gap-2 rounded-full bg-brand-charcoal text-white px-5 py-2.5 text-[11px] font-bold uppercase tracking-[0.1em] hover:bg-brand-teal transition-colors disabled:opacity-50"
          >
            <CloudUpload className="h-3.5 w-3.5" />
            {running ? "Migrating…" : "Migrate Now"}
          </button>
        </div>
        {migration && (
          <div
            data-testid="admin-storage-migration-result"
            className="mt-4 rounded-xl bg-brand-cream/40 border border-black/5 p-3 text-[12px] text-brand-charcoal"
          >
            <p className="font-bold mb-1">Last migration result:</p>
            <ul className="space-y-0.5">
              {Object.entries(migration).map(([ns, s]) => (
                <li key={ns}>
                  <span className="font-semibold capitalize">{ns}</span> · {s.files_found} found ·{" "}
                  <span className="text-brand-green">{s.uploaded} uploaded</span> · {s.skipped} skipped ·{" "}
                  {s.docs_updated} DB refs rewritten
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {!status.all_safe && status.at_risk_urls?.length > 0 && (
        <details className="rounded-2xl bg-white border border-black/5 p-4">
          <summary className="cursor-pointer text-[12.5px] font-bold text-brand-charcoal">
            Show {status.at_risk_total} at-risk URL{status.at_risk_total === 1 ? "" : "s"}
            {status.at_risk_total > status.at_risk_urls.length && (
              <span className="ml-1 font-normal text-brand-charcoal/55">
                (first {status.at_risk_urls.length})
              </span>
            )}
          </summary>
          <ul className="mt-3 space-y-1 max-h-72 overflow-y-auto text-[11.5px] font-mono text-brand-charcoal/75">
            {status.at_risk_urls.map((u) => (
              <li key={u} className="truncate">
                {u}
              </li>
            ))}
            {status.at_risk_total > status.at_risk_urls.length && (
              <li className="italic text-brand-charcoal/55">
                …and {status.at_risk_total - status.at_risk_urls.length} more
              </li>
            )}
          </ul>
        </details>
      )}

      <P469XSeedPanel />

      <ClearTestDataPanel />

      <button
        type="button"
        onClick={refresh}
        className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.1em] text-brand-charcoal/65 hover:text-brand-charcoal"
      >
        Refresh status
      </button>
    </div>
  );
};

const P469XSeedPanel = () => {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const run = async () => {
    if (busy) return;
    const ok = window.confirm(
      "Seed the six P469X Breakfast invoices into the orders collection?\n\n" +
        "Idempotent — POs already in the DB will be skipped."
    );
    if (!ok) return;
    setBusy(true);
    setError("");
    setResult(null);
    try {
      const { data } = await api.post("/admin/seed/p469x-invoices");
      setResult(data);
    } catch (e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="admin-p469x-seed-panel"
      className="rounded-2xl bg-white border border-black/5 p-4"
    >
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h4 className="font-display font-black text-[14px] text-brand-charcoal">
            One-shot · P469X Breakfast invoices
          </h4>
          <p className="text-[12px] text-brand-charcoal/55 leading-snug">
            Creates the 6 P469X agency invoices (POs WO27000107–WO270000112,
            project total $3,877.50) and the &quot;Continental Breakfast Package A&quot;
            menu item ($11.50 per guest). Idempotent — existing POs are
            retrofitted in place to use the new per-person pricing. Tap once
            after deploy to sync, then ask me to remove this button.
          </p>
        </div>
        <button
          type="button"
          onClick={run}
          disabled={busy}
          data-testid="admin-p469x-seed-btn"
          className="shrink-0 inline-flex items-center gap-2 rounded-full bg-brand-teal text-brand-charcoal px-5 py-2.5 text-[11px] font-bold uppercase tracking-[0.1em] hover:bg-brand-teal/85 transition-colors disabled:opacity-50"
        >
          <FileText className="h-3.5 w-3.5" />
          {busy ? "Seeding…" : "Seed P469X Invoices"}
        </button>
      </div>
      {error && (
        <div
          data-testid="admin-p469x-seed-error"
          className="mt-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-[12px] text-red-700"
        >
          {error}
        </div>
      )}
      {result && (
        <div
          data-testid="admin-p469x-seed-result"
          className="mt-4 rounded-xl bg-brand-cream/40 border border-black/5 p-3 text-[12px] text-brand-charcoal"
        >
          <p className="font-bold mb-1.5">
            <span className="text-brand-green">{result.created_count} created</span>
            {result.retrofitted_count > 0 && (
              <>
                {" · "}
                <span className="text-brand-teal">{result.retrofitted_count} retrofitted</span>
              </>
            )}
            {" · "}
            <span className="text-brand-charcoal/70">
              {result.unchanged_count ?? result.skipped_count ?? 0} unchanged
            </span>
          </p>
          {result.menu_item_status && (
            <p className="mb-2 text-[11px] text-brand-charcoal/60">
              Menu item · <span className="font-mono">{result.menu_item_id}</span>{" "}
              <span
                className={
                  result.menu_item_status === "created"
                    ? "text-brand-green font-bold"
                    : result.menu_item_status === "updated"
                    ? "text-brand-teal font-bold"
                    : "text-brand-charcoal/50"
                }
              >
                {result.menu_item_status}
              </span>
            </p>
          )}
          <ul className="space-y-0.5 font-mono text-[11.5px]">
            {(result.results || []).map((r) => (
              <li key={r.po} className="flex items-center gap-2">
                <span
                  className={`inline-block w-20 ${
                    r.status === "created"
                      ? "text-brand-green"
                      : r.status === "retrofitted"
                      ? "text-brand-teal"
                      : "text-brand-charcoal/50"
                  }`}
                >
                  {r.status}
                </span>
                <span>{r.po}</span>
                {r.total !== undefined && (
                  <span className="text-brand-charcoal/55">· ${r.total.toFixed(2)}</span>
                )}
              </li>
            ))}
          </ul>
          <p className="mt-2 text-[11px] text-brand-charcoal/55">
            Refresh the Orders tab to see them.
          </p>
        </div>
      )}
    </div>
  );
};

const ClearTestDataPanel = () => {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const refresh = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/admin/test-data/preview");
      setPreview(data);
    } catch (e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const purge = async () => {
    if (!preview) return;
    const willKeep = (preview.total_orders || 0) - (preview.orders || 0);
    const ok = window.confirm(
      `This will permanently delete:\n` +
        `  · ${preview.users} test users (@example.com)\n` +
        `  · ${preview.orders} test orders ($0 / TEST refs / @example.com)\n` +
        `  · ${preview.quote_requests} test quote requests\n` +
        `  · ${preview.partnership_inquiries} test partnership inquiries\n\n` +
        `${willKeep} real order${willKeep === 1 ? "" : "s"} will be kept.\n\n` +
        `This cannot be undone. Continue?`,
    );
    if (!ok) return;
    setBusy(true);
    setError("");
    setResult(null);
    try {
      const { data } = await api.post("/admin/clear-test-data", {
        confirm: true,
      });
      setResult(data.deleted);
      await refresh();
    } catch (e) {
      setError(
        formatApiErrorDetail(e?.response?.data?.detail) ||
          e.message ||
          "Could not clear data.",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="rounded-2xl bg-white border border-red-200/60 p-4 space-y-3"
      data-testid="admin-clear-test-data-panel"
    >
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="shrink-0 h-9 w-9 inline-flex items-center justify-center rounded-full bg-red-50 text-red-700">
            <Trash className="h-4 w-4" />
          </div>
          <div>
            <h4 className="font-display font-black text-[14px] text-brand-charcoal">
              Clear test data
            </h4>
            <p className="text-[12px] text-brand-charcoal/55 leading-snug max-w-md">
              Purges test users (<code>@example.com</code>), $0/TEST-prefixed
              orders, and their associated quotes &amp; partnerships. Real
              customer orders are kept.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={purge}
          disabled={busy || loading || !preview}
          data-testid="admin-clear-test-data-btn"
          className="shrink-0 inline-flex items-center gap-2 rounded-full bg-red-600 text-white px-5 py-2.5 text-[11px] font-bold uppercase tracking-[0.1em] hover:bg-red-700 transition-colors disabled:opacity-50"
        >
          <Trash2 className="h-3.5 w-3.5" />
          {busy ? "Clearing…" : "Clear test data"}
        </button>
      </div>

      {loading ? (
        <p className="text-[12px] text-brand-charcoal/55">
          Loading what would be purged…
        </p>
      ) : preview ? (
        <div
          className="grid grid-cols-2 md:grid-cols-5 gap-2"
          data-testid="admin-clear-test-data-preview"
        >
          <PurgeStat label="Test users" value={preview.users} />
          <PurgeStat label="Test orders" value={preview.orders} />
          <PurgeStat label="Test quotes" value={preview.quote_requests} />
          <PurgeStat label="Test partnerships" value={preview.partnership_inquiries} />
          <PurgeStat
            label="Real orders kept"
            value={(preview.total_orders || 0) - (preview.orders || 0)}
            tone="safe"
          />
        </div>
      ) : null}

      {result && (
        <div
          data-testid="admin-clear-test-data-result"
          className="rounded-xl bg-brand-green/10 border border-brand-green/30 p-3 text-[12.5px] text-brand-green-dark"
        >
          Cleared <b>{result.users}</b> users · <b>{result.orders}</b> orders ·{" "}
          <b>{result.quote_requests}</b> quotes ·{" "}
          <b>{result.partnership_inquiries}</b> partnerships ·{" "}
          <b>{result.payment_transactions}</b> payments.{" "}
          <b>{result.remaining_orders}</b> real order{result.remaining_orders === 1 ? "" : "s"} kept.
        </div>
      )}
      {error && (
        <div className="rounded-xl bg-red-50 border border-red-200 p-3 text-[12.5px] text-red-700">
          {error}
        </div>
      )}
    </div>
  );
};

const PurgeStat = ({ label, value, tone }) => {
  const cls =
    tone === "safe"
      ? "border-brand-green/30 text-brand-green-dark bg-brand-green/8"
      : value > 0
      ? "border-red-200 text-red-700 bg-red-50/60"
      : "border-black/8 text-brand-charcoal/55 bg-brand-cream/40";
  return (
    <div className={`rounded-2xl border p-3 ${cls}`}>
      <p className="text-[10px] font-bold uppercase tracking-[0.08em] opacity-80">{label}</p>
      <p className="mt-1 font-display font-black text-xl tabular-nums">{value ?? 0}</p>
    </div>
  );
};

const StorageStat = ({ label, value, tone }) => {
  const toneCls =
    tone === "safe"
      ? "border-brand-green/30 text-brand-green-dark bg-brand-green/8"
      : tone === "warn"
      ? "border-brand-gold/40 text-brand-charcoal bg-brand-gold/15"
      : "border-black/8 text-brand-charcoal/65 bg-brand-cream/40";
  return (
    <div className={`rounded-2xl border p-3 ${toneCls}`}>
      <p className="text-[10px] font-bold uppercase tracking-[0.1em] opacity-80">{label}</p>
      <p className="mt-1 font-display font-black text-2xl tabular-nums">{value}</p>
    </div>
  );
};

const AgencyRow = ({ agency, busy, onApprove, onReject, onSendPasswordReset, onCreateInvoice, highlighted = false, refreshTick = 0 }) => {
  const status = agency.status || "approved";
  const statusStyles = {
    pending: "bg-brand-gold/15 text-brand-gold-dark",
    approved: "bg-brand-green/15 text-brand-green",
    rejected: "bg-red-50 text-red-700",
  };
  const created = agency.created_at
    ? new Date(agency.created_at).toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
      })
    : "—";
  const rowRef = useRef(null);
  const [expanded, setExpanded] = useState(false);
  const [invLoading, setInvLoading] = useState(false);
  const [invData, setInvData] = useState(null);
  const [invError, setInvError] = useState("");

  useEffect(() => {
    if (highlighted && rowRef.current) {
      // Bring the highlighted applicant into view when the admin deep-links
      // from the "Pending approval" email. Runs once per mount.
      rowRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadOrders = async (force = false) => {
    if (invData && !force) return; // cached
    setInvLoading(true);
    setInvError("");
    try {
      const { data } = await api.get(`/admin/agencies/${agency.id}/orders`);
      setInvData(data);
    } catch (e) {
      setInvError(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load orders");
    } finally {
      setInvLoading(false);
    }
  };

  // Force reload when parent bumps the refreshTick (e.g., after a new
  // invoice is created via the drill-down "Create Invoice" button).
  useEffect(() => {
    if (expanded && refreshTick > 0) loadOrders(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTick]);

  const toggleExpand = () => {
    const next = !expanded;
    setExpanded(next);
    if (next) loadOrders();
  };

  const copyPayLink = async (order) => {
    if (!order.share_token) return;
    const url = `${window.location.origin}/invoice/${order.share_token}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Pay link copied");
    } catch {
      toast.error("Copy failed");
    }
  };

  const downloadOrderPdf = async (order) => {
    try {
      const resp = await api.get(`/admin/orders/${order.id}/pdf`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${order.invoice_number || order.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Download failed");
    }
  };

  return (
    <li
      ref={rowRef}
      data-testid={`admin-agency-row-${agency.id}`}
      className={`rounded-2xl bg-white border p-3 sm:p-4 transition-shadow ${
        highlighted
          ? "border-amber-400 ring-2 ring-amber-300/60 shadow-md"
          : "border-black/5"
      }`}
    >
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.1em] ${statusStyles[status] || statusStyles.approved}`}
            >
              {status}
            </span>
            <p className="font-display font-black text-[20px] sm:text-[22px] text-brand-charcoal tracking-tight leading-tight truncate">
              {agency.name || "—"}
            </p>
          </div>
          <p className="mt-1 text-[13px] text-brand-charcoal/70 truncate">
            <span className="font-bold text-brand-charcoal">{agency.email}</span>
            {agency.organization ? <span> · <b className="text-brand-charcoal">{agency.organization}</b></span> : null}
          </p>
          <p className="mt-0.5 text-[11px] text-brand-charcoal/45">
            Signed up {created}
            {status === "rejected" && agency.rejection_reason
              ? ` · Reason: ${agency.rejection_reason}`
              : ""}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap shrink-0">
          {status === "approved" && (
            <button
              type="button"
              data-testid={`admin-agency-view-orders-${agency.id}`}
              onClick={toggleExpand}
              className="inline-flex items-center gap-1.5 rounded-full bg-white border border-black/10 text-brand-charcoal px-3.5 py-2 text-[11px] font-bold uppercase tracking-[0.1em] hover:border-brand-teal hover:text-brand-teal transition-colors"
            >
              <FileText className="h-3.5 w-3.5" />
              {expanded ? "Hide" : "Invoices"}
              <ChevronDown className={`h-3.5 w-3.5 transition-transform ${expanded ? "rotate-180" : ""}`} />
            </button>
          )}
          {status !== "approved" && (
            <button
              type="button"
              data-testid={`admin-agency-approve-${agency.id}`}
              onClick={() => onApprove(agency)}
              disabled={busy}
              className="inline-flex items-center gap-1.5 rounded-full bg-brand-green text-white px-3.5 py-2 text-[11px] font-bold uppercase tracking-[0.1em] hover:bg-emerald-600 transition-colors disabled:opacity-50"
            >
              <CheckCircle2 className="h-3.5 w-3.5" />
              Approve
            </button>
          )}
          {status !== "rejected" && (
            <button
              type="button"
              data-testid={`admin-agency-reject-${agency.id}`}
              onClick={() => onReject(agency)}
              disabled={busy}
              className="inline-flex items-center gap-1.5 rounded-full bg-white border border-red-200 text-red-700 px-3.5 py-2 text-[11px] font-bold uppercase tracking-[0.1em] hover:bg-red-50 transition-colors disabled:opacity-50"
            >
              <XCircle className="h-3.5 w-3.5" />
              Reject
            </button>
          )}
          {status === "approved" && onSendPasswordReset && (
            <button
              type="button"
              data-testid={`admin-agency-reset-password-${agency.id}`}
              onClick={() => onSendPasswordReset(agency)}
              disabled={busy}
              title="Email a password reset link"
              className="inline-flex items-center gap-1.5 rounded-full bg-white border border-brand-charcoal/15 text-brand-charcoal px-3.5 py-2 text-[11px] font-bold uppercase tracking-[0.1em] hover:border-brand-teal hover:text-brand-teal transition-colors disabled:opacity-50"
            >
              <Mail className="h-3.5 w-3.5" />
              Reset Password
            </button>
          )}
        </div>
      </div>

      {expanded && (
        <div
          data-testid={`admin-agency-orders-panel-${agency.id}`}
          className="mt-4 rounded-2xl bg-brand-cream/40 border border-black/5 p-3 sm:p-4"
        >
          <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
            <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-brand-charcoal/55">
              Invoices &amp; orders
            </p>
            <button
              type="button"
              data-testid={`admin-agency-create-invoice-${agency.id}`}
              onClick={() => onCreateInvoice?.(agency)}
              className="inline-flex items-center gap-1.5 rounded-full bg-brand-teal text-white px-3.5 py-1.5 text-[10.5px] font-bold uppercase tracking-[0.1em] hover:bg-brand-charcoal transition-colors"
            >
              <Plus className="h-3.5 w-3.5" />
              New Pay-Link Invoice
            </button>
          </div>
          {invLoading && (
            <p className="text-[12.5px] text-brand-charcoal/55">Loading orders…</p>
          )}
          {!invLoading && invError && (
            <p className="text-[12.5px] text-red-700">{invError}</p>
          )}
          {!invLoading && invData && invData.items.length === 0 && (
            <div className="rounded-xl border border-dashed border-brand-charcoal/15 bg-white/60 px-3 py-6 text-center">
              <p className="text-[13px] text-brand-charcoal/60">
                No invoices yet for this agency.
              </p>
              <p className="mt-1 text-[11.5px] text-brand-charcoal/45">
                Use <b>New Pay-Link Invoice</b> above to create one, or attach
                existing orders from the Orders tab.
              </p>
            </div>
          )}
          {!invLoading && invData && invData.items.length > 0 && (
            <>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-brand-charcoal/60 mb-3">
                <span>
                  <b className="text-brand-charcoal">{invData.total_orders}</b> total order{invData.total_orders === 1 ? "" : "s"}
                </span>
                <span>
                  Lifetime value:{" "}
                  <b className="text-brand-charcoal tabular-nums">
                    ${invData.lifetime_value.toFixed(2)}
                  </b>
                </span>
                {invData.agency.tax_exempt_status && (
                  <span>
                    Tax status:{" "}
                    <b className={`uppercase tracking-[0.08em] ${
                      invData.agency.tax_exempt_status === "approved"
                        ? "text-brand-green"
                        : invData.agency.tax_exempt_status === "pending"
                        ? "text-brand-gold-dark"
                        : "text-red-700"
                    }`}>
                      {invData.agency.tax_exempt_status}
                    </b>
                  </span>
                )}
              </div>
              <ul className="space-y-2" data-testid={`admin-agency-orders-list-${agency.id}`}>
                {invData.items.map((o) => (
                  <AgencyOrderRow
                    key={o.id}
                    order={o}
                    onCopyPayLink={copyPayLink}
                    onDownloadPdf={downloadOrderPdf}
                  />
                ))}
              </ul>
            </>
          )}
        </div>
      )}
    </li>
  );
};

const AgencyOrderRow = ({ order, onCopyPayLink, onDownloadPdf }) => {
  const typeLabel = {
    self_pay_invoice: { label: "Invoice", cls: "bg-brand-teal/15 text-brand-teal" },
    manual_invoice: { label: "Manual Invoice", cls: "bg-brand-teal/15 text-brand-teal" },
    agency_po: { label: "PO", cls: "bg-brand-gold/15 text-brand-gold-dark" },
    agency_cash: { label: "Cash", cls: "bg-brand-cream text-brand-charcoal" },
    stripe: { label: "Card", cls: "bg-brand-green/15 text-brand-green" },
  }[order.type] || { label: order.type || "Order", cls: "bg-brand-cream text-brand-charcoal" };

  const statusLabel = {
    awaiting_payment: "Awaiting Payment",
    paid: "Paid",
    submitted: "Submitted",
    cancelled: "Cancelled",
  }[order.status] || (order.status || "—");

  const dt = order.created_at ? new Date(order.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";
  const ed = order.event_date ? new Date(order.event_date).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : null;

  const isSelfPay = order.type === "self_pay_invoice" || order.type === "manual_invoice";
  return (
    <li
      data-testid={`admin-agency-order-${order.id}`}
      className="rounded-xl bg-white border border-black/5 p-3"
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.1em] ${typeLabel.cls}`}>
              {typeLabel.label}
            </span>
            <p className="font-bold text-[13px] text-brand-charcoal tabular-nums">
              {order.invoice_number || order.id.slice(0, 8)}
            </p>
            <span className="text-[10.5px] font-bold uppercase tracking-[0.08em] text-brand-charcoal/50">
              {statusLabel}
            </span>
            {order.tax_exempt && (
              <span className="text-[10px] font-bold uppercase tracking-[0.08em] text-brand-green">Tax-exempt</span>
            )}
          </div>
          <p className="mt-0.5 text-[11.5px] text-brand-charcoal/55">
            Created {dt}
            {ed ? ` · Event ${ed}` : ""}
            {order.items_count ? ` · ${order.items_count} item${order.items_count === 1 ? "" : "s"}` : ""}
            {order.po_number ? ` · PO ${order.po_number}` : ""}
          </p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <p className="font-display font-black text-[15px] text-brand-charcoal tabular-nums">
            ${(order.total || 0).toFixed(2)}
          </p>
          <div className="flex items-center gap-1.5">
            {isSelfPay && order.share_token && (
              <button
                type="button"
                onClick={() => onCopyPayLink(order)}
                data-testid={`admin-agency-order-copy-link-${order.id}`}
                className="inline-flex items-center gap-1 rounded-full bg-white border border-black/10 text-brand-charcoal px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.08em] hover:border-brand-teal hover:text-brand-teal transition-colors"
                title="Copy pay link"
              >
                <Copy className="h-3 w-3" />
                Link
              </button>
            )}
            <button
              type="button"
              onClick={() => onDownloadPdf(order)}
              data-testid={`admin-agency-order-pdf-${order.id}`}
              className="inline-flex items-center gap-1 rounded-full bg-brand-charcoal text-white px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.08em] hover:bg-brand-teal transition-colors"
              title="Download PDF"
            >
              <Download className="h-3 w-3" />
              PDF
            </button>
            <a
              href={`/admin/orders?highlight=${encodeURIComponent(order.id)}`}
              data-testid={`admin-agency-order-edit-${order.id}`}
              className="inline-flex items-center gap-1 rounded-full bg-white border border-brand-teal/40 text-brand-teal px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.08em] hover:bg-brand-teal hover:text-white transition-colors"
              title="Open in Orders tab"
            >
              <ExternalLink className="h-3 w-3" />
              Edit
            </a>
          </div>
        </div>
      </div>
    </li>
  );
};


const PackageRow = ({ pkg, onEdit, onMove, canMoveUp, canMoveDown }) => {
  const stop = (e) => e.stopPropagation();
  return (
    <li
      data-testid={`admin-package-row-${pkg.id}`}
      className="rounded-2xl bg-white border border-black/5 overflow-hidden"
    >
      <div className="flex items-stretch gap-3 hover:bg-brand-cream/40 transition-colors">
        {/* Reorder controls */}
        <div className="flex flex-col items-center justify-center gap-1 px-2 sm:px-3 py-2 bg-brand-cream/40 border-r border-black/5">
          <button
            type="button"
            data-testid={`admin-package-move-up-${pkg.id}`}
            onClick={(e) => {
              stop(e);
              onMove?.(pkg.id, -1);
            }}
            disabled={!canMoveUp}
            aria-label="Move up"
            className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-white border border-black/10 text-brand-charcoal/70 hover:text-brand-teal hover:border-brand-teal disabled:opacity-30 disabled:hover:text-brand-charcoal/70 disabled:hover:border-black/10"
          >
            <ArrowUp className="h-3.5 w-3.5" />
          </button>
          <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-brand-charcoal/40 tabular-nums">
            {(pkg.sort_order ?? "—")}
          </span>
          <button
            type="button"
            data-testid={`admin-package-move-down-${pkg.id}`}
            onClick={(e) => {
              stop(e);
              onMove?.(pkg.id, 1);
            }}
            disabled={!canMoveDown}
            aria-label="Move down"
            className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-white border border-black/10 text-brand-charcoal/70 hover:text-brand-teal hover:border-brand-teal disabled:opacity-30 disabled:hover:text-brand-charcoal/70 disabled:hover:border-black/10"
          >
            <ArrowDown className="h-3.5 w-3.5" />
          </button>
        </div>

        <button
          type="button"
          onClick={() => onEdit?.(pkg)}
          className="flex-1 flex items-stretch gap-3 text-left min-w-0"
        >
          <div className="w-24 sm:w-32 aspect-square shrink-0 bg-brand-cream">
            {pkg.image ? (
              <img
                src={pkg.image}
                alt=""
                loading="lazy"
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-brand-charcoal/30">
                <ImageIcon className="h-6 w-6" />
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0 p-3 sm:p-4">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="inline-flex items-center rounded-full bg-brand-teal/12 text-brand-teal px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.1em]">
                Homepage Package
              </span>
              {pkg.is_published === false && (
                <span className="inline-flex items-center rounded-full bg-brand-charcoal/10 text-brand-charcoal/65 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.1em]">
                  Hidden
                </span>
              )}
              <span className="text-[10.5px] text-brand-charcoal/45 font-mono">
                {pkg.id}
              </span>
            </div>
            <p className="mt-1.5 font-bold text-[14px] text-brand-charcoal leading-tight line-clamp-2">
              {pkg.name}
            </p>
            <p className="mt-1 text-[12px] text-brand-charcoal/65 line-clamp-2">
              {pkg.description}
            </p>
            {pkg.best_for && (
              <p className="mt-1.5 text-[11px] text-brand-charcoal/55 line-clamp-1">
                <span className="font-bold uppercase tracking-[0.12em] text-[9.5px] text-brand-charcoal/45">
                  Best for:
                </span>{" "}
                {pkg.best_for}
              </p>
            )}
          </div>
          <div className="flex items-center pr-4 text-brand-charcoal/40">
            <Edit3 className="h-4 w-4" />
          </div>
        </button>
      </div>
    </li>
  );
};

export default AdminOrdersPage;

const ACTIVITY_KIND_META = {
  "order.created": { icon: Plus, color: "bg-emerald-100 text-emerald-700", label: "Order Created" },
  "order.updated": { icon: Pencil, color: "bg-blue-100 text-blue-700", label: "Order Updated" },
  "order.invoice_sent": { icon: Send, color: "bg-amber-100 text-amber-700", label: "Invoice Sent" },
  "order.paid": { icon: CreditCard, color: "bg-emerald-100 text-emerald-700", label: "Payment Received" },
  "partnership.created": { icon: Building2, color: "bg-violet-100 text-violet-700", label: "Partnership" },
  "quote.created": { icon: FileText, color: "bg-sky-100 text-sky-700", label: "Quote Request" },
  "gallery.created": { icon: ImageIcon, color: "bg-pink-100 text-pink-700", label: "Gallery Created" },
  "gallery.updated": { icon: Pencil, color: "bg-pink-100 text-pink-700", label: "Gallery Updated" },
  "gallery.deleted": { icon: Trash, color: "bg-red-100 text-red-700", label: "Gallery Deleted" },
  "package.updated": { icon: PackageIcon, color: "bg-orange-100 text-orange-700", label: "Package Updated" },
  "package.reordered": { icon: ArrowUp, color: "bg-orange-100 text-orange-700", label: "Packages Reordered" },
  "menu.updated": { icon: UtensilsCrossed, color: "bg-teal-100 text-teal-700", label: "Menu Updated" },
};

const formatActivityWhen = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const now = new Date();
    const diffSec = Math.floor((now - d) / 1000);
    if (diffSec < 60) return "just now";
    if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
    if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
    if (diffSec < 86400 * 7) return `${Math.floor(diffSec / 86400)}d ago`;
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch {
    return "";
  }
};

const ActivityRow = ({ entry }) => {
  const meta = ACTIVITY_KIND_META[entry.kind] || {
    icon: Sparkles,
    color: "bg-brand-charcoal/10 text-brand-charcoal/70",
    label: entry.kind,
  };
  const Icon = meta.icon;
  return (
    <li
      data-testid={`admin-activity-row-${entry.id}`}
      className="rounded-2xl bg-white border border-black/5 px-3 sm:px-4 py-3 flex items-start gap-3"
    >
      <span
        className={`shrink-0 inline-flex h-9 w-9 items-center justify-center rounded-full ${meta.color}`}
      >
        <Icon className="h-4 w-4" />
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="inline-flex items-center rounded-full bg-brand-cream/60 text-brand-charcoal/70 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.1em]">
            {meta.label}
          </span>
          <span className="text-[11px] text-brand-charcoal/45">
            {formatActivityWhen(entry.created_at)}
          </span>
        </div>
        <p className="mt-1 text-[13px] text-brand-charcoal leading-snug">
          {entry.summary}
        </p>
      </div>
    </li>
  );
};

// Compact editor for the agency markup config: default + per-category overrides.
// Rendered above the menu grid in the admin Menu tab. Saves via
// PUT /admin/pricing/markup and asks the parent to reload the menu so
// downstream previews (MenuItemRow, editors) refresh with the new rates.
// Admin batches tab — one row per submitted agency batch, expandable to
// show its child orders. Each child order also renders a batch badge in
// the Orders tab so the two views cross-reference cleanly.
const BatchesTab = () => {
  const [loading, setLoading] = useState(true);
  const [batches, setBatches] = useState([]);
  const [error, setError] = useState("");
  const [openBatchId, setOpenBatchId] = useState(null);
  const [detail, setDetail] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/batches");
      setBatches(data.items || []);
    } catch (e) {
      setError(e?.message || "Failed to load batches");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const openDetail = async (b) => {
    if (openBatchId === b.id) {
      setOpenBatchId(null);
      setDetail(null);
      return;
    }
    setOpenBatchId(b.id);
    setDetail(null);
    try {
      const { data } = await api.get(`/admin/batches/${b.id}`);
      setDetail(data);
    } catch (e) {
      setError(e?.message || "Failed to load batch detail");
    }
  };

  if (loading) {
    return (
      <div className="py-16 text-center text-[12.5px] text-brand-charcoal/55">
        <Loader2 className="mx-auto h-5 w-5 animate-spin" />
        <p className="mt-2">Loading batches…</p>
      </div>
    );
  }
  if (error) {
    return <p className="text-[12.5px] text-red-600">{error}</p>;
  }
  if (batches.length === 0) {
    return <EmptyState label="No batches yet." icon={Layers} />;
  }
  return (
    <ul data-testid="admin-batches-list" className="space-y-2">
      {batches.map((b) => {
        const isOpen = openBatchId === b.id;
        return (
          <li
            key={b.id}
            data-testid={`admin-batch-row-${b.id}`}
            className="rounded-2xl bg-white border border-black/5 overflow-hidden"
          >
            <button
              type="button"
              onClick={() => openDetail(b)}
              className="w-full text-left hover:bg-brand-cream/40 transition-colors px-4 py-3.5"
            >
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="inline-flex items-center gap-1 rounded-full bg-brand-gold/25 text-brand-gold-dark px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.1em]">
                      <Layers className="h-3 w-3" />
                      {b.ref}
                    </span>
                    <span className="text-[13.5px] font-bold text-brand-charcoal truncate">
                      {b.name}
                    </span>
                  </div>
                  <p className="mt-1 text-[11.5px] text-brand-charcoal/55">
                    {b.user_email} · {b.drops_count} drops · {b.mode === "same" ? "Same items" : "Different items"}
                  </p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-[15px] font-black text-brand-charcoal tabular-nums">
                    {fmtMoney(b.total)}
                  </p>
                  <p className="text-[10.5px] text-brand-charcoal/55">
                    {fmtDate(b.created_at).split(",")[0]}
                  </p>
                </div>
              </div>
            </button>
            {isOpen && (
              <div className="border-t border-black/5 bg-brand-cream/30 px-4 sm:px-5 py-4">
                {!detail ? (
                  <p className="text-[12px] text-brand-charcoal/55 py-3 text-center">
                    <Loader2 className="mx-auto h-4 w-4 animate-spin" />
                  </p>
                ) : (
                  <ul className="divide-y divide-black/5">
                    {detail.orders.map((o) => (
                      <li
                        key={o.id}
                        data-testid={`admin-batch-order-${o.id}`}
                        className="py-3 flex items-baseline justify-between gap-3 flex-wrap"
                      >
                        <div className="min-w-0 flex-1">
                          <p className="text-[12.5px] font-bold text-brand-charcoal">
                            #{o.batch_index} · {o.customer_name}{" "}
                            <span className="text-brand-charcoal/55 font-normal">
                              — {o.city}, {o.state}
                            </span>
                          </p>
                          <p className="text-[11px] text-brand-charcoal/55">
                            {o.event_date}
                            {o.event_time ? ` · ${o.event_time}` : ""} · PO {o.po_number} ·
                            delivery {fmtMoney(o.delivery_fee)}
                          </p>
                        </div>
                        <p className="text-[13px] font-black text-brand-charcoal tabular-nums">
                          {fmtMoney(o.total)}
                        </p>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
};



const PricingMarkupPanel = ({ categories = [], onSaved }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [defaultMk, setDefaultMk] = useState("1.20");
  const [rows, setRows] = useState([]); // [{_uid, category: "Beverages", markup: "1.10"}]
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/pricing/markup");
      setDefaultMk(String(data.default ?? 1.2));
      const entries = Object.entries(data.by_category || {}).map(([k, v]) => ({
        _uid: `row-${k}-${Math.random().toString(36).slice(2, 8)}`,
        category: k,
        markup: String(v),
      }));
      setRows(entries);
    } catch (e) {
      setError(e?.message || "Failed to load pricing config");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addRow = () => {
    // Pick a category that isn't already overridden, if any.
    const used = new Set(rows.map((r) => r.category));
    const next = categories.find((c) => !used.has(c)) || "";
    setRows((r) => [
      ...r,
      { _uid: `row-new-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, category: next, markup: "1.15" },
    ]);
  };
  const updateRow = (idx, patch) =>
    setRows((r) => r.map((row, i) => (i === idx ? { ...row, ...patch } : row)));
  const removeRow = (idx) => setRows((r) => r.filter((_, i) => i !== idx));

  const save = async () => {
    setError("");
    const d = parseFloat(defaultMk);
    if (!(d >= 1.0 && d <= 5.0)) {
      setError("Default markup must be between 1.00 and 5.00.");
      return;
    }
    const by_category = {};
    for (const r of rows) {
      const key = (r.category || "").trim();
      if (!key) continue;
      const val = parseFloat(r.markup);
      if (!(val >= 1.0 && val <= 5.0)) {
        setError(`Markup for "${key}" must be 1.00–5.00.`);
        return;
      }
      if (by_category[key] !== undefined) {
        setError(`Duplicate category override: "${key}".`);
        return;
      }
      by_category[key] = val;
    }
    setSaving(true);
    try {
      const { data } = await api.put("/admin/pricing/markup", {
        default: d,
        by_category,
      });
      setDefaultMk(String(data.default));
      setRows(
        Object.entries(data.by_category || {}).map(([k, v]) => ({
          _uid: `row-${k}-${Math.random().toString(36).slice(2, 8)}`,
          category: k,
          markup: String(v),
        })),
      );
      setToast("Pricing updated — live everywhere");
      setTimeout(() => setToast(""), 3500);
      await onSaved?.();
    } catch (e) {
      setError(
        formatApiErrorDetail(e?.response?.data?.detail) ||
          e?.message ||
          "Save failed",
      );
    } finally {
      setSaving(false);
    }
  };

  const usedCats = new Set(rows.map((r) => r.category));

  return (
    <section
      data-testid="admin-pricing-markup-panel"
      className="mb-5 rounded-2xl bg-brand-cream/50 border border-brand-charcoal/10 p-4 sm:p-5"
    >
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h3 className="font-black text-[15px] text-brand-charcoal tracking-tight">
            Agency pricing
          </h3>
          <p className="mt-0.5 text-[12px] text-brand-charcoal/60 leading-snug">
            Set the default markup applied to every agency price, plus optional
            per-category overrides (e.g. Beverages at a lower rate). Changes are
            live everywhere — cart, checkout, invoices, PDFs.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <label className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-brand-charcoal/65">
            Default
          </label>
          <input
            data-testid="admin-pricing-default-markup"
            type="number"
            step="0.01"
            min="1.0"
            max="5.0"
            value={defaultMk}
            onChange={(e) => setDefaultMk(e.target.value)}
            disabled={loading}
            className="h-9 w-24 rounded-lg bg-white border border-black/10 px-2.5 text-[13px] font-bold text-brand-charcoal tabular-nums text-right disabled:opacity-50"
          />
          <span className="text-[11px] text-brand-charcoal/55">
            = +{Math.round((parseFloat(defaultMk || "0") - 1) * 100) || 0}%
          </span>
        </div>
      </header>

      <div className="mt-4 space-y-2">
        {rows.length === 0 && !loading && (
          <p className="text-[12px] text-brand-charcoal/50 italic">
            No per-category overrides. Every category uses the default markup.
          </p>
        )}
        {rows.map((row, idx) => {
          const pct = Math.round((parseFloat(row.markup || "0") - 1) * 100) || 0;
          return (
            <div
              key={row._uid || `row-${row.category}-${idx}`}
              data-testid={`admin-pricing-row-${idx}`}
              className="flex items-center gap-2 flex-wrap"
            >
              <select
                data-testid={`admin-pricing-row-${idx}-category`}
                value={row.category}
                onChange={(e) => updateRow(idx, { category: e.target.value })}
                className="h-9 flex-1 min-w-[160px] rounded-lg bg-white border border-black/10 px-2.5 text-[12.5px] text-brand-charcoal"
              >
                {row.category && !categories.includes(row.category) && (
                  <option value={row.category}>{row.category}</option>
                )}
                <option value="">— pick a category —</option>
                {categories.map((c) => (
                  <option
                    key={c}
                    value={c}
                    disabled={c !== row.category && usedCats.has(c)}
                  >
                    {c}
                    {c !== row.category && usedCats.has(c) ? " (already set)" : ""}
                  </option>
                ))}
              </select>
              <input
                data-testid={`admin-pricing-row-${idx}-markup`}
                type="number"
                step="0.01"
                min="1.0"
                max="5.0"
                value={row.markup}
                onChange={(e) => updateRow(idx, { markup: e.target.value })}
                className="h-9 w-24 rounded-lg bg-white border border-black/10 px-2.5 text-[13px] font-bold text-brand-charcoal tabular-nums text-right"
              />
              <span className="text-[11px] text-brand-charcoal/55 tabular-nums w-14 text-right">
                +{pct}%
              </span>
              <button
                type="button"
                data-testid={`admin-pricing-row-${idx}-remove`}
                onClick={() => removeRow(idx)}
                className="inline-flex items-center justify-center h-9 w-9 rounded-lg bg-white border border-black/10 text-brand-charcoal/50 hover:text-red-600 hover:border-red-200 transition-colors"
                title="Remove override"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          );
        })}
      </div>

      {error && (
        <p
          data-testid="admin-pricing-error"
          className="mt-3 text-[12px] font-bold text-red-600"
        >
          {error}
        </p>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-2 justify-between">
        <button
          type="button"
          data-testid="admin-pricing-add-row"
          onClick={addRow}
          className="inline-flex items-center gap-1.5 rounded-full bg-white border border-black/10 text-brand-charcoal px-3.5 py-1.5 text-[11px] font-bold uppercase tracking-[0.1em] hover:border-brand-teal hover:text-brand-teal transition-colors"
        >
          <Plus className="h-3.5 w-3.5" />
          Add category override
        </button>
        <div className="flex items-center gap-3">
          {toast && (
            <span
              data-testid="admin-pricing-toast"
              className="text-[11.5px] font-bold text-brand-teal"
            >
              {toast}
            </span>
          )}
          <button
            type="button"
            data-testid="admin-pricing-save"
            onClick={save}
            disabled={saving || loading}
            className="inline-flex items-center gap-1.5 rounded-full bg-brand-charcoal text-white px-4 py-2 text-[11px] font-bold uppercase tracking-[0.1em] hover:bg-brand-teal transition-colors disabled:opacity-60"
          >
            {saving ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <CheckCircle2 className="h-3.5 w-3.5" />
            )}
            {saving ? "Saving…" : "Save pricing"}
          </button>
        </div>
      </div>
    </section>
  );
};


const MenuItemRow = ({ item, markup, markupByCat, onEdit, onDelete }) => {
  const price = typeof item.price === "number" ? item.price : null;
  const effMarkup =
    (markupByCat && item.category && markupByCat[item.category]) || markup;
  const agencyPrice = price != null ? (price * effMarkup).toFixed(2) : null;
  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const confirmTimerRef = useRef(null);

  // Auto-reset the confirm state after 4s so a click that gets forgotten
  // doesn't sit there waiting to nuke the row on the next accidental tap.
  useEffect(() => {
    if (!confirming) return undefined;
    confirmTimerRef.current = setTimeout(() => setConfirming(false), 4000);
    return () => clearTimeout(confirmTimerRef.current);
  }, [confirming]);

  const handleTrashClick = async (e) => {
    e.stopPropagation();
    if (!confirming) {
      setConfirming(true);
      return;
    }
    // second click → actually delete
    clearTimeout(confirmTimerRef.current);
    setDeleting(true);
    try {
      await onDelete?.(item);
      // Row will unmount on parent reload; no need to reset state.
    } catch {
      setDeleting(false);
      setConfirming(false);
    }
  };

  return (
    <li
      data-testid={`admin-menu-row-${item.id}`}
      className="relative rounded-2xl bg-white border border-black/5 overflow-hidden"
    >
      <button
        type="button"
        onClick={() => onEdit?.(item)}
        disabled={deleting}
        className="w-full flex items-stretch gap-3 text-left hover:bg-brand-cream/40 transition-colors disabled:opacity-50"
      >
        <div className="w-20 sm:w-24 aspect-square shrink-0 bg-brand-cream">
          {item.image ? (
            <img
              src={item.image}
              alt=""
              loading="lazy"
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-brand-charcoal/30">
              <ImageIcon className="h-5 w-5" />
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0 p-3">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center rounded-full bg-brand-teal/12 text-brand-teal px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.1em]">
              {item.category || "—"}
            </span>
            {item.is_published === false && (
              <span className="inline-flex items-center rounded-full bg-brand-charcoal/10 text-brand-charcoal/65 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.1em]">
                Hidden
              </span>
            )}
            {item.quote_only && (
              <span
                data-testid={`admin-menu-row-quote-only-${item.id}`}
                className="inline-flex items-center rounded-full bg-brand-gold/20 text-brand-gold-dark px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.1em]"
              >
                Quote only
              </span>
            )}
            <span className="text-[10px] text-brand-charcoal/45 font-mono truncate">
              {item.id}
            </span>
          </div>
          <p className="mt-1 font-bold text-[13.5px] text-brand-charcoal leading-tight line-clamp-2">
            {item.name}
          </p>
          {item.description && (
            <p className="mt-0.5 text-[11.5px] text-brand-charcoal/55 line-clamp-2">
              {item.description}
            </p>
          )}
        </div>
        <div className="flex flex-col items-end justify-center gap-0.5 pr-14 sm:pr-16 shrink-0">
          {price != null ? (
            <>
              <p className="font-display font-black text-[15px] sm:text-[16px] text-brand-charcoal tabular-nums leading-none">
                ${price.toFixed(2)}
              </p>
              <p className="text-[10px] text-brand-charcoal/45 tabular-nums">
                Ag ${agencyPrice}
              </p>
            </>
          ) : (
            <p className="text-[11px] text-brand-charcoal/40">—</p>
          )}
          <Edit3 className="h-3.5 w-3.5 text-brand-charcoal/40 mt-1" />
        </div>
      </button>
      {/* Inline trash icon — sits alongside the row edit affordance.
          Absolute-positioned because the row itself is a <button> and
          nested buttons are invalid HTML. Two-click confirmation avoids
          accidental deletions. */}
      <button
        type="button"
        data-testid={`admin-menu-row-delete-${item.id}`}
        aria-label={confirming ? `Confirm delete ${item.name}` : `Delete ${item.name}`}
        onClick={handleTrashClick}
        disabled={deleting}
        title={confirming ? "Click again to confirm" : "Delete item"}
        className={`absolute top-1/2 -translate-y-1/2 right-3 sm:right-4 inline-flex items-center justify-center rounded-full transition-all disabled:opacity-60 ${
          confirming
            ? "h-8 px-2.5 gap-1.5 bg-red-600 text-white text-[10.5px] font-bold uppercase tracking-[0.08em] shadow-md hover:bg-red-700 ring-2 ring-red-600/30"
            : "h-8 w-8 bg-white border border-red-200 text-red-500 hover:bg-red-50 hover:text-red-700 hover:border-red-300"
        }`}
      >
        {deleting ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : confirming ? (
          <>
            <Trash2 className="h-3 w-3" />
            <span>Confirm</span>
          </>
        ) : (
          <Trash2 className="h-3.5 w-3.5" />
        )}
      </button>
    </li>
  );
};


// ---------- Newsletter (Resend Audiences + Broadcasts) ----------
const NewsletterTab = () => {
  const [subs, setSubs] = useState({ items: [], total: 0, active: 0 });
  const [subsLoading, setSubsLoading] = useState(true);
  const [subsError, setSubsError] = useState("");
  const [broadcasts, setBroadcasts] = useState([]);
  const [broadcastsLoading, setBroadcastsLoading] = useState(true);
  const [env, setEnv] = useState({ live_sends_allowed: true, sender_email: "" });

  const refresh = async () => {
    setSubsLoading(true);
    setSubsError("");
    try {
      const { data } = await api.get("/admin/newsletter/subscribers");
      setSubs(data);
    } catch (e) {
      setSubsError(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setSubsLoading(false);
    }
  };

  const refreshBroadcasts = async () => {
    setBroadcastsLoading(true);
    try {
      const { data } = await api.get("/admin/newsletter/broadcasts");
      setBroadcasts(data.items || []);
    } catch (_) {
      setBroadcasts([]);
    } finally {
      setBroadcastsLoading(false);
    }
  };

  const refreshEnv = async () => {
    try {
      const { data } = await api.get("/admin/newsletter/env");
      setEnv(data || { live_sends_allowed: true, sender_email: "" });
    } catch (_) {
      // Safe default — assume live (production) if the check fails so we don't
      // give a false sense of safety.
      setEnv({ live_sends_allowed: true, sender_email: "" });
    }
  };

  useEffect(() => {
    refresh();
    refreshBroadcasts();
    refreshEnv();
  }, []);

  return (
    <div className="space-y-6" data-testid="admin-newsletter-tab">
      {!env.live_sends_allowed && (
        <div
          data-testid="newsletter-preview-mode-banner"
          className="rounded-2xl bg-brand-gold/15 border-2 border-brand-gold/50 p-4 flex items-start gap-3"
        >
          <div className="shrink-0 h-9 w-9 inline-flex items-center justify-center rounded-full bg-brand-gold text-brand-charcoal">
            <AlertTriangle className="h-5 w-5" />
          </div>
          <div className="flex-1">
            <h3 className="font-display font-black text-[15px] text-brand-charcoal">
              You&apos;re on the preview environment — live sends are blocked
            </h3>
            <p className="mt-1 text-[12.5px] leading-relaxed text-brand-charcoal/80">
              Drafts will be saved to your Resend dashboard, but{" "}
              <b>Send Now</b> and <b>Schedule</b> will refuse to actually
              deliver. To send to your real audience, sign in to the admin
              panel at <a href="https://freshfastfoodsllc.com" target="_blank" rel="noopener noreferrer" className="underline font-bold">freshfastfoodsllc.com</a>.
            </p>
          </div>
        </div>
      )}
      <div className="rounded-2xl bg-brand-teal/10 border border-brand-teal/30 p-4">
        <div className="flex items-start gap-3">
          <div className="shrink-0 h-9 w-9 inline-flex items-center justify-center rounded-full bg-brand-teal text-white">
            <Mail className="h-4.5 w-4.5" />
          </div>
          <div className="flex-1">
            <h3 className="font-display font-black text-[16px] tracking-tight text-brand-charcoal">
              Newsletter
            </h3>
            <p className="mt-1 text-[12.5px] leading-relaxed text-brand-charcoal/70">
              Send weekly or bi-weekly newsletters to your contact list. Every
              new customer, quote, and partnership is auto-subscribed via
              Resend. Compose below, then send now or schedule for later.
            </p>
            <div className="mt-2 flex gap-4 text-[11.5px] font-medium text-brand-charcoal/70">
              <span data-testid="newsletter-total-count">
                <b className="text-brand-charcoal">{subs.total}</b> total contacts
              </span>
              <span data-testid="newsletter-active-count">
                <b className="text-brand-green-dark">{subs.active}</b> active subscribers
              </span>
            </div>
          </div>
        </div>
      </div>

      <NewsletterComposer
        subscriberCount={subs.active}
        onSent={() => refreshBroadcasts()}
      />

      <NewsletterCsvImport onImported={() => refresh()} />

      <SubscribersPhonebook
        loading={subsLoading}
        error={subsError}
        subscribers={subs.items}
        onChanged={refresh}
      />

      <div className="rounded-2xl bg-white border border-black/5 p-4">
        <div className="mb-3">
          <h4 className="font-display font-black text-[14px] text-brand-charcoal">
            Broadcast history
          </h4>
          <p className="text-[12px] text-brand-charcoal/55 leading-snug">
            Newsletters created from this dashboard. Status updates from Resend.
          </p>
        </div>
        {broadcastsLoading ? (
          <p className="text-[13px] text-brand-charcoal/60">Loading…</p>
        ) : broadcasts.length === 0 ? (
          <EmptyState label="No newsletters sent yet." icon={SendIcon} />
        ) : (
          <ul
            className="divide-y divide-black/5 rounded-xl border border-black/5"
            data-testid="newsletter-broadcasts-list"
          >
            {broadcasts.slice(0, 25).map((b) => (
              <BroadcastRow key={b.id} broadcast={b} />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

const SubscriberRow = ({ sub, onRemoved }) => {
  const [removing, setRemoving] = useState(false);

  const remove = async () => {
    if (
      !window.confirm(
        `Unsubscribe ${sub.email} from the newsletter? They won't receive future broadcasts.`,
      )
    )
      return;
    setRemoving(true);
    try {
      await api.delete(
        `/admin/newsletter/subscribers/${encodeURIComponent(sub.email)}`,
      );
      onRemoved?.();
    } catch (e) {
      window.alert(
        formatApiErrorDetail(e?.response?.data?.detail) ||
          "Could not unsubscribe.",
      );
    } finally {
      setRemoving(false);
    }
  };

  const fullName = [sub.first_name, sub.last_name]
    .filter(Boolean)
    .join(" ")
    .trim();

  return (
    <li
      className="px-3 py-2.5 flex items-center justify-between gap-3"
      data-testid={`newsletter-subscriber-${sub.email}`}
    >
      <div className="min-w-0 flex-1">
        <p className="text-[13px] font-bold text-brand-charcoal truncate">
          {sub.email}
        </p>
        <p className="text-[11px] text-brand-charcoal/55 truncate">
          {fullName || "—"}
          {sub.unsubscribed && (
            <span className="ml-2 inline-block rounded-full bg-red-100 text-red-700 px-2 py-[1px] text-[9.5px] font-bold uppercase tracking-wide">
              Unsubscribed
            </span>
          )}
        </p>
      </div>
      <button
        type="button"
        onClick={remove}
        disabled={removing}
        data-testid={`newsletter-remove-${sub.email}`}
        className="shrink-0 inline-flex items-center gap-1 rounded-full bg-red-50 text-red-700 hover:bg-red-100 transition-colors px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider disabled:opacity-50"
        aria-label={`Unsubscribe ${sub.email}`}
      >
        <Trash2 className="h-3 w-3" />
        Remove
      </button>
    </li>
  );
};

const BroadcastRow = ({ broadcast }) => {
  const status = (broadcast.status || "draft").toLowerCase();
  const tone =
    status === "sent" || status === "delivered"
      ? "bg-brand-green/15 text-brand-green-dark"
      : status === "scheduled" || status === "queued"
      ? "bg-brand-gold/20 text-brand-charcoal"
      : status === "draft"
      ? "bg-black/5 text-brand-charcoal/65"
      : "bg-red-50 text-red-700";

  return (
    <li className="px-3 py-2.5 flex items-center justify-between gap-3">
      <div className="min-w-0 flex-1">
        <p className="text-[13px] font-bold text-brand-charcoal truncate">
          {broadcast.name || broadcast.subject || "(untitled)"}
        </p>
        <p className="text-[11px] text-brand-charcoal/55 truncate">
          {broadcast.subject || "—"}
          {broadcast.scheduled_at && (
            <span className="ml-2">
              · scheduled {fmtDate(broadcast.scheduled_at)}
            </span>
          )}
          {broadcast.sent_at && (
            <span className="ml-2">· sent {fmtDate(broadcast.sent_at)}</span>
          )}
          {broadcast.created_at && !broadcast.sent_at && !broadcast.scheduled_at && (
            <span className="ml-2">· created {fmtDate(broadcast.created_at)}</span>
          )}
        </p>
      </div>
      <span
        className={`shrink-0 inline-flex items-center rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider ${tone}`}
      >
        {status}
      </span>
    </li>
  );
};

const ManualSubscriberAdd = ({ onAdded }) => {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [first, setFirst] = useState("");
  const [last, setLast] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      await api.post("/admin/newsletter/subscribers", {
        email: email.trim(),
        first_name: first.trim() || null,
        last_name: last.trim() || null,
      });
      setEmail("");
      setFirst("");
      setLast("");
      setOpen(false);
      onAdded?.();
    } catch (e2) {
      setErr(
        formatApiErrorDetail(e2?.response?.data?.detail) ||
          "Could not add subscriber.",
      );
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="newsletter-add-subscriber-btn"
        className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-brand-charcoal text-white hover:bg-brand-teal transition-colors px-3.5 py-2 text-[11px] font-bold uppercase tracking-wider"
      >
        <Plus className="h-3.5 w-3.5" />
        Add
      </button>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid="newsletter-add-subscriber-form"
      className="absolute mt-12 right-4 z-20 w-80 rounded-2xl bg-white border border-black/10 shadow-xl p-3 space-y-2"
    >
      <Input
        autoFocus
        required
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="email@example.com"
        data-testid="newsletter-add-email"
        className="h-9 text-[12.5px]"
      />
      <div className="grid grid-cols-2 gap-2">
        <Input
          value={first}
          onChange={(e) => setFirst(e.target.value)}
          placeholder="First name"
          data-testid="newsletter-add-first"
          className="h-9 text-[12.5px]"
        />
        <Input
          value={last}
          onChange={(e) => setLast(e.target.value)}
          placeholder="Last name"
          data-testid="newsletter-add-last"
          className="h-9 text-[12.5px]"
        />
      </div>
      {err && <p className="text-[11.5px] text-red-700">{err}</p>}
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={busy}
          data-testid="newsletter-add-submit"
          className="flex-1 rounded-full bg-brand-charcoal text-white px-3 py-2 text-[11px] font-bold uppercase tracking-wider disabled:opacity-50"
        >
          {busy ? "Adding…" : "Add"}
        </button>
        <button
          type="button"
          onClick={() => {
            setOpen(false);
            setErr("");
          }}
          className="rounded-full bg-black/5 text-brand-charcoal px-3 py-2 text-[11px] font-bold uppercase tracking-wider"
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

const NewsletterCsvImport = ({ onImported }) => {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // reset so the same file can be re-picked
    if (!file) return;
    setBusy(true);
    setError("");
    setResult(null);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post(
        "/admin/newsletter/import-csv",
        form,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setResult(data.summary);
      onImported?.();
    } catch (e2) {
      setError(
        formatApiErrorDetail(e2?.response?.data?.detail) ||
          e2.message ||
          "Import failed.",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-2xl bg-white border border-black/5 p-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h4 className="font-display font-black text-[14px] text-brand-charcoal">
            Import contacts (CSV)
          </h4>
          <p className="text-[12px] text-brand-charcoal/55 leading-snug">
            Upload a .csv with an <code>email</code> column (optionally
            <code> first_name</code>, <code>last_name</code>). One email per
            line also works. Duplicates are skipped.
          </p>
        </div>
        <label
          htmlFor="newsletter-csv-input"
          data-testid="newsletter-csv-upload-label"
          className="shrink-0 inline-flex items-center gap-2 rounded-full bg-brand-charcoal text-white hover:bg-brand-teal transition-colors px-5 py-2.5 text-[11px] font-bold uppercase tracking-[0.1em] cursor-pointer disabled:opacity-50"
        >
          <UploadCloud className="h-3.5 w-3.5" />
          {busy ? "Importing…" : "Upload CSV"}
          <input
            id="newsletter-csv-input"
            data-testid="newsletter-csv-input"
            type="file"
            accept=".csv,.txt,text/csv"
            className="hidden"
            disabled={busy}
            onChange={upload}
          />
        </label>
      </div>
      {result && (
        <div
          data-testid="newsletter-csv-result"
          className="mt-3 rounded-xl bg-brand-green/10 border border-brand-green/30 p-3 text-[12.5px] text-brand-green-dark"
        >
          Imported <b>{result.added}</b> of <b>{result.total}</b> contacts
          {result.failed > 0 && ` · ${result.failed} failed`}.
        </div>
      )}
      {error && (
        <div className="mt-3 rounded-xl bg-red-50 border border-red-200 p-3 text-[12.5px] text-red-700">
          {error}
        </div>
      )}
    </div>
  );
};

// ---------- File-upload-based newsletter composer ----------
// Replaces the old contentEditable rich-text editor with a simple
// upload-an-image-or-PDF + subject + caption + send/schedule flow.

const NewsletterAssetUploader = ({ onUploaded, current, onClear }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    setError("");
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post(
        "/admin/newsletter/upload-asset",
        form,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      onUploaded?.(data);
    } catch (e) {
      setError(
        formatApiErrorDetail(e?.response?.data?.detail) ||
          e.message ||
          "Upload failed.",
      );
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer?.files?.[0];
    upload(file);
  };

  if (current) {
    return (
      <div
        data-testid="newsletter-asset-preview"
        className="rounded-xl border border-black/10 bg-brand-cream/30 p-3 flex items-center gap-3"
      >
        {current.asset_type === "image" ? (
          <img
            src={current.url}
            alt={current.filename}
            className="h-20 w-20 rounded-lg object-cover border border-black/5"
          />
        ) : (
          <div className="h-20 w-20 rounded-lg bg-brand-charcoal text-white flex flex-col items-center justify-center">
            <span className="text-[11px] font-black tracking-wider">PDF</span>
            <span className="text-[9px] opacity-70 mt-0.5">
              {Math.max(1, Math.round((current.size || 0) / 1024))} KB
            </span>
          </div>
        )}
        <div className="min-w-0 flex-1">
          <p className="text-[13px] font-bold text-brand-charcoal truncate">
            {current.filename}
          </p>
          <p className="text-[11px] text-brand-charcoal/55 truncate">
            Uploaded · {current.asset_type === "image" ? "Image" : "PDF"} ·{" "}
            <a
              href={current.url}
              target="_blank"
              rel="noreferrer"
              className="underline hover:text-brand-teal"
            >
              Preview
            </a>
          </p>
        </div>
        <button
          type="button"
          onClick={onClear}
          data-testid="newsletter-asset-clear"
          className="shrink-0 inline-flex items-center gap-1 rounded-full bg-red-50 text-red-700 hover:bg-red-100 transition-colors px-3 py-1.5 text-[10.5px] font-bold uppercase tracking-wider"
        >
          <Trash2 className="h-3 w-3" />
          Replace
        </button>
      </div>
    );
  }

  return (
    <div
      onDragOver={(e) => e.preventDefault()}
      onDrop={onDrop}
      data-testid="newsletter-asset-dropzone"
      className="rounded-xl border-2 border-dashed border-black/15 bg-brand-cream/30 p-6 text-center hover:border-brand-teal transition-colors"
    >
      <UploadCloud className="h-8 w-8 mx-auto text-brand-charcoal/40 mb-2" />
      <p className="text-[13px] font-bold text-brand-charcoal">
        Drop your newsletter here, or click to select
      </p>
      <p className="text-[11.5px] text-brand-charcoal/55 mt-1">
        Image (JPG, PNG, WebP) or PDF · up to 12 MB
      </p>
      <label
        htmlFor="newsletter-asset-input"
        className="mt-3 inline-flex items-center gap-2 rounded-full bg-brand-charcoal text-white hover:bg-brand-teal transition-colors px-5 py-2.5 text-[11px] font-bold uppercase tracking-[0.1em] cursor-pointer disabled:opacity-50"
      >
        <UploadCloud className="h-3.5 w-3.5" />
        {busy ? "Uploading…" : "Choose file"}
      </label>
      <input
        ref={inputRef}
        id="newsletter-asset-input"
        data-testid="newsletter-asset-input"
        type="file"
        accept="image/*,application/pdf,.pdf,.png,.jpg,.jpeg,.webp"
        className="hidden"
        disabled={busy}
        onChange={(e) => upload(e.target.files?.[0])}
      />
      {error && (
        <p
          data-testid="newsletter-asset-error"
          className="mt-3 text-[12px] text-red-700"
        >
          {error}
        </p>
      )}
    </div>
  );
};

const NewsletterComposer = ({ subscriberCount, onSent }) => {
  const [mode, setMode] = useState("upload"); // "upload" | "rich"
  const [subject, setSubject] = useState("");
  const [caption, setCaption] = useState("");
  const [asset, setAsset] = useState(null); // {url, asset_type, filename, size}
  const [richHtml, setRichHtml] = useState("");
  const [scheduledAt, setScheduledAt] = useState("");
  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const [previewHtml, setPreviewHtml] = useState(null);
  const [previewing, setPreviewing] = useState(false);

  const reset = () => {
    setSubject("");
    setCaption("");
    setAsset(null);
    setRichHtml("");
    setScheduledAt("");
  };

  const hasContent = () =>
    mode === "rich"
      ? richHtml.replace(/<[^>]*>/g, "").trim().length > 0
      : !!asset || caption.trim().length > 0;

  const buildPayload = (overrides = {}) => ({
    subject: subject.trim(),
    ...(mode === "rich"
      ? { rich_html: richHtml }
      : {
          caption: caption.trim() || null,
          asset_url: asset?.url || null,
          asset_type: asset?.asset_type || null,
          asset_filename: asset?.filename || null,
        }),
    name: subject.trim(),
    ...overrides,
  });

  const previewAsEmail = async () => {
    setError("");
    if (!subject.trim()) {
      setError("Please enter a subject line first.");
      return;
    }
    if (!hasContent()) {
      setError(
        mode === "rich"
          ? "Write some content first."
          : "Upload an image/PDF or write a caption to preview.",
      );
      return;
    }
    setPreviewing(true);
    try {
      const { data } = await api.post(
        "/admin/newsletter/preview",
        buildPayload({ send_now: false }),
      );
      setPreviewHtml(data.html);
    } catch (e) {
      setError(
        formatApiErrorDetail(e?.response?.data?.detail) ||
          e.message ||
          "Could not render preview.",
      );
    } finally {
      setPreviewing(false);
    }
  };

  const send = async (action) => {
    setError("");
    setSuccess("");
    if (!subject.trim()) {
      setError("Please enter a subject line.");
      return;
    }
    if (!hasContent()) {
      setError(
        mode === "rich"
          ? "Write some content first."
          : "Upload an image/PDF or write a caption first.",
      );
      return;
    }
    if (action === "send_now") {
      const confirmed = window.confirm(
        `Send "${subject}" now to ~${subscriberCount} subscribers? This cannot be undone.`,
      );
      if (!confirmed) return;
    }
    setBusy(true);
    try {
      const payload = buildPayload({
        send_now: action !== "draft",
        scheduled_at:
          action === "schedule" && scheduledAt
            ? new Date(scheduledAt).toISOString()
            : null,
      });
      const { data } = await api.post(
        "/admin/newsletter/broadcasts",
        payload,
      );
      if (action === "send_now") {
        setSuccess(
          data.sent
            ? `Sent! Resend is delivering your newsletter to ${subscriberCount} subscribers.`
            : "Broadcast created but Resend didn't confirm send. Check Resend dashboard.",
        );
      } else if (action === "schedule") {
        setSuccess(`Scheduled for ${new Date(scheduledAt).toLocaleString()}.`);
      } else {
        setSuccess("Draft saved in your Resend dashboard.");
      }
      onSent?.();
      reset();
    } catch (e) {
      setError(
        formatApiErrorDetail(e?.response?.data?.detail) ||
          e.message ||
          "Could not send.",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="rounded-2xl bg-white border border-black/5 p-4 space-y-4"
      data-testid="newsletter-composer"
    >
      <div>
        <h4 className="font-display font-black text-[14px] text-brand-charcoal">
          Compose newsletter
        </h4>
        <p className="text-[12px] text-brand-charcoal/55 leading-snug">
          Choose <b>Upload</b> for a designed image/PDF, or <b>Rich text</b>
          to type and format your message. Branded header, footer and
          unsubscribe link are added automatically.
        </p>
      </div>

      <div
        role="tablist"
        data-testid="newsletter-mode-tabs"
        className="inline-flex items-center gap-1 rounded-full bg-brand-cream/40 border border-black/10 p-1"
      >
        {[
          { id: "upload", label: "Upload (image / PDF)" },
          { id: "rich", label: "Rich text" },
        ].map((m) => {
          const active = mode === m.id;
          return (
            <button
              key={m.id}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => {
                setMode(m.id);
                setError("");
                setSuccess("");
              }}
              data-testid={`newsletter-mode-${m.id}`}
              className={`px-4 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-[0.08em] transition-colors ${
                active
                  ? "bg-brand-charcoal text-white"
                  : "text-brand-charcoal/65 hover:text-brand-charcoal"
              }`}
            >
              {m.label}
            </button>
          );
        })}
      </div>

      <div>
        <label className="block text-[11px] font-bold uppercase tracking-wider text-brand-charcoal/60 mb-1">
          Subject
        </label>
        <Input
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="e.g. This week at Fresh Fast Foods"
          data-testid="newsletter-subject"
          className="h-11 text-[14px]"
        />
      </div>

      {mode === "upload" ? (
        <>
          <div>
            <label className="block text-[11px] font-bold uppercase tracking-wider text-brand-charcoal/60 mb-1">
              Caption (optional intro)
            </label>
            <textarea
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              rows={3}
              placeholder="A short intro shown above the uploaded file. Leave blank to send the image / PDF on its own."
              data-testid="newsletter-caption"
              className="w-full rounded-xl bg-white border border-black/10 px-3 py-2.5 text-[13.5px] leading-relaxed text-brand-charcoal focus:outline-none focus:ring-2 focus:ring-brand-teal/30"
            />
          </div>

          <div>
            <label className="block text-[11px] font-bold uppercase tracking-wider text-brand-charcoal/60 mb-1">
              Newsletter file
            </label>
            <NewsletterAssetUploader
              current={asset}
              onUploaded={setAsset}
              onClear={() => setAsset(null)}
            />
          </div>
        </>
      ) : (
        <div>
          <label className="block text-[11px] font-bold uppercase tracking-wider text-brand-charcoal/60 mb-1">
            Body
          </label>
          <NewsletterRichEditor value={richHtml} onChange={setRichHtml} />
          <p className="mt-1.5 text-[11px] text-brand-charcoal/55">
            Tip: click <b>Preview as email</b> below to see exactly how it
            will look in your subscribers&apos; inbox.
          </p>
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:items-end gap-3 pt-2">
        <div className="flex-1">
          <label className="block text-[11px] font-bold uppercase tracking-wider text-brand-charcoal/60 mb-1">
            Schedule (optional)
          </label>
          <input
            type="datetime-local"
            value={scheduledAt}
            onChange={(e) => setScheduledAt(e.target.value)}
            data-testid="newsletter-schedule"
            className="h-11 w-full sm:w-auto rounded-xl bg-white border border-black/10 px-3 text-[13px] text-brand-charcoal"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => send("send_now")}
            disabled={busy}
            data-testid="newsletter-send-now"
            className="inline-flex items-center gap-2 rounded-full bg-brand-charcoal text-white px-5 py-2.5 text-[11px] font-bold uppercase tracking-[0.1em] hover:bg-brand-teal transition-colors disabled:opacity-50"
          >
            <SendIcon className="h-3.5 w-3.5" />
            {busy
              ? "Sending…"
              : `Send now${subscriberCount ? ` (${subscriberCount})` : ""}`}
          </button>
          <button
            type="button"
            onClick={() => send("schedule")}
            disabled={busy || !scheduledAt}
            data-testid="newsletter-schedule-send"
            className="inline-flex items-center gap-2 rounded-full bg-brand-gold text-brand-charcoal px-5 py-2.5 text-[11px] font-bold uppercase tracking-[0.1em] hover:opacity-90 transition-opacity disabled:opacity-40"
          >
            <CalendarIcon className="h-3.5 w-3.5" />
            Schedule
          </button>
          <button
            type="button"
            onClick={previewAsEmail}
            disabled={previewing}
            data-testid="newsletter-preview-btn"
            title="Render exactly what your subscribers will see"
            className="inline-flex items-center gap-2 rounded-full bg-brand-teal/15 text-brand-teal-dark hover:bg-brand-teal hover:text-white transition-colors px-5 py-2.5 text-[11px] font-bold uppercase tracking-[0.1em] disabled:opacity-50"
          >
            <Mail className="h-3.5 w-3.5" />
            {previewing ? "Rendering…" : "Preview as email"}
          </button>
          <button
            type="button"
            onClick={() => send("draft")}
            disabled={busy}
            data-testid="newsletter-save-draft"
            className="inline-flex items-center gap-2 rounded-full bg-black/5 text-brand-charcoal px-5 py-2.5 text-[11px] font-bold uppercase tracking-[0.1em] hover:bg-black/10 transition-colors disabled:opacity-50"
          >
            Save draft
          </button>
        </div>
      </div>

      {success && (
        <div
          data-testid="newsletter-success"
          className="rounded-xl bg-brand-green/10 border border-brand-green/30 p-3 text-[12.5px] text-brand-green-dark"
        >
          {success}
        </div>
      )}
      {error && (
        <div
          data-testid="newsletter-error"
          className="rounded-xl bg-red-50 border border-red-200 p-3 text-[12.5px] text-red-700"
        >
          {error}
        </div>
      )}

      {previewHtml != null && (
        <NewsletterPreviewModal
          html={previewHtml}
          subject={subject}
          onClose={() => setPreviewHtml(null)}
        />
      )}
    </div>
  );
};

const NewsletterPreviewModal = ({ html, subject, onClose }) => {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      data-testid="newsletter-preview-modal"
      role="dialog"
      aria-label="Newsletter preview"
      className="fixed inset-0 z-50 flex items-start sm:items-center justify-center bg-black/60 p-4 overflow-y-auto"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-3xl bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[92vh]"
      >
        <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-black/10 bg-brand-cream/30">
          <div className="min-w-0">
            <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-brand-charcoal/55">
              Email preview · Inbox view
            </p>
            <p className="mt-0.5 text-[14px] font-bold text-brand-charcoal truncate">
              {subject || "(no subject)"}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="newsletter-preview-close"
            className="shrink-0 inline-flex items-center justify-center h-9 w-9 rounded-full bg-black/5 hover:bg-black/10 text-brand-charcoal"
            aria-label="Close preview"
          >
            <XCircle className="h-5 w-5" />
          </button>
        </div>
        <div className="flex-1 overflow-hidden bg-[#f3f4f6]">
          <iframe
            data-testid="newsletter-preview-iframe"
            title="Newsletter preview"
            srcDoc={`<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1"></head><body style="margin:0;padding:24px;background:#f3f4f6;font-family:Helvetica,Arial,sans-serif;">${html}</body></html>`}
            sandbox=""
            className="w-full h-[78vh] border-0 bg-white"
          />
        </div>
        <div className="px-5 py-3 border-t border-black/10 bg-white flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center gap-2 rounded-full bg-brand-charcoal text-white px-5 py-2 text-[11px] font-bold uppercase tracking-[0.1em] hover:bg-brand-teal transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};


// ---------- Rich-text editor (contentEditable + execCommand) ----------
// Lightweight WYSIWYG used by the Newsletter composer when mode === "rich".
// Saved HTML is sanitized server-side before being sent (scripts/javascript:
// URLs/onclick handlers stripped, links forced to target=_blank rel=noopener).

const NewsletterRichEditor = ({ value, onChange }) => {
  const editorRef = useRef(null);
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");

  // Reset the editor's DOM only when the caller explicitly clears it
  // (e.g. after sending). We never write the user's typed HTML back into
  // innerHTML — that would jump the caret to the start AND require
  // sanitization. The server-side sanitizer is the authoritative defense
  // before delivery to subscribers.
  useEffect(() => {
    if (!editorRef.current) return;
    if ((value || "") === "") {
      // textContent is safe; no HTML parsed.
      editorRef.current.textContent = "";
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value === ""]);

  const exec = (cmd, arg) => {
    if (typeof document === "undefined") return;
    editorRef.current?.focus();
    document.execCommand(cmd, false, arg);
    onChange(editorRef.current?.innerHTML || "");
  };

  const insertLink = () => {
    const url = window.prompt("Link URL (https://…)");
    if (!url) return;
    exec("createLink", url);
  };

  const insertImageHtml = (src, alt) => {
    if (typeof document === "undefined") return;
    editorRef.current?.focus();
    const safeAlt = (alt || "").replace(/"/g, "");
    const img = `<img src="${src}" alt="${safeAlt}" style="display:block;max-width:100%;height:auto;border-radius:12px;margin:14px 0;" />`;
    document.execCommand("insertHTML", false, img);
    onChange(editorRef.current?.innerHTML || "");
  };

  const insertPdfHtml = (href, filename) => {
    if (typeof document === "undefined") return;
    editorRef.current?.focus();
    const label = (filename || "Newsletter.pdf").replace(/"/g, "");
    // Branded CTA button + filename caption — same look as upload-mode PDFs.
    const html =
      `<p style="text-align:center;margin:18px 0;">` +
      `<a href="${href}" target="_blank" rel="noopener noreferrer" ` +
      `style="display:inline-block;background:#0f0f0f;color:#fff;text-decoration:none;` +
      `font:700 13px/1 Helvetica,Arial,sans-serif;letter-spacing:0.08em;` +
      `text-transform:uppercase;padding:14px 28px;border-radius:999px;">` +
      `📄 View Newsletter (PDF)</a>` +
      `<br /><span style="font:400 12px/1.4 Helvetica,Arial,sans-serif;color:#777;">${label}</span>` +
      `</p>`;
    document.execCommand("insertHTML", false, html);
    onChange(editorRef.current?.innerHTML || "");
  };

  const uploadAndInsert = async (file) => {
    if (!file) return;
    const isImage = file.type.startsWith("image/");
    const isPdf =
      file.type === "application/pdf" ||
      (file.name || "").toLowerCase().endsWith(".pdf");
    if (!isImage && !isPdf) {
      setUploadError(
        "Only images and PDFs can be inserted. Other file types are not supported in email bodies.",
      );
      return;
    }
    setUploading(true);
    setUploadError("");
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post(
        "/admin/newsletter/upload-asset",
        form,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      if (data.asset_type === "pdf" || isPdf) {
        insertPdfHtml(data.url, data.filename || file.name);
      } else {
        insertImageHtml(data.url, file.name);
      }
    } catch (e) {
      setUploadError(
        formatApiErrorDetail(e?.response?.data?.detail) ||
          e.message ||
          "Could not upload file.",
      );
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const onPaste = (e) => {
    // Allow pasting screenshots / copied images directly into the editor.
    const items = e.clipboardData?.items || [];
    for (const it of items) {
      if (it.kind === "file" && it.type.startsWith("image/")) {
        const file = it.getAsFile();
        if (file) {
          e.preventDefault();
          uploadAndInsert(file);
          return;
        }
      }
    }
  };

  const onDrop = (e) => {
    const file = e.dataTransfer?.files?.[0];
    const isImage = file?.type.startsWith("image/");
    const isPdf =
      file?.type === "application/pdf" ||
      (file?.name || "").toLowerCase().endsWith(".pdf");
    if (file && (isImage || isPdf)) {
      e.preventDefault();
      uploadAndInsert(file);
    }
  };

  const onInput = () => {
    onChange(editorRef.current?.innerHTML || "");
  };

  return (
    <div
      className="rounded-xl border border-black/10 overflow-hidden bg-white"
      data-testid="newsletter-rich-editor"
    >
      <div className="flex flex-wrap items-center gap-1 px-2 py-1.5 border-b border-black/10 bg-black/[0.02]">
        <RteBtn onClick={() => exec("bold")} testId="rte-bold" title="Bold">
          <Bold className="h-3.5 w-3.5" />
        </RteBtn>
        <RteBtn onClick={() => exec("italic")} testId="rte-italic" title="Italic">
          <Italic className="h-3.5 w-3.5" />
        </RteBtn>
        <RteBtn
          onClick={() => exec("underline")}
          testId="rte-underline"
          title="Underline"
        >
          <Underline className="h-3.5 w-3.5" />
        </RteBtn>
        <span className="w-px h-4 bg-black/10 mx-1" />
        <RteBtn
          onClick={() => exec("formatBlock", "h2")}
          testId="rte-h2"
          title="Heading"
        >
          <Heading2 className="h-3.5 w-3.5" />
        </RteBtn>
        <RteBtn
          onClick={() => exec("formatBlock", "h3")}
          testId="rte-h3"
          title="Subheading"
        >
          <Heading1 className="h-3.5 w-3.5 opacity-70" />
        </RteBtn>
        <RteBtn
          onClick={() => exec("formatBlock", "p")}
          testId="rte-p"
          title="Paragraph"
        >
          <Pilcrow className="h-3.5 w-3.5" />
        </RteBtn>
        <span className="w-px h-4 bg-black/10 mx-1" />
        <RteBtn
          onClick={() => exec("insertUnorderedList")}
          testId="rte-ul"
          title="Bulleted list"
        >
          <ListIcon className="h-3.5 w-3.5" />
        </RteBtn>
        <RteBtn
          onClick={() => exec("insertOrderedList")}
          testId="rte-ol"
          title="Numbered list"
        >
          <span className="text-[11px] font-black tracking-tight">1.</span>
        </RteBtn>
        <span className="w-px h-4 bg-black/10 mx-1" />
        <RteBtn onClick={insertLink} testId="rte-link" title="Insert link">
          <LinkIcon className="h-3.5 w-3.5" />
        </RteBtn>
        <RteBtn
          onClick={() => fileInputRef.current?.click()}
          testId="rte-image"
          title="Insert image or PDF"
        >
          {uploading ? (
            <span className="text-[10px] font-bold">…</span>
          ) : (
            <UploadCloud className="h-3.5 w-3.5" />
          )}
        </RteBtn>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,application/pdf,.pdf"
          className="hidden"
          data-testid="rte-image-input"
          onChange={(e) => uploadAndInsert(e.target.files?.[0])}
        />
        <RteBtn
          onClick={() => exec("removeFormat")}
          testId="rte-clear"
          title="Clear formatting"
        >
          <span className="text-[10px] font-bold">×</span>
        </RteBtn>
      </div>
      <div
        ref={editorRef}
        data-testid="newsletter-rich-editor-body"
        contentEditable
        suppressContentEditableWarning
        onInput={onInput}
        onBlur={onInput}
        onPaste={onPaste}
        onDrop={onDrop}
        onDragOver={(e) => e.preventDefault()}
        className="min-h-[260px] max-h-[480px] overflow-y-auto p-4 text-[14px] leading-relaxed text-brand-charcoal focus:outline-none prose prose-sm max-w-none"
      />
      {(uploading || uploadError) && (
        <div className="px-3 py-2 border-t border-black/10 bg-brand-cream/30 text-[11.5px]">
          {uploading && (
            <span className="text-brand-charcoal/70">Uploading image…</span>
          )}
          {uploadError && (
            <span className="text-red-700">{uploadError}</span>
          )}
        </div>
      )}
    </div>
  );
};

const RteBtn = ({ children, onClick, title, testId }) => (
  <button
    type="button"
    onMouseDown={(e) => e.preventDefault()}
    onClick={onClick}
    title={title}
    data-testid={testId}
    className="h-7 min-w-[28px] px-1.5 inline-flex items-center justify-center rounded-md text-brand-charcoal/70 hover:bg-brand-charcoal hover:text-white transition-colors"
  >
    {children}
  </button>
);


// ---------- Subscribers phonebook ----------
// Fixed-height contact-list card with: sticky search, sort dropdown,
// sticky letter-group headers, alphabet rail (A-Z + #) for jump-to,
// and an Export CSV button. Never expands beyond the viewport — internal
// scroll only.

const _ALPHA = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

const SubscribersPhonebook = ({ loading, error, subscribers, onChanged }) => {
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("first"); // first | last | email | newest
  const scrollRef = useRef(null);
  const letterRefs = useRef({});

  const sortKeyFor = (s) => {
    if (sortBy === "email") return (s.email || "").toLowerCase();
    if (sortBy === "last")
      return ((s.last_name || s.email || "") + " " + (s.first_name || "")).toLowerCase();
    if (sortBy === "newest") return s.created_at || "";
    return ((s.first_name || s.last_name || s.email || "")).toLowerCase();
  };

  const filteredSorted = useMemo(() => {
    const list = (subscribers || []).filter((s) => {
      if (!search.trim()) return true;
      const q = search.toLowerCase();
      return (
        (s.email || "").toLowerCase().includes(q) ||
        (s.first_name || "").toLowerCase().includes(q) ||
        (s.last_name || "").toLowerCase().includes(q)
      );
    });
    const out = [...list].sort((a, b) => {
      const ka = sortKeyFor(a);
      const kb = sortKeyFor(b);
      if (sortBy === "newest") return kb.localeCompare(ka); // desc
      return ka.localeCompare(kb);
    });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscribers, search, sortBy]);

  // Group contacts by leading letter (or "#" for non-letters / blank).
  const groups = useMemo(() => {
    const map = new Map();
    const order = sortBy === "newest" ? null : [];
    for (const s of filteredSorted) {
      let letter;
      if (sortBy === "newest") {
        // For newest mode, bucket by month
        const d = (s.created_at || "").slice(0, 7) || "—";
        letter = d;
      } else {
        const seed =
          sortBy === "last"
            ? s.last_name || s.email || ""
            : sortBy === "email"
            ? s.email || ""
            : s.first_name || s.last_name || s.email || "";
        const c = (seed.trim().charAt(0) || "#").toUpperCase();
        letter = /[A-Z]/.test(c) ? c : "#";
      }
      if (!map.has(letter)) {
        map.set(letter, []);
        if (order) order.push(letter);
      }
      map.get(letter).push(s);
    }
    return order
      ? order.map((l) => [l, map.get(l)])
      : Array.from(map.entries());
  }, [filteredSorted, sortBy]);

  const presentLetters = useMemo(
    () => new Set(groups.map(([l]) => l)),
    [groups],
  );

  const jumpTo = (letter) => {
    const el = letterRefs.current[letter];
    if (el && scrollRef.current) {
      scrollRef.current.scrollTo({ top: el.offsetTop - 4, behavior: "smooth" });
    }
  };

  const exportCsv = () => {
    const rows = [
      ["email", "first_name", "last_name", "created_at", "unsubscribed"],
    ];
    for (const s of subscribers || []) {
      rows.push([
        s.email || "",
        s.first_name || "",
        s.last_name || "",
        s.created_at || "",
        s.unsubscribed ? "true" : "false",
      ]);
    }
    const csv = rows
      .map((r) =>
        r
          .map((cell) => {
            const v = String(cell ?? "");
            return /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
          })
          .join(","),
      )
      .join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const stamp = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `fff-subscribers-${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div
      className="rounded-2xl bg-white border border-black/5 overflow-hidden flex flex-col"
      style={{ maxHeight: "min(560px, calc(100vh - 200px))" }}
      data-testid="newsletter-subscribers-phonebook"
    >
      <div className="flex flex-col gap-2 px-4 pt-4 pb-3 border-b border-black/5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div>
            <h4 className="font-display font-black text-[14px] text-brand-charcoal">
              Subscribers
            </h4>
            <p className="text-[11.5px] text-brand-charcoal/55 leading-snug">
              {(subscribers || []).length} contacts · synced live from Resend
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={exportCsv}
              disabled={!subscribers?.length}
              data-testid="newsletter-export-csv"
              className="inline-flex items-center gap-1.5 rounded-full bg-black/5 text-brand-charcoal hover:bg-black/10 transition-colors px-3.5 py-2 text-[10.5px] font-bold uppercase tracking-wider disabled:opacity-40"
            >
              <UploadCloud className="h-3 w-3 rotate-180" />
              Export CSV
            </button>
            <ManualSubscriberAdd onAdded={onChanged} />
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <Input
            data-testid="newsletter-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by email or name…"
            className="h-9 flex-1 text-[12.5px]"
          />
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            data-testid="newsletter-sort"
            className="h-9 rounded-md bg-white border border-black/10 px-3 text-[12px] text-brand-charcoal focus:outline-none focus:ring-2 focus:ring-brand-teal/30"
          >
            <option value="first">Sort: First name</option>
            <option value="last">Sort: Last name</option>
            <option value="email">Sort: Email</option>
            <option value="newest">Sort: Newest first</option>
          </select>
        </div>
      </div>

      {loading ? (
        <p className="p-4 text-[13px] text-brand-charcoal/60">Loading…</p>
      ) : error ? (
        <div className="m-3 rounded-xl bg-red-50 border border-red-200 p-3 text-[13px] text-red-700">
          {error}
        </div>
      ) : (subscribers || []).length === 0 ? (
        <div className="p-6">
          <EmptyState
            label="No subscribers yet. Add manually, import a CSV, or wait for the next order."
            icon={Mail}
          />
        </div>
      ) : filteredSorted.length === 0 ? (
        <div className="p-6">
          <EmptyState label="No subscribers match that search." icon={Mail} />
        </div>
      ) : (
        <div className="relative flex-1 min-h-0 flex">
          <div
            ref={scrollRef}
            data-testid="newsletter-subscribers-list"
            className="flex-1 overflow-y-auto"
          >
            {groups.map(([letter, items]) => (
              <div
                key={letter}
                ref={(el) => {
                  if (el) letterRefs.current[letter] = el;
                }}
                data-testid={`phonebook-section-${letter}`}
              >
                <div className="sticky top-0 z-10 bg-brand-cream/95 backdrop-blur px-4 py-1.5 border-b border-black/5">
                  <span className="font-display font-black text-[11px] tracking-[0.1em] text-brand-charcoal/70">
                    {letter}
                  </span>
                  <span className="ml-2 text-[10px] text-brand-charcoal/45">
                    {items.length}
                  </span>
                </div>
                <ul className="divide-y divide-black/5">
                  {items.map((s) => (
                    <SubscriberRow
                      key={s.id || s.email}
                      sub={s}
                      onRemoved={onChanged}
                    />
                  ))}
                </ul>
              </div>
            ))}
          </div>
          {/* Alphabet rail — only when sorting alphabetically. */}
          {sortBy !== "newest" && (
            <nav
              aria-label="Jump to letter"
              data-testid="newsletter-alpha-rail"
              className="w-7 sm:w-8 shrink-0 border-l border-black/5 bg-brand-cream/30 flex flex-col items-center justify-start py-2 gap-[1px]"
            >
              {_ALPHA.concat(["#"]).map((l) => {
                const present = presentLetters.has(l);
                return (
                  <button
                    key={l}
                    type="button"
                    onClick={() => present && jumpTo(l)}
                    disabled={!present}
                    data-testid={`phonebook-jump-${l}`}
                    title={present ? `Jump to ${l}` : undefined}
                    className={`w-full text-[9.5px] font-black leading-none py-0.5 px-1 rounded transition-colors ${
                      present
                        ? "text-brand-charcoal/75 hover:bg-brand-teal hover:text-white cursor-pointer"
                        : "text-brand-charcoal/20 cursor-default"
                    }`}
                  >
                    {l}
                  </button>
                );
              })}
            </nav>
          )}
        </div>
      )}
    </div>
  );
};

